/* eslint-disable*/
import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faComment,
  faBell,
  faEnvelope,
  faPlus,
  faTicket,
  faChevronCircleLeft,
  faClock,
  faPhone,
  faIdCard,
  faPlay,
  faDownload,
  faSearch,
  faMobileAlt,
  faFlag,
  faSync,
  faPaperPlane,
  faEdit,
  faTrash,
  faVolumeUp,
  faEye,
  faTimes,
  faPhoneVolume,
  faComments,
  faFileAlt,
  faRobot,
  faCalendarAlt,
  faPaperclip,
} from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config.js";
import { emailApiClient, ensureEmailAuth, EMAIL_APIS_DISABLED } from "../email/api/client";
import profileImg from "../assets/img/profile.png";
import blueTick from "../assets/img/bluetick.png";
import moment from "moment";
import "../Subscriber/subscribe.css"; // Import the CSS file here
import { showToast } from "../components/toast";
import "react-toastify/dist/ReactToastify.css";
import TicketPopup from "./TicketPopup.jsx";
import EmailViewPopup from "./EmailViewPopup.jsx";
import ReminderPopup from "./ReminderPopup.jsx";
import FreeServicePopup from "./FreeServicePopup.jsx";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import AddOrderPopup from "./AddOrderPopup.jsx";
import AddPaidServiceOrder from "./AddPaidServiceOrder.jsx";
import VerifyKycPopup from "./VerifyKycPopup.jsx";
import CvlKycVerifyModal from "./CvlKycVerifyModal.jsx";
import CashFlowChart from "./CashFlowChart.jsx";
import SendPushNotificationPopup from "./SendPushNotificationPopup.jsx";
import SendSmsPopup from "./SendSmsPopup.jsx";
import { PermissionWrapper, hasPermission, getRoleUserid } from "../helpers/permission.js";
import { UploadBox } from "../components/Input.jsx";
import SubscriberWhatsappChat from "./SubscriberWhatsappChat.jsx";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import AudioPlayerPopup from "../components/AudioPlayerPopup.jsx";

// Permission checking functions at module level
const isFieldEditable = (id, fieldName) => {
  if (!id) return true; // New subscriber - all fields editable
  return hasPermission(fieldName, "edit", true); // Default true for backward compatibility
};

const isFieldVisible = (fieldName) => {
  return hasPermission(fieldName, "view", true); // Default true for backward compatibility
};

// Permission-aware table row component for editable fields
const PermissionTableRow = ({
  fieldName,
  label,
  isEditing,
  onToggleEdit,
  children,
  id,
  colSpan = "2",
  rightContent = null,
  disabled = false,
}) => {
  if (!isFieldVisible(fieldName)) return null;

  const canEdit = !disabled && isFieldEditable(id, fieldName);
  const actualFieldName = fieldName.replace("subscriber_", ""); // Remove prefix for onClick

  return (
    <tr>
      <td width={rightContent ? "140" : ""}>{label}</td>
      <td
        className={canEdit ? "edtext" : ""}
        colSpan={rightContent ? "1" : colSpan}
        data-id={actualFieldName}
        onClick={canEdit ? () => onToggleEdit(actualFieldName) : undefined}
        style={{ cursor: canEdit ? "pointer" : "default" }}
      >
        {children}
      </td>
      {rightContent && (
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {rightContent}
        </td>
      )}
    </tr>
  );
};

const FieldPermissionWrapper = ({ field, children }) => {
  const hasFieldViewPermission = hasPermission(field, "view");
  return hasFieldViewPermission ? children : null;
};

// Permission-aware input component for inline editing
const PermissionEditableField = ({
  fieldName,
  value,
  isEditing,
  editFieldName,
  onChange,
  onBlur,
  onKeyDown,
  type = "text",
  placeholder = "",
  id,
}) => {
  if (!isFieldVisible(fieldName)) return null;

  const canEdit = isFieldEditable(id, fieldName);

  if (isEditing === editFieldName && canEdit) {
    return (
      <span className="edit">
        <input
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus
        />
      </span>
    );
  }

  return <span className="value">{value}</span>;
};

// Permission-aware dropdown component for inline editing
const PermissionEditableDropdown = ({
  fieldName,
  value,
  isEditing,
  editFieldName,
  onChange,
  onBlur,
  options = [],
  optionValue = "id",
  optionLabel = "pick_name",
  placeholder = "Select...",
  id,
}) => {
  if (!isFieldVisible(fieldName)) return null;

  const canEdit = isFieldEditable(id, fieldName);

  if (isEditing === editFieldName && canEdit) {
    return (
      <span className="edit">
        <select value={value} onChange={onChange} onBlur={onBlur} autoFocus>
          <option value="">Select</option>
          {options?.map((item, index) => (
            <option key={index} value={item[optionValue]}>
              {item[optionLabel]}
            </option>
          ))}
        </select>
      </span>
    );
  }

  // Display selected value
  const selectedItem = options?.find((item) => item[optionValue] == value);
  const displayValue = selectedItem ? selectedItem[optionLabel] : value;

  return <span className="value">{displayValue}</span>;
};

// Skeleton Row Component for loading state
const SkeletonRow = ({ label, hasRightContent = false }) => (
  <tr className="skeleton-row">
    <td width={hasRightContent ? "140" : ""}>{label}</td>
    <td colSpan={hasRightContent ? "1" : "2"}>
      <div className="skeleton-text" style={{ width: "60%" }}></div>
    </td>
    {hasRightContent && (
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        <div className="skeleton-text" style={{ width: "100px", display: "inline-block" }}></div>
      </td>
    )}
  </tr>
);

// Table Skeleton Row Component for loading state in tables (Service Details, KYC, etc.)
const SENT_EMAILS_LIMIT = 50;
const INBOX_EMAILS_LIMIT = 20;

// Time if today, "4 Oct" this year, "4 Oct 2025" otherwise
const formatInboxDate = (date) => {
  if (!date) return "-";
  const m = moment(date);
  if (!m.isValid()) return "-";
  if (m.isSame(moment(), "day")) return m.format("hh:mm A");
  if (m.isSame(moment(), "year")) return m.format("D MMM");
  return m.format("D MMM YYYY");
};

const TableSkeletonRow = ({ columns = 5 }) => (
  <tr className="skeleton-row">
    {Array.from({ length: columns }).map((_, index) => (
      <td key={index}>
        <div className="skeleton-text" style={{ width: "70%" }}></div>
      </td>
    ))}
  </tr>
);

// List Skeleton Item Component for loading state in lists (Comments, Activity, KYC History)
const ListSkeletonItem = () => (
  <div className="mb-3 p-2" style={{ border: "1px solid #e0e0e0", borderRadius: "6px" }}>
    <div className="skeleton-text" style={{ width: "60%", marginBottom: "8px" }}></div>
    <div className="skeleton-text" style={{ width: "40%" }}></div>
  </div>
);

// Compact Journey Flow Component
const JourneyMiniFlow = ({ journeyData = [], loading }) => {
  if (loading) {
    return (
      <div className="mt-1" style={{ width: '150px' }}>
        <div className="skeleton-text" style={{ width: '100%', height: '10px' }}></div>
      </div>
    );
  }

  if (!journeyData || !Array.isArray(journeyData) || journeyData.length === 0) return null;

  return (
    <div className="journey-mini-flow mt-1 ms-1 d-flex align-items-center flex-wrap" style={{ fontSize: '13px' }}>
      {journeyData.map((item, index) => (
        <React.Fragment key={item.id || index}>
          {index > 0 && (
            <FontAwesomeIcon
              icon={faChevronRight}
              style={{ fontSize: '10px', margin: '0 5px', color: '#ccc' }}
            />
          )}
          <span
            className="journey-status-item"
            title={item.created_on ? `Reached: ${moment(item.created_on).format("DD MMM YYYY hh:mm A")}` : ""}
            style={{
              color: index === journeyData.length - 1 ? '#0056b3' : '#777',
              fontWeight: index === journeyData.length - 1 ? '700' : '500',
              cursor: 'help',
              whiteSpace: 'nowrap'
            }}
          >
            {item.status}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

const SubscriberEdit = ({
  subscription = [],
  cart_data = [],
  commentlist = [],
  activitylist = [],
  callsummary = [],
  kyc_logs = [],
  val_verifykycon,
  val_kycVerifiedby,
  // id,
  subscriptionPage,
  deleteCartItem,
  pageSaveDiscount,
  clearCart,
  pageBackDiscount,
  tax_rate,
  tax_amt,
  discount_val,
  offercode,
  cart_ref_data,
  cart_ref_offer,
}) => {
  const { id } = useParams();
  const token = localStorage.getItem("authToken");
  const [errorMessages, setErrorMessages] = useState({}); // State for error messages
  const [loading, setLoading] = useState(false); // New state to handle loading
  const [subscriberLoading, setSubscriberLoading] = useState(true); // Subscriber details section
  const [activityLoading, setActivityLoading] = useState(false); // Activity history section
  const [dropdownsLoading, setDropdownsLoading] = useState(true);
  const [isRendering, setIsRendering] = useState(false); // New state for rendering
  const tableRenderedRef = useRef(false); // Ref to track table rendering
  const location = useLocation();
  // Router state wins; fall back to the filters saved by the Subscriber listing
  const getStoredFilterParams = () => {
    try {
      return JSON.parse(sessionStorage.getItem("subscriberFilterParams")) || {};
    } catch (error) {
      return {};
    }
  };
  const filters = location.state?.filterParams || getStoredFilterParams();
  const navigate = useNavigate();
  // Latest subscriber id, so a response for a previous subscriber is ignored
  const currentIdRef = useRef(id);
  currentIdRef.current = id;
  const isCurrentSubscriber = (subscriberId) =>
    String(subscriberId) === String(currentIdRef.current);
  const [tempEditValue, setTempEditValue] = useState("");
  const [originalValues, setOriginalValues] = useState({}); // Store original values for revert functionality
  const [userIds, setUserIds] = useState([]);

  // Subscriber Voice state
  const [subscriberVoiceData, setSubscriberVoiceData] = useState(null);
  const [subscriberVoiceLoading, setSubscriberVoiceLoading] = useState(false);
  const [isDeletingVoice, setIsDeletingVoice] = useState(false);

  const currentIndex = userIds.findIndex((userId) => userId === parseInt(id));

  const previousId = userIds[currentIndex - 1] ?? null;
  const nextId = userIds[currentIndex + 1] ?? null;

  const val_pushToken = "dummyPushToken";

  // State variables for each input
  const [subscriberId, setSubscriberId] = useState("");
  const [recentContacted, setRecentContacted] = useState("");
  const [leadSource, setLeadSource] = useState("");
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [mobileno, setMobileno] = useState("");
  const [otherphone, setOtherphone] = useState("");
  const [state, setState] = useState("");
  const [occupation, setOccupation] = useState("");
  const [language, setLanguage] = useState("");
  const [expInMarket, setExpInMarket] = useState("");
  const [investments, setInvestments] = useState("");
  const [convertible, setConvertible] = useState("");
  const [invCapital, setInvCapital] = useState("");
  const [dematBrokers, setDematBrokers] = useState("");
  const [riskLevel, setRiskLevel] = useState("");
  const [rAssigned, setRAssigned] = useState("");
  const [rAssignedName, setRAssignedName] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [inFolio, setInFolio] = useState(0); // Interested in Streetfolio (0 = Not Interested, 1 = Interested)
  const [askfps, setAskfps] = useState(0); // Askfps (0 = No, 1 = Yes)
  const [leadEn, setLeadEn] = useState(0); // Lead EN (0 = Existing, 1 = New)
  const [showSfIntroModal, setShowSfIntroModal] = useState(false);
  const [sfCampaignTemplates, setSfCampaignTemplates] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("21");
  const [isSendingSfIntro, setIsSendingSfIntro] = useState(false);
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);
  const [isTermsAgreementOpen, setIsTermsAgreementOpen] = useState(false);

  const fetchSfCampaignTemplates = async () => {
    try {
      setIsFetchingTemplates(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}subscriber/sf-intro-campaigns`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const list = response.data?.data || response.data?.templates || response.data || [];
      if (Array.isArray(list)) {
        setSfCampaignTemplates(list);
        if (list.length > 0) {
          setSelectedCampaignId(String(list[0].id));
        }
      }
    } catch (error) {
      console.error("Error fetching SF Intro campaign templates:", error);
    } finally {
      setIsFetchingTemplates(false);
    }
  };

  const handleSendSfIntro = async () => {
    if (!selectedCampaignId) {
      showToast.error("Please select a campaign template");
      return;
    }
    try {
      setIsSendingSfIntro(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_URL}subscriber/send-sf-intro`,
        {
          campaign_id: Number(selectedCampaignId),
          subscriber_id: Number(id),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success || response.status === 200) {
        showToast.success("SF Intro notification initiated successfully");
        setShowSfIntroModal(false);
      } else {
        showToast.error(response.data?.message || "Failed to send SF Intro notification");
      }
    } catch (error) {
      console.error("Error sending SF Intro notification:", error);
      showToast.error(
        error?.response?.data?.message || error.message || "Failed to send SF Intro notification"
      );
    } finally {
      setIsSendingSfIntro(false);
    }
  };

  const [showEmailNotificationModal, setShowEmailNotificationModal] = useState(false);
  const [emailNotificationTemplates, setEmailNotificationTemplates] = useState([]);
  const [selectedEmailNotificationCampaignId, setSelectedEmailNotificationCampaignId] = useState("");
  const [isSendingEmailNotification, setIsSendingEmailNotification] = useState(false);
  const [isFetchingEmailNotificationTemplates, setIsFetchingEmailNotificationTemplates] = useState(false);

  const fetchEmailNotificationCampaignTemplates = async () => {
    try {
      setIsFetchingEmailNotificationTemplates(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}subscriber/email-notification-campaigns`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const list = response.data?.data || response.data?.templates || response.data || [];
      if (Array.isArray(list)) {
        setEmailNotificationTemplates(list);
        if (list.length > 0) {
          setSelectedEmailNotificationCampaignId(String(list[0].id));
        } else {
          setSelectedEmailNotificationCampaignId("");
        }
      }
    } catch (error) {
      console.error("Error fetching Email Notification campaign templates:", error);
    } finally {
      setIsFetchingEmailNotificationTemplates(false);
    }
  };

  const handleSendEmailNotification = async () => {
    if (!selectedEmailNotificationCampaignId) {
      showToast.error("Please select a campaign template");
      return;
    }
    try {
      setIsSendingEmailNotification(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_URL}subscriber/send-sf-intro`,
        {
          campaign_id: Number(selectedEmailNotificationCampaignId),
          subscriber_id: Number(id),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success || response.status === 200) {
        showToast.success("Email notification initiated successfully");
        setShowEmailNotificationModal(false);
      } else {
        showToast.error(response.data?.message || "Failed to send email notification");
      }
    } catch (error) {
      console.error("Error sending email notification:", error);
      showToast.error(
        error?.response?.data?.message || error.message || "Failed to send email notification"
      );
    } finally {
      setIsSendingEmailNotification(false);
    }
  };
  const [assignedToName, setAssignedToName] = useState("");
  const [assignedToFolio, setAssignedToFolio] = useState(""); // NEW: Folio assigned to
  const [assignedToFolioName, setAssignedToFolioName] = useState(""); // NEW: Folio assigned to name
  const [assignedToChat, setAssignedToChat] = useState(""); // NEW: Chat assigned to
  const [assignedToChatName, setAssignedToChatName] = useState(""); // NEW: Chat assigned to name
  const [leadStatus, setLeadStatus] = useState("");
  const [clientStatus, setClientStatus] = useState("");
  const [subscriberStatus, setSubscriberStatus] = useState("");
  const [reasonNotInterested, setReasonNotInterested] = useState("");
  const [nextFollowup, setNextFollowup] = useState("");
  const [interestedSegments, setInterestedSegments] = useState("");
  const [priceExpected, setPriceExpected] = useState("");
  const [utmTerm, setUtmTerm] = useState("");
  const [billTo, setBillTo] = useState("");
  const [gstNo, setGstNo] = useState("");
  const [complianceScore, setComplianceScore] = useState("");
  const [aiTag, setAiTag] = useState("");
  const [createdOn, setCreatedOn] = useState("");
  const [updatedOn, setUpdatedOn] = useState("");
  const [verifiedOn, setVerifiedOn] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [verifyUser, setVerifyUser] = useState("");
  const [verifyKyc, setVerifyKyc] = useState("");
  const [verifyKycOn, setVerifyKycOn] = useState("");
  const [kycVerifiedBy, setKycVerifiedBy] = useState("");
  const [kycVerifiedByName, setKycVerifiedByName] = useState("");
  const [comments, setComments] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentTab, setCommentTab] = useState("lead");
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [subscriberSummary, setSubscriberSummary] = useState("");
  const [subscriberSummaryLoading, setSubscriberSummaryLoading] = useState(false);
  const [isPlacingAiCall, setIsPlacingAiCall] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentFile, setCommentFile] = useState(null);
  const [commentsActiveTab, setCommentsActiveTab] = useState("comments");

  useEffect(() => {
    if (
      !hasPermission("subscriber_comment", "view") &&
      (hasPermission("subscriber_ai_comments", "view") ||
        hasPermission("subscriber_summary", "view"))
    ) {
      setCommentsActiveTab("ai_comments");
    }
  }, []);
  const [isActivityHistoryOpen, setIsActivityHistoryOpen] = useState(false);
  const [openTickets, setOpenTickets] = useState([]);
  const [kycLogs, setKycLogs] = useState([]);
  const [kycLogsLoading, setKycLogsLoading] = useState(false);
  const [isKycSectionOpen, setIsKycSectionOpen] = useState(false);
  const [activityList, setActivityList] = useState([]);
  const [activityTab, setActivityTab] = useState("activity");
  const [voiceLogsList, setVoiceLogsList] = useState([]);
  const [voiceLogsLoading, setVoiceLogsLoading] = useState(false);
  const [callSummary, setCallSummary] = useState([]);
  const [callSummaryLoading, setCallSummaryLoading] = useState(false);
  const [callLogTab, setCallLogTab] = useState("call_summary");

  useEffect(() => {
    if (
      !hasPermission("subscriber_callhistory", "view") &&
      hasPermission("subscriber_voice_call_logs", "view")
    ) {
      setCallLogTab("voice_call_logs");
    }
  }, []);
  const [voiceCallLogs, setVoiceCallLogs] = useState([]);
  const [popupAudio, setPopupAudio] = useState(null);
  const [voiceCallLogsLoading, setVoiceCallLogsLoading] = useState(false);
  const [selectedVoiceTranscript, setSelectedVoiceTranscript] = useState(null);
  const [emailTab, setEmailTab] = useState("sent");
  // Email opened in the read-only popup (a Sent or Inbox row)
  const [viewEmail, setViewEmail] = useState(null);
  const [sentEmails, setSentEmails] = useState([]);
  const [sentEmailsLoading, setSentEmailsLoading] = useState(false);
  const [sentEmailsPage, setSentEmailsPage] = useState(1);
  const [sentEmailsTotal, setSentEmailsTotal] = useState(0);
  // Saved subscriber email (not the editable form field) used for the Inbox lookup
  const [inboxAddress, setInboxAddress] = useState("");
  const inboxAddressRef = useRef("");
  const [inboxEmails, setInboxEmails] = useState([]);
  const [inboxEmailsLoading, setInboxEmailsLoading] = useState(false);
  const [inboxEmailsError, setInboxEmailsError] = useState(false);
  const [inboxEmailsTotal, setInboxEmailsTotal] = useState(0);
  const [inboxNextCursor, setInboxNextCursor] = useState(null);
  const [kycDetails, setKycDetails] = useState(null);
  const [kycDetailsLoading, setKycDetailsLoading] = useState(false);
  const [serviceDetails, setServiceDetails] = useState([]);
  const [subscriptionReport, setsubscriptionReport] = useState([]);
  const [subscriberToken, setSubscriberToken] = useState("");
  const [dob, setDob] = useState("");
  const [cashFlowSubscriptionReport, setCashFlowSubscriptionReport] = useState(
    []
  );
  const [serviceDetailsLoading, setServiceDetailsLoading] = useState(false);
  const [subscriptionReportLoading, setSubscriptionReportLoading] =
    useState(false);
  const [cashFlowLoading, setCashFlowLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [istrade, setIstrade] = useState(0);
  const [isfolio, setIsfolio] = useState(0);
  const [serviceCategory, setServiceCategory] = useState("");
  const [acceptanceData, setAcceptanceData] = useState([]);
  const [acceptanceLoading, setAcceptanceLoading] = useState(false);
  const [downloadingAcceptanceId, setDownloadingAcceptanceId] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSlides, setLightboxSlides] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [renewingService, setRenewingService] = useState(null);
  const [isRenewLoading, setIsRenewLoading] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState({
    leadSourceList: [],
    leadStatusList: [],
    clientStatusList: [],
    invCapList: [],
    statusList: [],
    priceExpectedList: [],
    investmentList: [],
    convertibleList: [],
    expInMarketList: [],
    fullUserList: [],
    languageList: [],
    subscriberStatusList: [],
    stateList: [],
    riskLevelList: [],
    dematBrokersList: [],
    interestSegmentList: [],
    reasonNotInterestedList: [],
    ticketCategoryList: [],
    serviceList: [],
    amountList: [],
    paymodeList: [],
    paidTradesList: [],
    paidTradesList: [],
    sourceList: [],
    complianceScoreList: [],
  });
  const [isRead, setIsRead] = useState(0);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isCheckingPush, setIsCheckingPush] = useState(false);
  const [isRefreshingDevice, setIsRefreshingDevice] = useState(false);
  const [isRefreshingFolioDevice, setIsRefreshingFolioDevice] = useState(false);
  const [journeyData, setJourneyData] = useState([]);
  const [journeyLoading, setJourneyLoading] = useState(false);

  const handleRefreshDeviceId = async (refreshType = 0) => {
    if (!mobileno) {
      showToast.error("Subscriber phone number is missing");
      return;
    }
    const isFolio = Number(refreshType) === 1;
    try {
      if (isFolio) {
        setIsRefreshingFolioDevice(true);
      } else {
        setIsRefreshingDevice(true);
      }
      const token = localStorage.getItem("authToken");
      const payload = { phone: mobileno, mobileno: mobileno, type: isFolio ? 1 : 0 };

      const response = await axios.post(
        `${API_URL}subscriber/refresh-device-id`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const resData = response.data;
      if (resData?.success) {
        const innerData = resData.data;
        if (innerData?.reset) {
          showToast.success(
            innerData.message ||
              (isFolio
                ? "SF device ID reset to null successfully"
                : "SG device ID reset to null successfully")
          );
          if (id) {
            fetchSubscriber();
          }
        } else {
          showToast.info(
            innerData?.message ||
              (isFolio
                ? "SF device ID refresh check completed"
                : "SG device ID refresh check completed")
          );
        }
      } else {
        showToast.error(
          resData?.message ||
            (isFolio
              ? "Failed to refresh SF device ID"
              : "Failed to refresh SG device ID")
        );
      }
    } catch (error) {
      console.error(
        isFolio ? "Error refreshing SF device ID:" : "Error refreshing SG device ID:",
        error
      );
      showToast.error(
        error.response?.data?.message ||
          (isFolio ? "Failed to refresh SF device ID" : "Failed to refresh SG device ID")
      );
    } finally {
      if (isFolio) {
        setIsRefreshingFolioDevice(false);
      } else {
        setIsRefreshingDevice(false);
      }
    }
  };

  const fetchSubscriber = async () => {
    const requestId = id;
    try {
      setSubscriberLoading(true);
      const token = localStorage.getItem("authToken");

      const response = await axios.get(`${API_URL}subscriber/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: filters,
      });
      if (!isCurrentSubscriber(requestId)) return;

      const subscriberData = response.data.data.subscriber;
      
      setSubscriberId(subscriberData.subscriberid || "");
      setRecentContacted(subscriberData.recent_contacted || "");
      setFullname(subscriberData.fullname || "");
      setLeadSource(subscriberData.lead_source || "");
      setEmail(subscriberData.email || "");
      setInboxAddress((subscriberData.email || "").trim());
      setMobileno(subscriberData.mobileno || "");
      setOtherphone(subscriberData.otherphone || "");
      setState(subscriberData.state || "");
      setOccupation(subscriberData.occupation || "");
      setLanguage(subscriberData.language || "");
      setExpInMarket(subscriberData.exp_in_market || "");
      setInvestments(subscriberData.investments || "");
      setConvertible(subscriberData.convertible || "");
      setInvCapital(subscriberData.inv_capital || "");
      setDematBrokers(subscriberData.demat_brokers || "");
      setRiskLevel(subscriberData.risk_level || "");
      setRAssigned(subscriberData.r_assigned !== null && subscriberData.r_assigned !== undefined ? String(subscriberData.r_assigned) : "");
      setRAssignedName(subscriberData.r_assigned_name || (typeof subscriberData.r_assigned === 'string' && isNaN(Number(subscriberData.r_assigned)) ? subscriberData.r_assigned : ""));
      setIsRead(subscriberData.isRead || 0);
      // For assignedTo, use assignedto_name from API response to find the ID
      setAssignedTo(subscriberData.assignedto || "");
      setInFolio(subscriberData.in_folio !== undefined && subscriberData.in_folio !== null ? subscriberData.in_folio : 0);
      setAskfps(subscriberData.askfps !== undefined && subscriberData.askfps !== null ? subscriberData.askfps : 0);
      setLeadEn(subscriberData.lead_en !== undefined && subscriberData.lead_en !== null ? Number(subscriberData.lead_en) : 0);
      setAssignedToName(subscriberData.assignedto_name || subscriberData.assignedto || "");
      setAssignedToFolio(subscriberData.assignedto_folio || "");
      setAssignedToFolioName(subscriberData.assignedto_folio_name || "");
      setAssignedToChat(subscriberData.assignedto_chat || "");
      setAssignedToChatName(subscriberData.assignedto_chat_name || "");
      // if (
      //   subscriberData.assignedto_name &&
      //   dropdownOptions.assignedUserList?.length > 0
      // ) {
      //   // Find ID from assignedto_name if dropdown options are already loaded
      //   const assignedUser = dropdownOptions.assignedUserList.find(
      //     (item) => item.tu_user_fullname === subscriberData.assignedto_name
      //   );
      //   setAssignedTo(
      //     assignedUser ? assignedUser.tu_id : subscriberData.assignedto || ""
      //   );
      // } else if (subscriberData.assignedto) {
      //   // Fallback to assignedto ID if name is not available
      //   setAssignedTo(subscriberData.assignedto);
      // } else {
      //   // Store name temporarily, will be converted to ID in useEffect
      //   setAssignedTo(subscriberData.assignedto_name || "");
      // }
      setLeadStatus(subscriberData.lead_status || "");
      setClientStatus(subscriberData.client_status || "");
      setSubscriberStatus(subscriberData.subscriber_status || "");
      setReasonNotInterested(subscriberData.reason_not_interested || "");
      setNextFollowup(subscriberData.next_followup || "");
      setInterestedSegments(subscriberData.interested_segments || "");
      setPriceExpected(subscriberData.price_expected || "");
      setUtmTerm(subscriberData.utm_term || "");
      setBillTo(subscriberData.bill_to || "");
      setBillTo(subscriberData.bill_to || "");
      setGstNo(subscriberData.gstno || "");
      setComplianceScore(subscriberData.compliance_score !== null && subscriberData.compliance_score !== undefined ? subscriberData.compliance_score : "");
      setAiTag(subscriberData.ai_tag || "");
      setCreatedOn(subscriberData.created_on || "");
      setUpdatedOn(subscriberData.updated_on || "");
      setVerifiedOn(subscriberData.verifiedon || "");
      setReminderTime(subscriberData.reminder_time || null);
      setVerifyUser(subscriberData.verifyuser || "");
      setVerifyKyc(subscriberData.verifykyc || "");
      setVerifyKycOn(subscriberData.verifykycon || "");
      setKycVerifiedBy(subscriberData.kyc_verifiedby || "");
      setKycVerifiedByName(subscriberData.kyc_verifiedby_name || "");
      setSubscriberToken(subscriberData.token || "");
      setServiceCategory(subscriberData.source_name || "");
      const istradeValue = subscriberData.application?.is_trade_user || 0;
      const isfolioValue = subscriberData.application?.is_folio_user || 0;

      setIstrade(istradeValue);
      setIsfolio(isfolioValue);

      setUserIds(response.data.data.subscriberIds);
    } catch (error) {
      if (!isCurrentSubscriber(requestId)) return;
      console.error("Error fetching subscriber:", error);
      showToast.error(error.message || "Failed to fetch subscriber data");
      setErrorMessages(error.message || "Failed to fetch subscriber data");
    } finally {
      if (isCurrentSubscriber(requestId)) setSubscriberLoading(false);
    }
  };

  // Helper function to automatically mark subscriber as read (only if currently unread)
  const markAsReadAutomatically = async () => {
    // Only mark as read if currently unread (isRead === 0)
    if (isRead === 1) return;

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${API_URL}subscriber/mark-as-read`,
        {
          subscriberId: parseInt(id),
          isRead: 1
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 || response.status === 201 || response.data?.success) {
        setIsRead(1);
      }
    } catch (error) {
      // Silently fail - don't show error toast for automatic marking
      console.error("Error auto-marking as read:", error);
    }
  };

  const checkRenderingStatus = useCallback(() => {
    if (tableRenderedRef.current) {
      // Add a small delay to ensure the table is fully rendered
      setTimeout(() => {
        setIsRendering(false);
      }, 300);
    }
  }, []);

  useEffect(() => {
    if (serviceDetails.length > 0 || subscriptionReport.length > 0) {
      checkRenderingStatus();
    }
  }, [serviceDetails, subscriptionReport, checkRenderingStatus]);

  const fetchDropdownOptions = async () => {
    try {
      setDropdownsLoading(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(`${API_URL}subscriber/get-dropdown`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      const dropdownData = response.data.data;

      // Update dropdown options state with all available lists
      setDropdownOptions({
        leadSourceList: dropdownData.leadSource || [],
        leadStatusList: dropdownData.leadStatus || [],
        clientStatusList: dropdownData.clientStatus || [],
        invCapList: dropdownData.invCapital || [],
        statusList: dropdownData.subscriberStatus || [], // pending
        priceExpectedList: dropdownData.priceExpected || [], // pending
        investmentList: dropdownData.investments || [], // Note: it's investmentslist not investmentList
        convertibleList: dropdownData.convertible || [],
        expInMarketList: dropdownData.expInMarket || [],
        fullUserList: dropdownData?.fulluserlist || [],
        assignedUserList: dropdownData?.assignedTo || [],
        assignedResearchUserList: dropdownData?.assignedToResearch || [],
        languageList: dropdownData.languageList || [],
        subscriberStatusList: dropdownData.subscriber_statuslist || [],
        stateList: dropdownData.stateList || [],
        riskLevelList: dropdownData.riskLevels || [],
        dematBrokersList: dropdownData.dematBroker || [], // Note: it's dematborkerslist not dematBrokersList
        interestSegmentList: dropdownData.interestedSegments || [],
        reasonNotInterestedList: dropdownData.notInterestedReasons || [],
        // Additional lists from the response
        reminderWayList: dropdownData.reminder_waylist || [],
        utmCampaignList: dropdownData.utmcampaignlist || [],
        utmContentList: dropdownData.utmcontentlist || [],
        utmMediumList: dropdownData.utmmediumlist || [],
        utmSourceList: dropdownData.utmsourcelist || [],
        utmTermList: dropdownData.utmtermlist || [],
        userList: dropdownData.userlist || [],
        subsReqCountList: dropdownData.subsReqCountList || [],
        serviceList: response.data.data.servicelist || [],
        ticketCategoryList: response.data.data.ticketCategorylist || [],
        amountList: response.data.data.amountlist || [],
        paymodeList: response.data.data.paymodeList || [],
        paidTradesList: response.data.data.paidtradeList || [],
        sourceList: dropdownData.sourceList || [],
        complianceScoreList: dropdownData.complianceScoreList || [],
      });
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
      setErrorMessages((prev) => ({ ...prev, dropdowns: error.message }));
    } finally {
      setDropdownsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriber();
  }, [id]);

  useEffect(() => {
    const loadData = async () => {
      const currentToken = localStorage.getItem("authToken") || token;
      if (currentToken) {
        await fetchDropdownOptions();
      } else {
        setDropdownsLoading(false);
      }
    };
    loadData();
  }, []);

  // Convert assignedTo from name to ID once dropdownOptions are loaded
  // useEffect(() => {
  //   // Only convert if assignedTo is a string (name) and not already a number (ID)
  //   // This runs when dropdownOptions are loaded, not when assignedTo changes
  //   if (
  //     assignedTo &&
  //     typeof assignedTo === "string" &&
  //     isNaN(Number(assignedTo)) &&
  //     dropdownOptions.assignedUserList?.length > 0
  //   ) {
  //     const assignedUser = dropdownOptions.assignedUserList.find(
  //       (item) => item.tu_user_fullname === assignedTo
  //     );
  //     if (assignedUser && assignedUser.tu_id !== assignedTo) {
  //       setAssignedTo(assignedUser.tu_id);
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [dropdownOptions.assignedUserList]);

  const fetchServiceSubscriptionReports = async (subscriberId) => {
    try {
      // setCommentLoading(true);
      setServiceDetailsLoading(true);
      setSubscriptionReportLoading(true);
      setCashFlowLoading(true);

      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(
        `${API_URL}subscriber/subscriber/${subscriberId}/services`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (response.data.success) {
        setServiceDetails(response.data.serviceDetails);
        setsubscriptionReport(response.data.subscriptionReport.data);
        setCashFlowSubscriptionReport(response.data.cashFlowSubscriptions);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setErrorMessages((prev) => ({
        ...prev,
        comments: error.message || "Failed to fetch comments",
      }));
    } finally {
      // setCommentLoading(false);
      if (isCurrentSubscriber(subscriberId)) {
        setServiceDetailsLoading(false);
        setSubscriptionReportLoading(false);
        setCashFlowLoading(false);
      }
    }
  };

  const fetchComments = async (subscriberId) => {
    try {
      setCommentLoading(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(
        `${API_URL}subscriber/get-comments/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (response.data.success && response.data.data) {
        setComments(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setErrorMessages((prev) => ({
        ...prev,
        comments: error.message || "Failed to fetch comments",
      }));
    } finally {
      if (isCurrentSubscriber(subscriberId)) setCommentLoading(false);
    }
  };

  const fetchSubscriberSummary = async (subscriberId) => {
    try {
      setSubscriberSummaryLoading(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(
        `${API_URL}subscriber/get-summary/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      if (isCurrentSubscriber(subscriberId)) {
        setSubscriberSummary(response.data.data?.summary || "");
      }
    } catch (error) {
      console.error("Error fetching subscriber summary:", error);
      if (isCurrentSubscriber(subscriberId)) {
        showToast.error(error.response?.data?.message || "Failed to load subscriber summary");
      }
    } finally {
      if (isCurrentSubscriber(subscriberId)) setSubscriberSummaryLoading(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!id) return;

    try {
      setIsGeneratingSummary(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.post(
        `${API_URL}subscriber/generate-summary/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (isCurrentSubscriber(id)) {
        setSubscriberSummary(response.data.data?.summary || "");
        showToast.success("Summary generated and saved successfully");
      }
    } catch (error) {
      console.error("Error generating subscriber summary:", error);
      showToast.error(error.response?.data?.message || "Failed to generate subscriber summary");
    } finally {
      if (isCurrentSubscriber(id)) setIsGeneratingSummary(false);
    }
  };

  const handleAiVoiceCall = async () => {
    if (!mobileno) {
      showToast.warning("Subscriber mobile number is not available.");
      return;
    }

    const selectedLanguage =
      dropdownOptions.languageList.find((item) => item.id == language)?.name ||
      language ||
      "";

    try {
      setIsPlacingAiCall(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.post(
        `${API_URL}voiceCall/instant`,
        {
          subUid: String(id || ""),
          mobile: mobileno,
          name: fullname || "Valued Customer",
          leadStatus: leadStatus || "new",
          custom: {
            profileSummary: subscriberSummary || "",
            callSummary: subscriberSummary || "",
            summary: subscriberSummary || "",
            // created_date: createdOn || "",
            lead_source: leadSource || "",
            language: selectedLanguage,
            // recent_contacted: recentContacted || "",
          },
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      showToast.success(
        response.data?.message || "AI call placed successfully."
      );
    } catch (error) {
      console.error("Error placing subscriber AI call:", error);
      showToast.error(
        error.response?.data?.message || "Failed to trigger AI call."
      );
    } finally {
      setIsPlacingAiCall(false);
    }
  };

  const handleCommentFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (limit to 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        showToast.error("File size must be less than 5MB");
        e.target.value = ""; // Clear the input
        return;
      }

      // Check file type (allow images, audio, PDFs, and common document types)
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
      ];

      if (!allowedTypes.includes(file.type) && !file.type.startsWith("audio/") && !file.type.startsWith("video/")) {
        showToast.error(
          "File type not supported. Please upload images, audio files, videos (MPEG/MP4), PDFs, or documents."
        );
        e.target.value = ""; // Clear the input
        return;
      }

      setCommentFile(file);
    } else {
      setCommentFile(null);
    }
  };

  const postComment = async () => {
    if (!commentText.trim() && !commentFile) return;

    try {
      setCommentLoading(true);

      // Create FormData for multipart/form-data request
      const formData = new FormData();
      formData.append("id", id);
      formData.append("fieldvalue", commentText);

      // Add file if selected
      if (commentFile) {
        formData.append("comment_file", commentFile);
      }

      const response = await axios.post(
        `${API_URL}subscriber/save-comment`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success || response.status) {
        showToast.success("Comment added successfully");
        // Clear the comment text and file
        setCommentText("");
        setCommentFile(null);
        // Refresh comments
        fetchComments(id);
        // Auto-mark as read if unread
        markAsReadAutomatically();
      } else {
        showToast.error("Failed to post comment");
        // Handle API error response
        console.error("API Error:", response.data.message);
        setErrorMessages((prev) => ({
          ...prev,
          commentPost: response.data.message || "Failed to post comment",
        }));
      }
    } catch (error) {
      console.error("Error posting comment:", error);
      showToast.error("Failed to post comment");
    } finally {
      setCommentLoading(false);
    }
  };

  const fetchKycLogs = async (subscriberId) => {
    try {
      setKycLogsLoading(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(
        `${API_URL}subscriber/get-kyc-logs/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (response.data.success && response.data.data) {
        setKycLogs(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching KYC logs:", error);
      setErrorMessages((prev) => ({
        ...prev,
        kycLogs: error.message || "Failed to fetch KYC logs",
      }));
    } finally {
      if (isCurrentSubscriber(subscriberId)) setKycLogsLoading(false);
    }
  };

  const fetchCallSummary = async (subscriberId) => {
    try {
      setCallSummaryLoading(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(
        `${API_URL}subscriber/get-call-summary/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (response.data.success && response.data.data) {
        setCallSummary(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching call summary:", error);
      setErrorMessages((prev) => ({
        ...prev,
        callSummary: error.message || "Failed to fetch call summary",
      }));
    } finally {
      if (isCurrentSubscriber(subscriberId)) setCallSummaryLoading(false);
    }
  };

  const fetchSentEmails = async (subscriberId, page = 1) => {
    // The Emails box is only shown (and loaded) with subscriber_email_data view rights
    if (!hasPermission("subscriber_email_data", "view")) return;
    try {
      setSentEmailsLoading(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(
        `${API_URL}subscriber/get-email-list/${subscriberId}`,
        {
          params: { page, limit: SENT_EMAILS_LIMIT },
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (response.data.success && response.data.data) {
        setSentEmails(response.data.data.emails || []);
        setSentEmailsTotal(Number(response.data.data.total) || 0);
        setSentEmailsPage(Number(response.data.data.current_page) || page);
      }
    } catch (error) {
      console.error("Error fetching sent emails:", error);
      setErrorMessages((prev) => ({
        ...prev,
        sentEmails: error.message || "Failed to fetch sent emails",
      }));
    } finally {
      if (isCurrentSubscriber(subscriberId)) setSentEmailsLoading(false);
    }
  };

  const fetchInboxEmails = async (address, cursor) => {
    if (EMAIL_APIS_DISABLED) return;
    if (!hasPermission("subscriber_email_data", "view")) return;
    try {
      setInboxEmailsLoading(true);
      setInboxEmailsError(false);
      await ensureEmailAuth();
      const response = await emailApiClient.get("/email/lookup", {
        params: {
          email: address,
          folder: "received",
          cursor: cursor || undefined,
          limit: INBOX_EMAILS_LIMIT,
        },
      });

      if (inboxAddressRef.current !== address) return;
      const result = response.data || {};
      const items = result.items || [];
      setInboxEmails((prev) => (cursor ? [...prev, ...items] : items));
      setInboxEmailsTotal(Number(result.total) || 0);
      setInboxNextCursor(result.hasMore ? result.nextCursor || null : null);
    } catch (error) {
      console.error("Error fetching inbox emails:", error);
      if (inboxAddressRef.current === address) setInboxEmailsError(true);
    } finally {
      if (inboxAddressRef.current === address) setInboxEmailsLoading(false);
    }
  };

  useEffect(() => {
    inboxAddressRef.current = inboxAddress;
    setInboxEmails([]);
    setInboxEmailsTotal(0);
    setInboxNextCursor(null);
    setInboxEmailsError(false);
    setInboxEmailsLoading(false);
    if (inboxAddress.length >= 3) fetchInboxEmails(inboxAddress);
  }, [inboxAddress]);

  const fetchKycDetails = async (subscriberId) => {
    try {
      setKycDetailsLoading(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(
        `${API_URL}subscriber/get-kyc-details/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        setKycDetails(response.data.data[0]);
        setDob(response.data.data[0].dob || "");
      }
    } catch (error) {
      console.error("Error fetching KYC details:", error);
      setErrorMessages((prev) => ({
        ...prev,
        kycDetails: error.message || "Failed to fetch KYC details",
      }));
    } finally {
      if (isCurrentSubscriber(subscriberId)) setKycDetailsLoading(false);
    }
  };

  const fetchJourneyData = async (subscriberId) => {
    try {
      setJourneyLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_URL}subscriber/journey/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (Array.isArray(response.data)) {
        setJourneyData(response.data);
      } else if (response.data?.success && response.data?.data) {
        setJourneyData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching journey data:", error);
    } finally {
      if (isCurrentSubscriber(subscriberId)) setJourneyLoading(false);
    }
  };

  const fetchActivityLogs = async (subscriberId) => {
    try {
      setActivityLoading(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(
        `${API_URL}subscriber/get-subscriber-logs/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (response.data.success && response.data.data) {
        // Update the activity list state with the fetched logs
        setActivityList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      setErrorMessages((prev) => ({
        ...prev,
        activityLogs: error.message || "Failed to fetch activity logs",
      }));
    } finally {
      if (isCurrentSubscriber(subscriberId)) setActivityLoading(false);
    }
  };

  const fetchSubscriberVoiceLogs = async (subscriberId) => {
    if (!subscriberId) return;
    try {
      setVoiceLogsLoading(true);
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(
        `${API_URL}voiceCall/subscriber-voice-logs/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (response.data && response.data.data) {
        setVoiceLogsList(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching subscriber voice logs:", error);
    } finally {
      if (isCurrentSubscriber(subscriberId)) setVoiceLogsLoading(false);
    }
  };

  const fetchOpenTickets = async (subscriberId) => {
    try {
      const currentToken = localStorage.getItem("authToken") || token;
      const response = await axios.get(
        `${API_URL}subscriber/ticket/${subscriberId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (response.data && response.data.tickets) {
        setOpenTickets(response.data.tickets); // ✅ use tickets array from API
      } else {
        setOpenTickets([]); // clear if no tickets
      }
    } catch (error) {
      console.error("Error fetching open tickets:", error);
      setErrorMessages((prev) => ({
        ...prev,
        openTickets: error.message || "Failed to fetch open tickets",
      }));
      if (isCurrentSubscriber(subscriberId)) setOpenTickets([]); // clear on error
    }
  };

  useEffect(() => {
    if (id) {
      // Clear the previous subscriber's data so each section shows its own loader
      setComments([]);
      setSubscriberSummary("");
      setKycLogs([]);
      setActivityList([]);
      setCallSummary([]);
      setSentEmails([]);
      setSentEmailsTotal(0);
      setSentEmailsPage(1);
      setViewEmail(null);
      setInboxAddress("");
      setKycDetails(null);
      setDob("");
      setServiceDetails([]);
      setsubscriptionReport([]);
      setCashFlowSubscriptionReport([]);
      setOpenTickets([]);
      setAcceptanceData([]);
      setJourneyData([]);
      setSubscriberVoiceData(null);
      setVoiceCallLogs([]);

      fetchComments(id);
      if (
        hasPermission("subscriber_summary", "view") ||
        hasPermission("subscriber_ai_comments", "view")
      ) {
        fetchSubscriberSummary(id);
      }
      if (hasPermission("subscriber_voice", "view", true)) {
        fetchSubscriberVoice(id);
      }
      if (
        hasPermission("subscriber_voice_call_logs", "view", true) ||
        hasPermission("subscriber_ai_comments", "view", true)
      ) {
        fetchVoiceCallLogs(id);
      }
      fetchKycLogs(id);
      fetchActivityLogs(id);
      fetchSubscriberVoiceLogs(id);
      fetchCallSummary(id);
      fetchSentEmails(id, 1);
      fetchKycDetails(id);
      fetchServiceSubscriptionReports(id);
      fetchOpenTickets(id);
      fetchAcceptanceData(id);
      fetchJourneyData(id);
    }
  }, [id]);

  const fetchVoiceCallLogs = async (subId) => {
    if (!subId) return;
    setVoiceCallLogsLoading(true);
    try {
      const currentToken = localStorage.getItem("authToken");
      const res = await axios.get(
        `${API_URL}voiceCall/logs?subscriberId=${subId}&limit=50`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      if (isCurrentSubscriber(subId)) {
        setVoiceCallLogs(res?.data?.items || []);
      }
    } catch (err) {
      console.warn("Could not load AI voice call logs:", err?.message);
      if (isCurrentSubscriber(subId)) {
        setVoiceCallLogs([]);
      }
    } finally {
      if (isCurrentSubscriber(subId)) {
        setVoiceCallLogsLoading(false);
      }
    }
  };

  const fetchSubscriberVoice = async (subId) => {
    if (!subId) return;
    setSubscriberVoiceLoading(true);
    try {
      const currentToken = localStorage.getItem("authToken");
      const res = await axios.get(
        `${API_URL}voiceCall/subscriber-voice/${subId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      if (isCurrentSubscriber(subId)) {
        setSubscriberVoiceData(res?.data?.data || null);
      }
    } catch (err) {
      console.warn("Could not load subscriber voice data:", err?.message);
      if (isCurrentSubscriber(subId)) {
        setSubscriberVoiceData(null);
      }
    } finally {
      if (isCurrentSubscriber(subId)) {
        setSubscriberVoiceLoading(false);
      }
    }
  };

  const handleDeleteSubscriberVoice = async (voiceRecordId) => {
    if (!voiceRecordId) return;
    if (!window.confirm("Are you sure you want to delete this subscriber voice record?")) {
      return;
    }
    setIsDeletingVoice(true);
    try {
      const currentToken = localStorage.getItem("authToken");
      await axios.delete(
        `${API_URL}voiceCall/subscriber-voice/${voiceRecordId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      showToast("Subscriber voice record deleted successfully", "success");
      setSubscriberVoiceData(null);
    } catch (err) {
      console.error("Failed to delete subscriber voice:", err);
      showToast(err?.response?.data?.message || "Failed to delete record", "error");
    } finally {
      setIsDeletingVoice(false);
    }
  };

  const [isEditingSubscriberVoice, setIsEditingSubscriberVoice] = useState(false);
  const [editVoiceForm, setEditVoiceForm] = useState({
    dnd: 0,
    dndReason: "",
    aiWarmthScore: "",
    aiTags: "",
    whatsappConsent: 0,
  });
  const [isSavingVoice, setIsSavingVoice] = useState(false);

  const handleOpenEditSubscriberVoice = () => {
    if (!subscriberVoiceData) return;
    setEditVoiceForm({
      dnd: subscriberVoiceData.dnd || 0,
      dndReason: subscriberVoiceData.dnd_reason || "",
      aiWarmthScore:
        subscriberVoiceData.ai_warmth_score !== null &&
        subscriberVoiceData.ai_warmth_score !== undefined
          ? subscriberVoiceData.ai_warmth_score
          : "",
      aiTags: subscriberVoiceData.ai_tags || "",
      whatsappConsent: subscriberVoiceData.whatsapp_consent || 0,
    });
    setIsEditingSubscriberVoice(true);
  };

  const handleSaveSubscriberVoice = async (e) => {
    if (e) e.preventDefault();
    if (!subscriberVoiceData?.id) return;
    setIsSavingVoice(true);
    try {
      const currentToken = localStorage.getItem("authToken");
      await axios.patch(
        `${API_URL}voiceCall/subscriber-voice/${subscriberVoiceData.id}`,
        {
          dnd: Number(editVoiceForm.dnd),
          dndReason: editVoiceForm.dndReason,
          aiWarmthScore:
            editVoiceForm.aiWarmthScore !== ""
              ? Number(editVoiceForm.aiWarmthScore)
              : null,
          aiTags: editVoiceForm.aiTags,
          whatsappConsent: Number(editVoiceForm.whatsappConsent),
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      showToast("Subscriber voice record updated successfully", "success");
      setIsEditingSubscriberVoice(false);
      fetchSubscriberVoice(subscriberVoiceData.subscriber_id || id);
      fetchSubscriberVoiceLogs(subscriberVoiceData.subscriber_id || id);
    } catch (err) {
      console.error("Failed to update subscriber voice:", err);
      showToast(err?.response?.data?.message || "Failed to update record", "error");
    } finally {
      setIsSavingVoice(false);
    }
  };

  const [isEditing, setIsEditing] = useState("");
  const [isModalOpenCvlKycModal, setIsModalOpenCvlKycModal] = useState(false);
  const [isModalOpenVerifyKycModal, setIsModalOpenVerifyKycModal] =
    useState(false);
  const [isModalOpenAddTicketModal, setIsModalOpenAddTicketModal] =
    useState(false);
  const [isModalOpenFreeServiceModal, setIsModalOpenFreeServiceModal] =
    useState(false);
  const [isModalOpenReminderModal, setIsModalOpenReminderModal] =
    useState(false);
  const [isModalOpenAddOrderModal, setIsModalOpenAddOrderModal] =
    useState(false);
  const [isModalOpenAddPaidServiceModal, setIsModalOpenAddPaidServiceModal] =
    useState(false);
  const [isModalOpenSendSmsModal, setIsModalOpenSendSmsModal] = useState(false);
  const [
    isModalOpenSendPushNotificationModal,
    setIsModalOpenSendPushNotificationModal,
  ] = useState(false);
  const [isModalOpenCartAddModal, setIsModalOpenCartAddModal] = useState(false);
  const [isModalOpenCallOverviewModal, setIsModalOpenCallOverviewModal] =
    useState(false);
  const [selectedCallSummary, setSelectedCallSummary] = useState(null);

  const closeModal = () => {
    setIsModalOpenVerifyKycModal(false);
    setIsModalOpenAddTicketModal(false);
    setIsModalOpenFreeServiceModal(false);
    setIsModalOpenReminderModal(false);
    setIsModalOpenAddTicketModal(false);
    setIsModalOpenAddOrderModal(false);
    setIsModalOpenAddPaidServiceModal(false);
    setIsModalOpenSendSmsModal(false);
    setIsModalOpenSendPushNotificationModal(false);
    setIsModalOpenCartAddModal(false);
    setIsModalOpenCallOverviewModal(false);
    setSelectedCallSummary(null);
  };

  const toggleModalVerifyKycModal = () => {
    setIsModalOpenVerifyKycModal(!isModalOpenVerifyKycModal);
  };
  const toggleModalAddTicketModal = () => {
    setIsModalOpenAddTicketModal(!isModalOpenAddTicketModal);
  };
  const toggleModalOpenFreeServiceModal = () => {
    setIsModalOpenFreeServiceModal(!isModalOpenFreeServiceModal);
  };
  const toggleModalOpenReminderModal = () => {
    setIsModalOpenReminderModal(!isModalOpenReminderModal);
  };
  const toggleModalOpenAddOrderModal = () => {
    setIsModalOpenAddOrderModal(!isModalOpenAddOrderModal);
  };
  const toggleModalOpenAddPaidServiceModal = () => {
    setIsModalOpenAddPaidServiceModal(!isModalOpenAddPaidServiceModal);
  };

  const toggleModalOpenSendSmsModal = () => {
    setIsModalOpenSendSmsModal(!isModalOpenSendSmsModal);
  };

  const toggleModalOpenSendPushNotificationModal = () => {
    setIsModalOpenSendPushNotificationModal(
      !isModalOpenSendPushNotificationModal
    );
  };

  const toggleModalOpencardaddModal = () => {
    setIsModalOpenCartAddModal(!isModalOpenCartAddModal);
  };

  // Function to create single dropdown box
  const SingleDropdownBox = ({ name, options, value, onChange }) => (
    <select
      name={name}
      className="form-control"
      value={value}
      onChange={onChange}
      required
    >
      {options.map((option, index) => (
        <option key={index} value={option}>
          {option}
        </option>
      ))}
    </select>
  );

  // Modal component for adding a cart item
  const CartAddModal = ({ show, onClose, onSubmit, servicelist, id }) => {
    const [cartService, setCartService] = useState("");
    const [serviceTrade, setServiceTrade] = useState("");

    const handleSubmit = (event) => {
      event.preventDefault();
      onSubmit({ cartService, serviceTrade });
    };

    const handleServiceChange = (e) => {
      setCartService(e.target.value);
      // You may fetch corresponding trades for this service
    };

    return (
      show && (
        <div
          id="cardaddmodal"
          className="modal fade show"
          style={{ display: "block" }}
        >
          <div className="modal-dialog modal-md">
            <div className="modal-content">
              <div className="modal-header">
                <div className="col-md-6">
                  <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                    Add Cart Item
                  </h5>
                </div>
                <div className="col-md-6">
                  <button type="button" className="close" onClick={onClose}>
                    &times;
                  </button>
                </div>
                <div className="clearfix"></div>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <input type="hidden" name="subid" value={id} />
                  <div className="panel-body">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="form-group">
                          <label className="control-label">Service</label>
                          <SingleDropdownBox
                            name="cart_service"
                            options={servicelist}
                            value={cartService}
                            onChange={handleServiceChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="form-group">
                          <label className="control-label">Trades</label>
                          <select
                            name="servicetrade"
                            className="form-control"
                            value={serviceTrade}
                            onChange={(e) => setServiceTrade(e.target.value)}
                            required
                          >
                            {/* Assuming trades will be populated dynamically */}
                            <option value="">Select Trade</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-12" style={{ textAlign: "right" }}>
                      <button
                        type="submit"
                        name="submit"
                        className="btn btn-primary"
                      >
                        Submit
                      </button>
                      <button
                        type="button"
                        name="cancel"
                        className="btn btn-default"
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )
    );
  };

  // Main App component
  const [showCartModal, setShowCartModal] = useState(false);

  const handleCartSubmit = (formData) => {
    console.log("Cart Item Submitted:", formData);
    setShowCartModal(false);
  };

  const handleVerifyKycSubmit = async (formData) => {
    try {
      setLoading(true);
      const response = await axios.put(
        `${API_URL}subscriber/save-kyc`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        showToast.success("KYC verification updated successfully");
        // Close the modal
        setIsModalOpenVerifyKycModal(false);
        // Refresh KYC details
        fetchKycDetails(id);
        // Refresh KYC logs
        fetchKycLogs(id);
        // Auto-mark as read if unread
        markAsReadAutomatically();
        window.location.reload();
      } else {
        showToast.error("Failed to verify KYC");
        setErrorMessages((prev) => ({
          ...prev,
          kycVerify: response.data.message || "Failed to verify KYC",
        }));
      }
    } catch (error) {
      console.error("Error verifying KYC:", error);
      showToast.error("Failed to verify KYC");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEdit = (fieldName) => {
    // Store original value before editing
    const currentValue = getCurrentFieldValue(fieldName);
    setOriginalValues((prev) => ({
      ...prev,
      [fieldName]: currentValue,
    }));
    setIsEditing(fieldName);
  };

  // Helper function to get current field value
  const getCurrentFieldValue = (fieldName) => {
    const fieldMap = {
      lead_source: leadSource,
      fullname: fullname,
      email: email,
      mobileno: mobileno,
      otherphone: otherphone,
      dob: dob,
      state: state,
      occupation: occupation,
      language: language,
      exp_in_market: expInMarket,
      investments: investments,
      convertible: convertible,
      inv_capital: invCapital,
      demat_brokers: dematBrokers,
      risk_level: riskLevel,
      r_assigned: rAssigned,
      assigned_to_research: rAssigned,
      assignedto: assignedTo,
      assigned_to: assignedTo, // Handle both field name variations
      in_folio: inFolio,
      askfps: askfps,
      lead_en: leadEn,
      assignedto_folio: assignedToFolio, // NEW: Folio assigned to
      assignedto_chat: assignedToChat, // NEW: Chat assigned to
      lead_status: leadStatus,
      client_status: clientStatus,
      subscriber_status: subscriberStatus,
      service_category: serviceCategory,
      reason_not_interested: reasonNotInterested,
      next_followup: nextFollowup,
      interested_segments: interestedSegments,
      price_expected: priceExpected,
      utm_term: utmTerm,
      bill_to: billTo,
      gstno: gstNo,
      compliance_score: complianceScore,
      ai_tag: aiTag,
    };
    return fieldMap[fieldName] || "";
  };

  // Handle blur event - revert to original value if no change was made
  const handleBlur = (fieldName) => {
    // Normalize field name for assignedTo (handle both "assignedto" and "assigned_to")
    const normalizedFieldName =
      fieldName === "assignedto" ? "assigned_to" : fieldName;
    const currentValue = getCurrentFieldValue(normalizedFieldName);
    const originalValue =
      originalValues[normalizedFieldName] || originalValues[fieldName];

    // If value hasn't changed, revert to original
    if (currentValue === originalValue) {
      revertFieldValue(normalizedFieldName, originalValue);
    }

    // Clear editing state
    setIsEditing("");
  };

  // Helper function to revert field value
  const revertFieldValue = (fieldName, originalValue) => {
    const setterMap = {
      lead_source: setLeadSource,
      fullname: setFullname,
      email: setEmail,
      mobileno: setMobileno,
      otherphone: setOtherphone,
      dob: setDob,
      state: setState,
      occupation: setOccupation,
      language: setLanguage,
      exp_in_market: setExpInMarket,
      investments: setInvestments,
      convertible: setConvertible,
      inv_capital: setInvCapital,
      demat_brokers: setDematBrokers,
      risk_level: setRiskLevel,
      r_assigned: setRAssigned,
      assigned_to_research: setRAssigned,
      assignedto: setAssignedTo,
      assigned_to: setAssignedTo, // Handle both field name variations
      in_folio: setInFolio,
      askfps: setAskfps,
      lead_en: setLeadEn,
      assignedto_folio: setAssignedToFolio, // NEW: Folio assigned to
      assignedto_chat: setAssignedToChat, // NEW: Chat assigned to
      lead_status: setLeadStatus,
      client_status: setClientStatus,
      subscriber_status: setSubscriberStatus,
      service_category: setServiceCategory,
      reason_not_interested: setReasonNotInterested,
      next_followup: setNextFollowup,
      interested_segments: setInterestedSegments,
      price_expected: setPriceExpected,
      utm_term: setUtmTerm,
      bill_to: setBillTo,
      gstno: setGstNo,
      compliance_score: setComplianceScore,
      ai_tag: setAiTag,
    };

    const setter = setterMap[fieldName];
    if (setter && originalValue !== undefined) {
      setter(originalValue);
    }
  };

  const handleChange = async (fieldname, value) => {
    try {
      // Show loading state
      setLoading(true);

      // If value hasn't actually changed (compare to original value when edit started), skip API/log
      const prevOriginal = Object.prototype.hasOwnProperty.call(
        originalValues,
        fieldname
      )
        ? originalValues[fieldname]
        : getCurrentFieldValue(fieldname);
      const prevStr = (prevOriginal == null ? "" : String(prevOriginal)).trim();
      const nextStr = (value == null ? "" : String(value)).trim();
      if (prevStr === nextStr) {
        setIsEditing("");
        setLoading(false);
        return;
      }

      // Special handling for DOB update (separate API)
      if (fieldname === "dob") {
        const token = localStorage.getItem("authToken");
        const response = await axios.put(
          `${API_URL}subscriber/update-dob/${id}`,
          { dob: value },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response?.status === 200 || response?.data?.success) {
          showToast.success("DOB updated successfully");
          setDob(value);
          setKycDetails((prev) => (prev ? { ...prev, dob: value } : prev));
          fetchActivityLogs(id);
          setIsEditing(false);
          // Auto-mark as read if unread
          markAsReadAutomatically();
        } else {
          showToast.error("Failed to update DOB");
        }
        return;
      }
      if (fieldname === "assignedto_chat") {
        const success = await handleAssignChat(id, value);
        if (success) {
          // Update local state
          setAssignedToChat(value);
          setAssignedToChatName("");
          showToast.success("Chat assignment updated successfully");
          fetchActivityLogs(id);
          setIsEditing(false);
          markAsReadAutomatically();
        } else {
          // Revert to original value
          revertFieldValue(fieldname, originalValues[fieldname]);
        }
        return;
      }

      let valueToSend = value;

      // For dropdown fields, convert ID to display value before sending to API
      switch (fieldname) {
        case "lead_source":
          const leadSourceItem = dropdownOptions.leadSourceList.find(
            (item) => item.id == value
          );
          valueToSend = leadSourceItem ? leadSourceItem.pick_name : value;
          break;
        // case "language":
        //   const languageItem = dropdownOptions.languageList.find(item => item.id == value);
        //   valueToSend = languageItem ? languageItem.name : value;
        //   break;
        case "state":
          // Find the state by stateId and get the stateCode
          const stateItem = dropdownOptions.stateList.find(
            (item) => item.stateId == value
          );
          valueToSend = stateItem ? String(stateItem.stateCode) : value; // Send stateCode as string
          break;
        case "exp_in_market":
          const expInMarketItem = dropdownOptions.expInMarketList.find(
            (item) => item.id == value
          );
          valueToSend = expInMarketItem ? expInMarketItem.pick_name : value;
          break;
        case "investments":
          const investmentsItem = dropdownOptions.investmentList.find(
            (item) => item.id == value
          );
          valueToSend = investmentsItem ? investmentsItem.pick_name : value;
          break;
        case "convertible":
          const convertibleItem = dropdownOptions.convertibleList.find(
            (item) => item.id == value
          );
          valueToSend = convertibleItem ? convertibleItem.pick_name : value;
          break;
        case "inv_capital":
          const invCapitalItem = dropdownOptions.invCapList.find(
            (item) => item.id == value
          );
          valueToSend = invCapitalItem ? invCapitalItem.pick_name : value;
          break;
        case "demat_brokers":
          const dematBrokersItem = dropdownOptions.dematBrokersList.find(
            (item) => item.id == value
          );
          valueToSend = dematBrokersItem ? dematBrokersItem.pick_name : value;
          break;
        case "risk_level":
          const riskLevelItem = dropdownOptions.riskLevelList.find(
            (item) => item.id == value
          );
          valueToSend = riskLevelItem ? riskLevelItem.pick_name : value;
          break;
        case "r_assigned":
          valueToSend = value;
          break;
        case "assigned_to":
        case "assignedto":
          // For assigned_to, send the tu_id directly as fieldvalue (not the name)
          // The value passed is already the tu_id from the dropdown
          valueToSend = value; // Send the ID directly, don't convert to name
          break;
        case "in_folio":
        case "askfps":
        case "lead_en":
          valueToSend = String(value);
          break;
        case "assignedto_folio":
          // For folio assigned to, send the tu_id directly as fieldvalue
          valueToSend = value; // Send the ID directly
          break;
        case "assignedto_chat":
          // For chat assigned to, send the tu_id directly as fieldvalue
          valueToSend = value; // Send the ID directly
          break;
        case "lead_status":
          const leadStatusItem = dropdownOptions.leadStatusList.find(
            (item) => item.id == value
          );
          valueToSend = leadStatusItem ? leadStatusItem.pick_name : value;
          break;
        case "client_status":

          const clientStatusItem = dropdownOptions.clientStatusList.find(
            (item) => item.id == value
          );
          valueToSend = clientStatusItem ? clientStatusItem.pick_name : value;
          break;
        case "subscriber_status":
          const subscriberStatusItem =
            dropdownOptions.subscriberStatusList.find(
              (item) => item.id == value
            );
          valueToSend = subscriberStatusItem
            ? subscriberStatusItem.pick_name
            : value;
          break;
        case "reason_not_interested":
          const reasonNotInterestedItem =
            dropdownOptions.reasonNotInterestedList.find(
              (item) => item.id == value
            );
          valueToSend = reasonNotInterestedItem
            ? reasonNotInterestedItem.pick_name
            : value;
          break;
        case "interested_segments":
          const interestedSegmentsItem =
            dropdownOptions.interestSegmentList.find(
              (item) => item.id == value
            );
          valueToSend = interestedSegmentsItem
            ? interestedSegmentsItem.pick_name
            : value;
          break;
        case "price_expected":
          const priceExpectedItem = dropdownOptions.priceExpectedList.find(
            (item) => item.id == value
          );
          valueToSend = priceExpectedItem ? priceExpectedItem.pick_name : value;
          break;
        case "service_category":
          // For service_category, send the numeric value (0 or 1) directly
          valueToSend = parseInt(value); // Send the numeric value (0 or 1) directly
          break;
        case "compliance_score":
          const complianceScoreItem = dropdownOptions.complianceScoreList.find(
            (item) => item.value == value
          );
          // If Red Flag is selected, pass -1 as the fieldvalue as requested
          if (complianceScoreItem && complianceScoreItem.label === "Red Flag") {
            valueToSend = "-1";
          } else {
            valueToSend = complianceScoreItem ? complianceScoreItem.label : value;
          }
          break;
        default:
          // For non-dropdown fields, use the value as is
          valueToSend = value;
          break;
      }

      // Call your API to update the field
      const token = localStorage.getItem("authToken");

      // Map field names for API compatibility
      let apiFieldName = fieldname;
      if (fieldname === "assigned_to") {
        apiFieldName = "assignedto";
      } else if (fieldname === "assigned_to_research") {
        apiFieldName = "r_assigned";
      } else if (fieldname === "service_category") {
        apiFieldName = "source"; // Map to the actual API field name
      }
      // Send both the ID and the display value to the API
      const response = await axios.put(
        `${API_URL}subscriber/update-subscriber/${id}`,
        {
          fieldname: apiFieldName,
          fieldvalue: valueToSend, // Send the display value
          fieldid: value, // Also send the ID value
          is_dropdown: isDropdownField(fieldname), // Flag to indicate if this is a dropdown field
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        showToast.success(
          `${fieldname.replace("_", " ")} updated successfully`
        );

        const now = new Date();
        const formattedNow = now.toISOString().slice(0, 19).replace('T', ' ');
        setUpdatedOn(formattedNow);

        // If assignedto is updated, also update assignedto_folio with the same value
        if (fieldname === "assigned_to" || fieldname === "assignedto") {
          try {
            await axios.put(
              `${API_URL}subscriber/update-subscriber/${id}`,
              {
                fieldname: "assignedto_folio",
                fieldvalue: valueToSend,
                fieldid: value,
                is_dropdown: true,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          } catch (error) {
            console.error("Error updating assignedto_folio:", error);
            // Don't show error to user as main update was successful
          }
        }

        // If assignedto_folio is updated, also update assignedto with the same value
        if (fieldname === "assignedto_folio") {
          try {
            await axios.put(
              `${API_URL}subscriber/update-subscriber/${id}`,
              {
                fieldname: "assignedto",
                fieldvalue: valueToSend,
                fieldid: value,
                is_dropdown: true,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          } catch (error) {
            console.error("Error updating assignedto:", error);
            // Don't show error to user as main update was successful
          }
        }

        switch (fieldname) {
          case "lead_source":
            setLeadSource(value);
            break;
          case "fullname":
            setFullname(value);
            break;
          case "email":
            setEmail(value);
            break;
          case "mobileno":
            setMobileno(value);
            break;
          case "otherphone":
            setOtherphone(value);
            break;
          case "dob":
            setDob(value);
            break;
          case "state":
            setState(value);
            break;
          case "occupation":
            setOccupation(value);
            break;
          case "language":
            setLanguage(value);
            break;
          case "exp_in_market":
            setExpInMarket(value);
            break;
          case "investments":
            setInvestments(value);
            break;
          case "convertible":
            setConvertible(value);
            break;
          case "inv_capital":
            setInvCapital(value);
            break;
          case "demat_brokers":
            setDematBrokers(value);
            break;
          case "risk_level":
            setRiskLevel(value);
            break;
          case "r_assigned":
          case "assigned_to_research":
            setRAssigned(value);
            setRAssignedName("");
            break;
          case "assigned_to":
          case "assignedto":
            setAssignedTo(value);
            setAssignedToName("");
            // Also update Folio Assign To with the same value
            setAssignedToFolio(value);
            setAssignedToFolioName("");
            break;
          case "in_folio":
            setInFolio(parseInt(value, 10));
            break;
          case "askfps":
            setAskfps(parseInt(value, 10));
            break;
          case "lead_en":
            setLeadEn(parseInt(value, 10) || 0);
            break;
          case "assignedto_folio":
            setAssignedToFolio(value);
            setAssignedToFolioName("");
            // Also update Assign To with the same value
            setAssignedTo(value);
            setAssignedToName("");
            break;
          case "assignedto_chat":
            setAssignedToChat(value);
            setAssignedToChatName("");
            break;
          case "lead_status":
            setLeadStatus(value);
            fetchJourneyData(id);
            break;
          case "client_status":
            setClientStatus(value);
            break;
          case "subscriber_status":
            setSubscriberStatus(value);
            break;
          case "service_category":
            setServiceCategory(value);
            break;
          case "reason_not_interested":
            setReasonNotInterested(value);
            break;
          case "next_followup":
            setNextFollowup(value);
            break;
          case "interested_segments":
            setInterestedSegments(value);
            break;
          case "price_expected":
            setPriceExpected(value);
            break;
          case "utm_term":
            setUtmTerm(value);
            break;
          case "bill_to":
            setBillTo(value);
            break;
          case "gstno":
            setGstNo(value);
            break;
          case "compliance_score":
            setComplianceScore(value);
            break;
          case "ai_tag":
            setAiTag(value);
            break;
          default:
            break;
        }
        fetchActivityLogs(id);

        setIsEditing(false);
        // Auto-mark as read if unread
        markAsReadAutomatically();
      } else {
        showToast.error("Update failed");
        setErrorMessages((prev) => ({ ...prev, update: "Update failed." }));
      }
    } catch (error) {
      console.error("Update error:", error);
      showToast.error("Something went wrong while updating");
    } finally {
      setLoading(false);
    }
  };

  const isDropdownField = (fieldname) => {
    const dropdownFields = [
      "lead_source",
      "language",
      "state",
      "exp_in_market",
      "investments",
      "convertible",
      "inv_capital",
      "demat_brokers",
      "risk_level",
      "r_assigned",
      "assigned_to_research",
      "assigned_to",
      "in_folio",
      "askfps",
      "lead_en",
      "assignedto_folio",
      "assignedto_chat",
      "lead_status",
      "client_status",
      "subscriber_status",
      "reason_not_interested",
      "interested_segments",
      "price_expected",
      "service_category",
      "compliance_score",
      "ai_tag",
    ];

    return dropdownFields.includes(fieldname);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "00:00";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const handleAddTicketSubmit = async (ticketData) => {
    if (!ticketData.ticketCategory) {
      showToast.error("Select Ticket Category");
      return;
    }

    if (!ticketData.ticketHeading) {
      showToast.error("Ticket Heading is Required");
      return;
    }

    const formData = new FormData();
    formData.append("subscriberid", id);
    formData.append("categoryid", ticketData.ticketCategory);
    formData.append("ticket_title", ticketData.ticketHeading);
    formData.append("ticket_description", ticketData.ticketDesc);
    formData.append(`attachments`, ticketData.ticketImage);

    try {
      // setLoading(true);
      const response = await axios.post(
        `${API_URL}subscriber/save-ticket`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response.data.success) {
        showToast.success("Ticket Added Successfully");
        fetchSubscriber();
        fetchOpenTickets(id);
        // setIsModalOpenAddTicketModal(false);
        // Auto-mark as read if unread
        markAsReadAutomatically();
        return true;
      }
    } catch (error) {
      showToast.error(error);
    }
  };

  const handleSetReminder = async (reminderData) => {
    const payload = {
      fieldname: "reminder_time",
      fieldvalue: reminderData.reminder,
    };

    try {
      const response = await axios.put(
        `${API_URL}subscriber/update-subscriber/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        showToast.success("Reminder Set Successfully");
        fetchSubscriber();
        setIsModalOpenReminderModal(false);
        markAsReadAutomatically();
      }
    } catch (error) {
      showToast.error(error);
    }
  };

  const handleAddFreeService = async (freeServiceData) => {
    const payload = {
      subscriberid: id, // matches DTO (optional)
      fullname: freeServiceData.fullname || "", // optional
      mobileno: freeServiceData.mobileno || "", // optional
      email: freeServiceData.email || "", // optional
      languageid: freeServiceData.languageid
        ? Number(freeServiceData.languageid)
        : undefined, // optional
      serviceid: Number(freeServiceData.serviceid), // required
      // trades_total: 1, // Enforce 1 credit for Free Service
      trades_total: parseInt(freeServiceData.tradesTotal),
      stype: "Free M", // matches DTO optional field
    };

    try {
      setServiceDetailsLoading(true);
      const response = await axios.post(
        `${API_URL}subscription/save-free-service`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        showToast.success("Free Service Added Successfully");
        setIsModalOpenFreeServiceModal(false);
        // Auto-mark as read if unread
        markAsReadAutomatically();
        fetchServiceSubscriptionReports(id);
        fetchComments(id);
      }
    } catch (error) {
      showToast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add Free Service"
      );
      setServiceDetailsLoading(false);
    }
  };

  const handleOpenRenewModal = (service) => {
    setRenewingService(service);
    setIsRenewModalOpen(true);
  };

  const handleConfirmRenewCredit = async () => {
    if (!renewingService?.id) return;
    try {
      setIsRenewLoading(true);
      const response = await axios.post(
        `${API_URL}subscription/renew-free-credit/${renewingService.id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        showToast.success(
          response.data.message || "1 Credit added and service renewed successfully!"
        );
        setIsRenewModalOpen(false);
        setRenewingService(null);
        fetchServiceSubscriptionReports(id);
        fetchComments(id);
      }
    } catch (error) {
      showToast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to renew credit"
      );
    } finally {
      setIsRenewLoading(false);
    }
  };


  const handleAddPaidServiceOrder = async (paidServiceData) => {
    const isAddon = paidServiceData.addon === 1;
    const payload = {
      serviceId: Number(paidServiceData.serviceid),
      orderId: Number(paidServiceData.orderid),
      tradesTotal: Number(paidServiceData.trade_purchase),
      amount: isAddon ? 0 : Number(paidServiceData.amount),
      activationDate: paidServiceData.activation_date
        ? (paidServiceData.activation_date instanceof Date
            ? paidServiceData.activation_date.toISOString().split("T")[0]
            : moment(paidServiceData.activation_date).format("YYYY-MM-DD"))
        : null,
      stype: "Paid",
      status: "In Progress",
      expiryDate: null,
      referralcode: paidServiceData.referralcode || null,
      addon: isAddon ? 1 : 0,
    };

    try {
      setServiceDetailsLoading(true);
      const response = await axios.post(
        `${API_URL}subscription/save-paid-popup`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        showToast.success(
          isAddon
            ? "Add On Order Added Successfully"
            : "Paid Service Order Added Successfully"
        );
        setIsModalOpenAddPaidServiceModal(false);
        markAsReadAutomatically();
        fetchServiceSubscriptionReports(id);
      }
    } catch (error) {
      showToast.error(error?.response?.data?.message || "Something went wrong");
      setServiceDetailsLoading(false);
    }
  };


  const handleAddOrderData = async (orderData) => {
    const payload = {
      subscriberid: id,
      orderid: orderData.order,
      pending_amount: orderData.pending_amount,
      order_type: parseInt(orderData.order_type),
      actual_amount: parseInt(orderData.actual_amount),
      pending_amount: parseInt(orderData.pending_amount),
      discount_amount: parseInt(orderData.discount_amount),
      paid_amount: parseInt(orderData.paid_amount),
      pay_date: orderData.payment_date,
      order_due_date: orderData.order_due_date,
      pay_mode: orderData.payment_mode,
      pay_refno: orderData.payment_reference,
      billedto: orderData.billed_to,
      gstin: orderData.gst_no,
      state_code: String(orderData.state),
      referralcode: orderData.referralcode || null,
    };

    try {
      // setLoading(true);
      const response = await axios.post(
        `${API_URL}orders/save-order-popup`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        showToast.success(response.data.message);

        // Update subscriber state if it's currently empty
        if (!state && orderData.state) {
          const stateItem = dropdownOptions.stateList.find(
            (item) => String(item.stateCode) === String(orderData.state)
          );
          if (stateItem) {
            handleChange("state", stateItem.stateId);
          }
        }

        // setIsModalOpenAddOrderModal(false);
        // Auto-mark as read if unread
        markAsReadAutomatically();
        return true;
      }
    } catch (error) {
      showToast.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmalCARSubmit = async () => {
    if (!token) {
      showToast.error("Authorization token not found.");
      return;
    }

    const payload = {
      subscriberId: id,
    };

    try {
      setIsSendingEmail(true);
      const response = await axios.post(
        `${API_URL}subscriber/send-car`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`, // ✅ Headers
          },
        }
      );
      if (response.status === 201) {
        showToast.success(response.data.message);
      }
    } catch (error) {
      showToast.error(error);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSendPushNotificationSubmit = async (payload) => {
    if (!token) {
      showToast.error("Authorization token not found.");
      return false;
    }

    try {
      const response = await axios.post(
        `${API_URL}subscription/send-subscription-push`,
        {
          title: payload.title,
          message: payload.message,
          checkingAll: false,              // ✅ static
          selectedIds: [payload.subscriberId], // ✅ API expects array
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        showToast.success(
          response.data.message || "Push Notification Send Successfully"
        );
        return true;
      }
    } catch (error) {
      showToast.error(
        error?.response?.data?.message || "Failed to send push notification"
      );
      return false;
    }
  };


  const handleCheckPushNotification = async () => {
    try {
      setIsCheckingPush(true);
      const response = await axios.post(
        `${API_URL}subscriber/push-notification`,
        { subscriberId: id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "accept": "*/*"
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        showToast.success(response.data.message || "Push Notification Checked Successfully");
      }
    } catch (error) {
      console.error("Check push notification error:", error);
      showToast.error(error?.response?.data?.message || "Failed to check push notification");
    } finally {
      setIsCheckingPush(false);
    }
  };


  const handleSendSmsSubmit = async (payload) => {
    if (!token) {
      showToast.error("Authorization token not found.");
      return;
    }
    try {
      // setLoading(true);
      const response = await axios.post(
        `${API_URL}subscriber/send-sms`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`, // ✅ Headers
          },
        }
      );
      if (response.data.success) {
        showToast.success("SMS Send Successfully");
        markAsReadAutomatically();
        return true;
      }
    } catch (error) {
      showToast.error(error);
    }
  };

  const handleRedCallClick = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}subscriber/makeCallToSubscriber`,
        { id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLoading(false);
      if (response.data && response.data.msg) {
        // Use your toast or alert
        showToast.success(response.data.msg);
      } else {
        showToast.success("Call initiated.");
      }
    } catch (error) {
      setLoading(false);
      showToast.error("Something went wrong.");
    }
  };

  const handleAssignChat = async (subscriberId, assignToId) => {
    if (!subscriberId || !assignToId) {
      showToast.error("Subscriber ID and Assign To ID are required");
      return false;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}subscriber/assign-chat`,
        {
          id: subscriberId,
          assignto_id: assignToId
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        // showToast.success("Chat assigned successfully");
        // markAsReadAutomatically();
        return true;
      } else {
        showToast.error("Failed to assign chat");
        return false;
      }
    } catch (error) {
      console.error("Error assigning chat:", error);
      if (error.response) {
        showToast.error(error.response.data?.message || "Failed to assign chat");
      } else {
        showToast.error("Network error. Please try again.");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchAcceptanceData = async (subscriberId) => {
    try {
      setAcceptanceLoading(true);
      const currentToken = localStorage.getItem("authToken") || token;

      const response = await axios.get(
        `${API_URL}subscriber/get-acceptance`,
        {
          headers: { Authorization: `Bearer ${currentToken}` },
          params: { subscriberid: subscriberId }
        }
      );

      if (!isCurrentSubscriber(subscriberId)) return;
      if (response.data.success && Array.isArray(response.data.data)) {

        const sorted = response.data.data.sort(
          (a, b) => new Date(b.accepted_on) - new Date(a.accepted_on)
        );

        setAcceptanceData(sorted);  // <-- SAVE ALL RECORDS
      }
    } catch (error) {
      console.error("Error fetching acceptance data:", error);
    } finally {
      if (isCurrentSubscriber(subscriberId)) setAcceptanceLoading(false);
    }
  };
  const handleDownloadAcceptancePdf = async (acceptanceId) => {
    if (!acceptanceId || downloadingAcceptanceId) return;

    try {
      setDownloadingAcceptanceId(acceptanceId);
      const currentToken = localStorage.getItem("authToken") || token;

      const response = await axios.get(
        `${API_URL}subscriber/download-acceptance-pdf/${acceptanceId}`,
        {
          headers: { Authorization: `Bearer ${currentToken}` },
          responseType: "blob",
        }
      );

      let fileName = `Terms of Service Agreement - ${subscriberId || acceptanceId}.pdf`;
      const disposition = response.headers ? response.headers["content-disposition"] : null;
      if (disposition && disposition.indexOf("filename=") !== -1) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (matches != null && matches[1]) {
          fileName = matches[1].replace(/['"]/g, "");
        }
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading acceptance PDF:", error);
      const status = error.response?.status;
      if (!error.response) {
        // No response at all: usually a download manager extension took over the request and saved the file itself
        showToast.info("If the download did not start, please check your connection and try again");
      } else if (status === 404) {
        showToast.error("Agreement record not found");
      } else if (status === 401) {
        showToast.error("Session expired. Please login again.");
      } else {
        showToast.error("Unable to generate PDF, please try again");
      }
    } finally {
      setDownloadingAcceptanceId(null);
    }
  };

  // Add this function with your other handler functions
  const openPanLightbox =(panCopyUrl) => {
    if (!panCopyUrl) {
      showToast.error("No PAN copy available to view");
      return;
    }

    // Check if the URL is already a full URL
    let imageUrl = panCopyUrl;

    // If it doesn't start with http, prepend API_URL
    if (!imageUrl.startsWith('http')) {
      imageUrl = `${API_URL}${imageUrl}`;
    }

    const slides = [
      {
        src: imageUrl,
        alt: "PAN Copy"
      }
    ];

    setLightboxSlides(slides);
    setLightboxIndex(0);
    setLightboxOpen(true);
  };

  const handleDownloadKycRes = async (kycResData) => {
    if (!kycResData && !id) return;

    try {
      const token = localStorage.getItem("authToken");
      const cleanData = String(kycResData || "").trim();
      const isPdfUrl = cleanData.toLowerCase().includes(".pdf");
      const defaultFileName = `${subscriberId || kycDetails?.pan_number || "kyc_details"}.${isPdfUrl ? "pdf" : "json"}`;

      // Direct backend download endpoint (streams S3 attachment with Content-Disposition)
      const response = await axios.get(`${API_URL}cvl-kyc/download/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      let fileName = defaultFileName;
      const disposition = response.headers ? response.headers["content-disposition"] : null;
      if (disposition && disposition.indexOf("filename=") !== -1) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (matches != null && matches[1]) {
          fileName = matches[1].replace(/['"]/g, "");
        }
      }

      const contentType = response.data?.type || (isPdfUrl ? "application/pdf" : "application/json");
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showToast.success("KYC document downloaded successfully!");
    } catch (err) {
      console.error("Backend download error, trying direct link fallback:", err);
      if (kycResData && (kycResData.startsWith("http://") || kycResData.startsWith("https://"))) {
        const isPdf = kycResData.toLowerCase().includes(".pdf");
        const link = document.createElement("a");
        link.href = kycResData;
        link.download = `${subscriberId || "kyc_details"}.${isPdf ? "pdf" : "json"}`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        showToast.error("Failed to download KYC document");
      }
    }
  };

  const formatDate = (date) => {
    if (
      !date ||
      date === "0000-00-00" ||
      date === "0000-00-00 00:00:00" ||
      date.includes("1899") ||  // Check for any 1899 dates
      date === "30/11/1899" || // Specific check
      date === "1899-11-30"    // Check for ISO format
    ) {
      return "";
    }
    return date;
  };

  const isSubscribed = (dropdownOptions.leadStatusList || []).find(item => item.id == leadStatus)?.pick_name === "Subscribed" || leadStatus === "Subscribed";

  // Alternate mobile accepts exactly 10 digits or "NA"; empty just closes the editor
  const saveOtherphone = () => {
    if (otherphone.length === 0) {
      handleBlur("otherphone");
    } else if (otherphone !== "NA" && !/^\d{10}$/.test(otherphone)) {
      showToast.warning("Alternate mobile number must be exactly 10 digits or NA");
    } else {
      handleChange("otherphone", otherphone);
    }
  };

  // Comments and AI Call Details share one row; each takes full width if the other is hidden
  const showCommentsPanel =
    hasPermission("subscriber_comment", "view") ||
    hasPermission("subscriber_ai_comments", "view") ||
    hasPermission("subscriber_summary", "view");
  const showAiCallDetailsPanel = hasPermission("subscriber_voice", "view");
  const commentsRowColClass =
    showCommentsPanel && showAiCallDetailsPanel ? "col-md-6" : "col-md-12";

  return (
    <div className="panel-content subscriber-detail mt-5">
      {/* {loading && <LoadingSpinner />} */}
      {/* {(loading || isRendering) && <LoadingSpinner />} */}
      <div className="panel panel-default with-rounded-corner">
        <div className="row pb-2">
          <div className="col-md-3 mt-4">

            {/* Profile + Name + Icons Row */}
            <div className="d-flex align-items-center flex-wrap">

              <img
                src={profileImg}
                alt="Profile"
                style={{ width: "32px", marginRight: "10px" }}
              />

              <div className="d-flex align-items-center flex-wrap">

                <span id="subscribername" className="me-1">
                  {subscriberLoading ? (
                    <span className="skeleton-text" style={{ width: "120px", display: "inline-block" }}></span>
                  ) : (
                    fullname
                  )}
                </span>

                {istrade === 1 && (
                  <span title="Trade active" className="ms-1">
                    <FontAwesomeIcon
                      icon={faMobileAlt}
                      className="text-primary"
                      style={{ fontSize: "1.1rem" }}
                    />
                  </span>
                )}

                {isfolio === 1 && (
                  <span title="Folio active" className="ms-1">
                    <FontAwesomeIcon
                      icon={faMobileAlt}
                      className="text-success"
                      style={{ fontSize: "1.1rem" }}
                    />
                  </span>
                )}

                {verifyKyc === 1 && (
                  <FontAwesomeIcon
                    icon={faIdCard}
                    className="text-success ms-1"
                    style={{ fontSize: "1.1rem" }}
                  />
                )}

                {verifyUser === 1 && (
                  <img
                    src={blueTick}
                    alt="Verified"
                    className="ms-1"
                    style={{ width: "18px" }}
                    title="Verified"
                  />
                )}

                {(Boolean(aiTag) ||
                  Boolean(subscriberVoiceData?.ai_last_called_on) ||
                  Boolean(subscriberVoiceData?.last_call_log_id) ||
                  Number(subscriberVoiceData?.ai_call_count_30d) > 0 ||
                  (voiceCallLogs && voiceCallLogs.length > 0)) && (
                  <span
                    title={aiTag ? `AI Called (${aiTag})` : "AI Voice Called"}
                    className="ms-1"
                  >
                    <FontAwesomeIcon
                      icon={faRobot}
                      style={{
                        fontSize: "1.1rem",
                        color: "#6f42c1",
                        verticalAlign: "middle",
                      }}
                    />
                  </span>
                )}

                {complianceScore !== "" &&
                  complianceScore != null &&
                  complianceScore != 0 && (
                    <span
                      className={`ms-2 ${complianceScore === "Red Flag" || complianceScore == -1
                        ? "d-inline-flex align-items-center justify-content-center"
                        : "badge rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm"
                        }`}
                      style={{
                        width: "22px",
                        height: "22px",
                        minWidth: "22px",
                        backgroundColor: "transparent",
                        border:
                          complianceScore === "Red Flag" || complianceScore == -1
                            ? "none"
                            : "1px solid #28a745",
                        color:
                          complianceScore === "Red Flag" || complianceScore == -1
                            ? "#dc3545"
                            : "#28a745",
                        fontSize:
                          complianceScore === "Red Flag" || complianceScore == -1
                            ? "0.8rem"
                            : "0.6rem",
                        lineHeight: "1",
                        padding: "0",
                      }}
                      title="Compliance Score"
                    >
                      {complianceScore === "Red Flag" || complianceScore == -1 ? (
                        <FontAwesomeIcon icon={faFlag} />
                      ) : (
                        `${complianceScore}%`
                      )}
                    </span>
                  )}

              </div>
            </div>

            {/* Journey BELOW Name */}
            <div className="mt-2">
              <JourneyMiniFlow
                journeyData={journeyData}
                loading={journeyLoading}
              />
            </div>

          </div>



          <div className="col-md-9" style={{ textAlign: "right" }}>
            {nextId !== 0 && nextId !== "" && nextId !== null && (
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1"
                onClick={() =>
                  navigate(`/Subscriber/edit/${nextId}`, {
                    state: { filterParams: filters },
                  })
                }
                title="Next Subscriber"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            )}
            {previousId !== 0 && previousId !== "" && previousId !== null && (
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1"
                onClick={() =>
                  navigate(`/Subscriber/edit/${previousId}`, {
                    state: { filterParams: filters },
                  })
                }
                title="Previous Subscriber"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
            )}
            <PermissionWrapper module="send_sms" action="add">
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1 anchorSMS"
                onClick={toggleModalOpenSendSmsModal}
              >
                <FontAwesomeIcon icon={faComment} /> Send SMS
              </button>
            </PermissionWrapper>
            {subscriberToken !== "" && subscriberToken !== null && (
              <>
                <PermissionWrapper module="send_push_notification" action="add">
                  <button
                    className="btn btn-default btn-sm pull-right mx-1 my-1 anchorPushNotification"
                    onClick={toggleModalOpenSendPushNotificationModal}
                  >
                    <FontAwesomeIcon icon={faBell} /> Send Notification
                  </button>
                </PermissionWrapper>
                <PermissionWrapper
                  module="check_push_notification"
                  action="add"
                >
                  <button
                    className="btn btn-default btn-sm pull-right mx-1 my-1 checkPushToken"
                    data-pushtoken={val_pushToken}
                    onClick={handleCheckPushNotification}
                    disabled={isCheckingPush}
                  >
                    {isCheckingPush ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Checking...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faBell} /> Check Push Notification
                      </>
                    )}
                  </button>
                </PermissionWrapper>
              </>
            )}
            <PermissionWrapper module="send_email_car" action="add">
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1 anchorMail"
                onClick={handleSendEmalCARSubmit}
                disabled={isSendingEmail || loading || subscriberLoading} // Disable when sending or overall loading
              >
                {isSendingEmail ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faEnvelope} /> Send Email CAR
                  </>
                )}
              </button>
            </PermissionWrapper>
            <PermissionWrapper module="add_free_service" action="add">
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1 anchorFreeService"
                onClick={toggleModalOpenFreeServiceModal}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Free Service
              </button>
            </PermissionWrapper>
            <PermissionWrapper module="add_paid_service_order" action="add">
              <button
                className="btn btn-primary btn-sm pull-right mx-1 my-1 viewPopupPaidOrder"
                onClick={toggleModalOpenAddPaidServiceModal}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Paid Service Order
              </button>
            </PermissionWrapper>
            <PermissionWrapper module="add_order" action="add">
              <button
                className="btn btn-primary btn-sm pull-right mx-1 my-1 btnPlaceOrder"
                onClick={toggleModalOpenAddOrderModal}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Order
              </button>
            </PermissionWrapper>
            <PermissionWrapper module="set_reminder" action="add">
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1 anchorReminderTime"
                onClick={toggleModalOpenReminderModal}
              >
                <FontAwesomeIcon icon={faClock} />{" "}
                {reminderTime && moment(reminderTime).isSameOrAfter(moment())
                  ? moment(reminderTime).format("DD MMM, YYYY hh:mm A")
                  : "No Reminder Set"}
              </button>
            </PermissionWrapper>
            <PermissionWrapper module="add_ticket" action="add">
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1 anchorTicketService"
                title="Add Ticket"
                onClick={toggleModalAddTicketModal}
              >
                <FontAwesomeIcon icon={faTicket} /> Add Ticket
              </button>
            </PermissionWrapper>

            <button
              className="btn btn-default btn-sm pull-right mx-1 my-1"
              onClick={() =>
                navigate(`/Subscriber`, {
                  state: { filterParams: filters },
                })
              }
              title="Go Back To Subscriber"
            >
              <FontAwesomeIcon icon={faChevronCircleLeft} /> Go Back
            </button>
            <button
              className={`btn btn-sm pull-right mx-1 my-1 ${isRead === 1 ? "btn-success" : "btn-warning"
                }`}
              disabled
              title={isRead === 1 ? "Mark As New" : "Mark As Contacted"}
              style={{ cursor: "default" }}
            >
              {isRead === 1 ? "Mark As New" : "Mark As Contacted"}
            </button>           
            <PermissionWrapper module="sf_device_refresh" action="view">
              <button
                className="btn btn-primary btn-sm pull-right mx-1 my-1 anchorRefreshFolioDevice"
                onClick={() => handleRefreshDeviceId(1)}
                disabled={isRefreshingFolioDevice || loading || subscriberLoading}
                title="SF Device Refresh"
              >
                {isRefreshingFolioDevice ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSync} /> SF Device Refresh
                  </>
                )}
              </button>
            </PermissionWrapper>
            <PermissionWrapper module="sg_device_refresh" action="view">
              <button
                className="btn btn-primary btn-sm pull-right mx-1 my-1 anchorRefreshDevice"
                onClick={() => handleRefreshDeviceId(0)}
                disabled={isRefreshingDevice || loading || subscriberLoading}
                title="SG Device Refresh"
              >
                {isRefreshingDevice ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSync} /> SG Device Refresh
                  </>
                )}
              </button>
            </PermissionWrapper>
            <PermissionWrapper module="send_email_notification" action="add">
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1 anchorMail"
                onClick={() => {
                  setShowEmailNotificationModal(true);
                  fetchEmailNotificationCampaignTemplates();
                }}
                title="Send Email Notification"
              >
                <FontAwesomeIcon icon={faEnvelope} /> Send Email Notification
              </button>
            </PermissionWrapper>
            <PermissionWrapper module="send_sf_intro" action="add">
              <button
                className="btn btn-default btn-sm pull-right mx-1 my-1 anchorMail"
                onClick={() => {
                  setShowSfIntroModal(true);
                  fetchSfCampaignTemplates();
                }}
                title="Send SF Intro Notification"
              >
                <FontAwesomeIcon icon={faPaperPlane} /> Send SF Intro
              </button>
            </PermissionWrapper>
          </div>
        </div>

        {/* Hidden input and table - No PHP needed here */}
        <div className="row">
          <div className="col-md-4">
            <div className="panel panel-default with-rounded-corner with-shadow">
              <div className="panel-body">
                <table
                  style={{ fontSize: "11px", color: "#808080", width: "100%" }}
                >
                  <tbody>
                    <tr>
                      <td>
                        Created On :{" "}
                        {moment(createdOn).format("ddd MMM DD YYYY hh:mm A")}
                      </td>
                      <td></td>
                    </tr>
                    {updatedOn && (
                      <FieldPermissionWrapper field="subscriber_modified_time">
                        <tr>
                          <td id="modifiedtime">
                            Modified On :{" "}
                            {moment(updatedOn).format("ddd MMM DD YYYY hh:mm A")}
                          </td>
                          <td></td>
                        </tr>
                      </FieldPermissionWrapper>
                    )}
                    {verifiedOn && (
                      <tr>
                        <td>
                          Verified On:{" "}
                          {moment(verifiedOn).format("ddd MMM DD YYYY hh:mm A")}
                        </td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="table-responsive">
                  <table className="table table-condensed table-hover bg-white usertable">
                    <tbody id="userDetailTable">
                      <tr>
                        <td width="140">Subscriber ID</td>
                        <td colSpan="2">{loading || subscriberLoading ? <div className="skeleton-text" style={{ width: "40%" }}></div> : subscriberId}</td>
                      </tr>
                    {loading || subscriberLoading || dropdownsLoading ? (
                      <>
                        {/* Skeleton rows for all fields - matching the actual field count */}
                        <SkeletonRow label="Lead Source" />
                        <SkeletonRow label="Name" />
                        <SkeletonRow label="Email Id" />
                        <SkeletonRow label="Mobile No." hasRightContent={true} />
                        <SkeletonRow label="Alternate Mobile" hasRightContent={true} />
                        <SkeletonRow label="DOB" />
                        <SkeletonRow label="Place / State" />
                        <SkeletonRow label="Occupation" />
                        <SkeletonRow label="Language" />
                        <SkeletonRow label="Exp. In Markets" />
                        <SkeletonRow label="Investments" />
                        <SkeletonRow label="Convertible" />
                        <SkeletonRow label="Inv. Capital" />
                        <SkeletonRow label="Demat Brokers" />
                        <SkeletonRow label="Risk Levels" />
                        <SkeletonRow label="Assigned To" />
                        <SkeletonRow label="Interested In Streetfolio" />
                        <SkeletonRow label="Folio Assigned To" />
                        <SkeletonRow label="Chat Assigned To" />
                        <SkeletonRow label="Lead Status" />
                        <SkeletonRow label="Client Status" />
                        <SkeletonRow label="Subscriber Status" />
                        <SkeletonRow label="Service Category" />
                        <SkeletonRow label="Reason Not Interested" />
                        <SkeletonRow label="Next Followup" />
                        <SkeletonRow label="Interested Segments" />
                        <SkeletonRow label="Price Expected" />
                        <SkeletonRow label="UTM Term" />
                        <SkeletonRow label="Billed To" />
                        <SkeletonRow label="GST No." />
                      </>
                    ) : (
                      <>
                        <PermissionTableRow
                          fieldName="subscriber_lead_source" // Permission key prefix with 'subscriber_'
                          label="Lead Source"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2" // Span across 2 columns
                        >
                          <PermissionEditableDropdown
                            fieldName="subscriber_lead_source"
                            value={leadSource}
                            isEditing={isEditing}
                            editFieldName="lead_source"
                            onChange={(e) =>
                              handleChange("lead_source", e.target.value)
                            }
                            onBlur={() => handleBlur("lead_source")}
                            options={dropdownOptions.leadSourceList}
                            optionValue="id"
                            optionLabel="pick_name"
                            placeholder="Select Lead Source"
                            id={id}
                          />
                        </PermissionTableRow>
                        {/* Name Field with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_fullname"
                          label="Name"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "fullname" ? (
                            <span className="edit">
                              <input
                                type="text"
                                value={fullname}
                                onChange={(e) => setFullname(e.target.value)}
                                onBlur={(e) => {
                                  if (e.target.value.trim() === "") {
                                    handleBlur("fullname");
                                  } else {
                                    handleChange("fullname", e.target.value);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    if (e.target.value.trim() === "") {
                                      handleBlur("fullname");
                                    } else {
                                      handleChange("fullname", e.target.value);
                                    }
                                  }
                                }}
                                autoFocus
                              />
                            </span>
                          ) : (
                            <span className="value">{fullname}</span>
                          )}
                        </PermissionTableRow>

                        {/* Email Field with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_email"
                          label="Email Id"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "email" ? (
                            <span className="edit">
                              <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onBlur={(e) => {
                                  if (e.target.value.trim() === "") {
                                    handleBlur("email");
                                  } else {
                                    handleChange("email", e.target.value);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    if (e.target.value.trim() === "") {
                                      handleBlur("email");
                                    } else {
                                      handleChange("email", e.target.value);
                                    }
                                  }
                                }}
                                autoFocus
                              />
                            </span>
                          ) : (
                            <span className="value">{email}</span>
                          )}
                        </PermissionTableRow>

                        <PermissionTableRow
                          fieldName="subscriber_mobileno"
                          label="Mobile No."
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          rightContent={
                            mobileno && mobileno.trim() !== "" ? ( // ✅ only show if number exists
                              <>
                                <Link
                                  to={`https://api.whatsapp.com/send?phone=+91${mobileno}&text=${encodeURIComponent(
                                    `Dear Investor/ Trader \n\nMy name is ${assignedToName}; I'm contacting you from Streetgains. https://streetgains.in/\n\nYou have registered your details on our website. I wanted to reach out to you to learn your requirements for assistance in Investment and Trading.\n\nOur Research Services are different from others as it is Credit Based Subscription Model\n\nWhat is the best time to call you?\n\nI would like to understand more. Please call me back or reply on this WhatsApp.\n\nFor more details, please visit our website: https://streetgains.in/\n\nDisclaimer: “Investments in the securities market are subject to market risks. Read all the related documents carefully before investing.”`
                                  )}`}
                                  target="_blank"
                                  className="btn btn-success btn-sm smbtn"
                                  style={{ padding: "4px 8px", fontSize: "0.7rem" }}
                                  title="Send WhatsApp Message"
                                >
                                  <FontAwesomeIcon icon={faWhatsapp} />
                                </Link>
                                <Link
                                  to={`tel:+91${mobileno}`}
                                  className="btn btn-primary btn-sm smbtn mx-1"
                                  style={{ padding: "4px 8px", fontSize: "0.7rem", width: "32px" }}
                                  title="Call from Phone"
                                >
                                  <FontAwesomeIcon icon={faPhone} />
                                </Link>
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm smbtn"
                                  style={{ padding: "4px 8px", fontSize: "0.7rem", width: "32px", height: "26px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                                  title="Call (Alternative)"
                                  onClick={handleRedCallClick}
                                >
                                  <FontAwesomeIcon icon={faPhone} />
                                </button>
                              </>
                            ) : null
                          }
                        >
                          <PermissionEditableField
                            fieldName="subscriber_mobileno"
                            value={mobileno}
                            isEditing={isEditing}
                            editFieldName="mobileno"
                            onChange={(e) => {
                              const val = e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 10);
                              setMobileno(val);
                            }}
                            onBlur={(e) => {
                              if (mobileno.length !== 10 && mobileno.length > 0) {
                                showToast.warning(
                                  "Mobile number must be exactly 10 digits"
                                );
                                return;
                              }
                              if (mobileno.length === 10) {
                                handleChange("mobileno", mobileno);
                              } else {
                                handleBlur("mobileno");
                              }
                            }}
                            type="text"
                            id={id}
                          />
                        </PermissionTableRow>

                        <PermissionTableRow
                          fieldName="subscriber_otherphone"
                          label="Alternate Mobile"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          rightContent={
                            /^\d{10}$/.test(otherphone || "") ? (
                              // <PermissionWrapper module="subscriber" action="call">
                              <>
                                <Link
                                  to={`https://api.whatsapp.com/send?phone=+91${otherphone}&text=${encodeURIComponent(
                                    `Dear Investor/ Trader \n\nMy name is ${assignedToName}; I'm contacting you from Streetgains. https://streetgains.in/\n\nYou have registered your details on our website. I wanted to reach out to you to learn your requirements for assistance in Investment and Trading.\n\nOur Research Services are different from others as it is Credit Based Subscription Model\n\nWhat is the best time to call you?\n\nI would like to understand more. Please call me back or reply on this WhatsApp.\n\nFor more details, please visit our website: https://streetgains.in/\n\nDisclaimer: “Investments in the securities market are subject to market risks. Read all the related documents carefully before investing.”`
                                  )}`}
                                  target="_blank"
                                  className="btn btn-success btn-sm smbtn"
                                  style={{ padding: "4px 8px", fontSize: "0.7rem" }}
                                  title="Send WhatsApp Message"
                                >
                                  <FontAwesomeIcon icon={faWhatsapp} />
                                </Link>
                                <Link
                                  to={`tel:+91${otherphone}`}
                                  className="btn btn-primary btn-sm smbtn mx-1"
                                  style={{ padding: "4px 8px", fontSize: "0.7rem", width: "32px" }}
                                  title="Call from Phone"
                                >
                                  <FontAwesomeIcon icon={faPhone} />
                                </Link>
                                <Link
                                  // to={`tel:+91${otherphone}`}
                                  className="btn  btn-sm smbtn"
                                  style={{ padding: "4px 8px", fontSize: "0.7rem", width: "32px" }}
                                // title="Call (Alternative)"
                                // onClick={handleRedCallClick}
                                >
                                  {/* <FontAwesomeIcon icon={faPhone} /> */}
                                </Link>
                              </>
                            ) : // </PermissionWrapper>
                              null
                          }
                        >
                          {isEditing === "otherphone" ? (
                            <span className="edit">
                              <input
                                type="text"
                                value={otherphone}
                                onChange={(e) => {
                                  // ✅ allow max 10 digits, or the text "NA" (typing "N" on the way is allowed)
                                  const raw = e.target.value.trim().toUpperCase();
                                  if (raw && "NA".startsWith(raw)) {
                                    setOtherphone(raw);
                                    return;
                                  }
                                  const val = raw.replace(/\D/g, "").slice(0, 10);
                                  setOtherphone(val);
                                }}
                                onBlur={saveOtherphone}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") saveOtherphone();
                                }}
                                autoFocus
                              />
                            </span>
                          ) : (
                            <span className="value">{otherphone}</span>
                          )}
                        </PermissionTableRow>
                        {/* DOB Field */}
                        <PermissionTableRow
                          fieldName="subscriber_dob"
                          label="DOB"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "dob" ? (
                            <span className="edit">
                              <input
                                type="date"
                                value={dob || ""}
                                onChange={(e) => setDob(e.target.value)}
                                onBlur={(e) => {
                                  if ((e.target.value || "").trim() === "") {
                                    handleBlur("dob");
                                  } else {
                                    handleChange("dob", e.target.value);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    if ((e.target.value || "").trim() === "") {
                                      handleBlur("dob");
                                    } else {
                                      handleChange("dob", e.target.value);
                                    }
                                  }
                                }}
                                autoFocus
                              />
                            </span>
                          ) : (
                            <span className="value">
                              {dob
                                ? (() => {
                                  const date = new Date(dob);
                                  const day = String(date.getDate()).padStart(
                                    2,
                                    "0"
                                  );
                                  const month = String(
                                    date.getMonth() + 1
                                  ).padStart(2, "0");
                                  const year = date.getFullYear();
                                  return `${day}-${month}-${year}`;
                                })()
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Place / State */}
                        <PermissionTableRow
                          fieldName="subscriber_state"
                          label="Place / State"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                        >
                          <PermissionEditableDropdown
                            fieldName="subscriber_state"
                            value={state}
                            isEditing={isEditing}
                            editFieldName="state"
                            onChange={(e) => handleChange("state", e.target.value)}
                            onBlur={() => handleBlur("state")}
                            options={dropdownOptions.stateList}
                            optionValue="stateId"
                            optionLabel="stateName"
                            placeholder="Select State"
                            id={id}
                          />
                        </PermissionTableRow>

                        {/* Occupation */}
                        <PermissionTableRow
                          fieldName="subscriber_occupation"
                          label="Occupation"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                        >
                          <PermissionEditableField
                            fieldName="subscriber_occupation"
                            value={occupation}
                            isEditing={isEditing}
                            editFieldName="occupation"
                            onChange={(e) => setOccupation(e.target.value)}
                            onBlur={(e) => {
                              if (e.target.value.trim() === "") {
                                handleBlur("occupation");
                              } else {
                                handleChange("occupation", e.target.value);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                if (e.target.value.trim() === "") {
                                  handleBlur("occupation");
                                } else {
                                  handleChange("occupation", e.target.value);
                                }
                              }
                            }}
                            type="text"
                            id={id}
                          />
                        </PermissionTableRow>
                        {/* Language */}
                        {/* Language Field with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_language"
                          label="Language"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "language" ? (
                            <span className="edit">
                              <select
                                value={language}
                                onChange={(e) =>
                                  handleChange("language", e.target.value)
                                }
                                onBlur={() => handleBlur("language")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.languageList.map((item, index) => (
                                  <option key={index} value={item.id}>
                                    {item.name}
                                  </option>
                                ))}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {language
                                ? dropdownOptions.languageList.find(
                                  (item) => item.id == language
                                )?.name || language
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Exp. In Markets Field with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_exp_in_market"
                          label="Exp. In Markets"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "exp_in_market" ? (
                            <span className="edit">
                              <select
                                value={expInMarket}
                                onChange={(e) =>
                                  handleChange("exp_in_market", e.target.value)
                                }
                                onBlur={() => handleBlur("exp_in_market")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.expInMarketList.map(
                                  (item, index) => (
                                    <option key={index} value={item.id}>
                                      {item.pick_name}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {expInMarket
                                ? dropdownOptions.expInMarketList.find(
                                  (item) => item.id == expInMarket
                                )?.pick_name || expInMarket
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Investments Field with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_investments"
                          label="Investments"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "investments" ? (
                            <span className="edit">
                              <select
                                value={investments}
                                onChange={(e) =>
                                  handleChange("investments", e.target.value)
                                }
                                onBlur={() => handleBlur("investments")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.investmentList.map(
                                  (item, index) => (
                                    <option key={index} value={item.id}>
                                      {item.pick_name}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {investments
                                ? dropdownOptions.investmentList.find(
                                  (item) => item.id == investments
                                )?.pick_name || investments
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Convertible Field with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_convertible"
                          label="Convertible"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "convertible" ? (
                            <span className="edit">
                              <select
                                value={convertible}
                                onChange={(e) =>
                                  handleChange("convertible", e.target.value)
                                }
                                onBlur={() => handleBlur("convertible")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.convertibleList.map(
                                  (item, index) => (
                                    <option key={index} value={item.id}>
                                      {item.pick_name}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {convertible
                                ? dropdownOptions.convertibleList.find(
                                  (item) => item.id == convertible
                                )?.pick_name || convertible
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Inv. Capital Field with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_inv_capital"
                          label="Inv. Capital"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "inv_capital" ? (
                            <span className="edit">
                              <select
                                value={invCapital}
                                onChange={(e) =>
                                  handleChange("inv_capital", e.target.value)
                                }
                                onBlur={() => handleBlur("inv_capital")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.invCapList.map((item, index) => (
                                  <option key={index} value={item.id}>
                                    {item.pick_name}
                                  </option>
                                ))}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {invCapital
                                ? dropdownOptions.invCapList.find(
                                  (item) => item.id == invCapital
                                )?.pick_name || invCapital
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Demat Brokers */}
                        {/* Demat Brokers with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_demat_brokers"
                          label="Demat Brokers"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "demat_brokers" ? (
                            <span className="edit">
                              <select
                                value={dematBrokers}
                                onChange={(e) =>
                                  handleChange("demat_brokers", e.target.value)
                                }
                                onBlur={() => handleBlur("demat_brokers")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.dematBrokersList.map(
                                  (item, index) => (
                                    <option key={index} value={item.id}>
                                      {item.pick_name}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {dematBrokers
                                ? dropdownOptions.dematBrokersList.find(
                                  (item) => item.id == dematBrokers
                                )?.pick_name || dematBrokers
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Risk Levels with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_risk_level"
                          label="Risk Levels"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "risk_level" ? (
                            <span className="edit">
                              <select
                                value={riskLevel}
                                onChange={(e) =>
                                  handleChange("risk_level", e.target.value)
                                }
                                onBlur={() => handleBlur("risk_level")}
                                autoFocus
                              >
                                {!riskLevel && <option value="">Select</option>}
                                {dropdownOptions.riskLevelList.map(
                                  (item, index) => (
                                    <option key={index} value={item.id}>
                                      {item.pick_name}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {riskLevel
                                ? dropdownOptions.riskLevelList.find(
                                  (item) => item.id == riskLevel
                                )?.pick_name || riskLevel
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                       

                        {/* Assigned To with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_assignedto"
                          label="Assigned To"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "assignedto" ? (
                            <span className="edit">
                              <select
                                value={assignedTo}
                                onChange={(e) =>
                                  handleChange("assignedto", e.target.value)
                                }
                                onBlur={() => handleBlur("assignedto")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.assignedUserList?.map(
                                  (item, index) => (
                                    <option key={index} value={item.tu_id}>
                                      {item.tu_user_fullname}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">

                              {assignedToName || dropdownOptions.assignedUserList?.find(
                                (item) => item.tu_id == assignedTo
                              )?.tu_user_fullname || assignedTo || ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Interested In Streetfolio with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_in_folio"
                          label="Interested In Streetfolio"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "in_folio" ? (
                            <span className="edit">
                              <select
                                value={inFolio}
                                onChange={(e) =>
                                  handleChange("in_folio", e.target.value)
                                }
                                onBlur={() => handleBlur("in_folio")}
                                autoFocus
                              >
                                <option value={0}>Not Interested</option>
                                <option value={1}>Interested</option>
                              </select>
                            </span>
                          ) : (
                            <span
                              className="value"
                              style={{
                                color: Number(inFolio) === 1 ? "#28a745" : "inherit",
                                fontWeight: Number(inFolio) === 1 ? "600" : "normal",
                              }}
                            >
                              {Number(inFolio) === 1 ? "Interested" : "Not Interested"}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Askfps with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_askfps"
                          label="Askfps"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "askfps" ? (
                            <span className="edit">
                              <select
                                value={askfps}
                                onChange={(e) =>
                                  handleChange("askfps", e.target.value)
                                }
                                onBlur={() => handleBlur("askfps")}
                                autoFocus
                              >
                                <option value={0}>No</option>
                                <option value={1}>Yes</option>
                              </select>
                            </span>
                          ) : (
                            <span
                              className="value"
                              style={{
                                color: Number(askfps) === 1 ? "#28a745" : "inherit",
                                fontWeight: Number(askfps) === 1 ? "600" : "normal",
                              }}
                            >
                              {Number(askfps) === 1 ? "Yes" : "No"}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Leads with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_lead_en"
                          label="Leads"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "lead_en" ? (
                            <span className="edit">
                              <select
                                value={Number(leadEn) === 1 ? 1 : 0}
                                onChange={(e) =>
                                  handleChange("lead_en", e.target.value)
                                }
                                onBlur={() => handleBlur("lead_en")}
                                autoFocus
                              >
                                <option value={0}>Existing</option>
                                <option value={1}>New</option>
                              </select>
                            </span>
                          ) : (
                            <span
                              className="value"
                              style={{
                                color: Number(leadEn) === 1 ? "#28a745" : "#007bff",
                                fontWeight: "600",
                              }}
                            >
                              {Number(leadEn) === 1 ? "New" : "Existing"}
                            </span>
                          )}
                        </PermissionTableRow>
                         {/* Assigned to research with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_assigned_to_research"
                          label="Assigned to Research"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "assigned_to_research" || isEditing === "r_assigned" ? (
                            <span className="edit">
                              <select
                                value={rAssigned ? String(rAssigned) : ""}
                                onChange={(e) =>
                                  handleChange("r_assigned", e.target.value)
                                }
                                onBlur={() => handleBlur("r_assigned")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {(dropdownOptions.assignedResearchUserList?.length > 0
                                  ? dropdownOptions.assignedResearchUserList
                                  : dropdownOptions.assignedUserList || dropdownOptions.fullUserList || []
                                )?.map((item, index) => (
                                  <option key={index} value={String(item.tu_id || item.id)}>
                                    {item.tu_user_fullname || item.user_fullname}
                                  </option>
                                ))}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {dropdownOptions.assignedResearchUserList?.find(
                                (item) => (item.tu_id || item.id) == rAssigned
                              )?.tu_user_fullname || dropdownOptions.assignedResearchUserList?.find(
                                (item) => (item.tu_id || item.id) == rAssigned
                              )?.user_fullname || dropdownOptions.assignedUserList?.find(
                                (item) => (item.tu_id || item.id) == rAssigned
                              )?.tu_user_fullname || dropdownOptions.assignedUserList?.find(
                                (item) => (item.tu_id || item.id) == rAssigned
                              )?.user_fullname || dropdownOptions.fullUserList?.find(
                                (item) => (item.tu_id || item.id || item.userid) == rAssigned
                              )?.tu_user_fullname || dropdownOptions.fullUserList?.find(
                                (item) => (item.tu_id || item.id || item.userid) == rAssigned
                              )?.user_fullname || (rAssignedName && rAssignedName != 0 ? rAssignedName : "") || (rAssigned && rAssigned != 0 && isNaN(Number(rAssigned)) ? rAssigned : "")}
                            </span>
                          )}
                        </PermissionTableRow>
                        <PermissionTableRow
                          fieldName="subscriber_assignedto_folio"
                          label="Folio Assigned To"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "assignedto_folio" ? (
                            <span className="edit">
                              <select
                                value={assignedToFolio}
                                onChange={(e) =>
                                  handleChange("assignedto_folio", e.target.value)
                                }
                                onBlur={() => handleBlur("assignedto_folio")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.assignedUserList?.map(
                                  (item, index) => (
                                    <option key={index} value={item.tu_id}>
                                      {item.tu_user_fullname}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {assignedToFolioName || dropdownOptions.assignedUserList?.find(
                                (item) => item.tu_id == assignedToFolio
                              )?.tu_user_fullname || assignedToFolio || ""}
                            </span>
                          )}
                        </PermissionTableRow>
                        <PermissionTableRow
                          fieldName="subscriber_assignedto_chat"
                          label="Chat Assigned To"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "assignedto_chat" ? (
                            <span className="edit">
                              <select
                                value={assignedToChat}
                                onChange={(e) =>
                                  handleChange("assignedto_chat", e.target.value)
                                }
                                onBlur={() => handleBlur("assignedto_chat")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.assignedUserList?.map(
                                  (item, index) => (
                                    <option key={index} value={item.tu_id}>
                                      {item.tu_user_fullname}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {assignedToChatName || dropdownOptions.assignedUserList?.find(
                                (item) => item.tu_id == assignedToChat
                              )?.tu_user_fullname || assignedToChat || ""}
                            </span>
                          )}
                        </PermissionTableRow>
                        {/* Lead Status with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_lead_status"
                          label="Lead Status"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                          disabled={isSubscribed && getRoleUserid() != 1}
                        >
                          {/* {isEditing === "lead_status" ? (
                            <span className="edit">
                              <select
                                value={leadStatus}
                                onChange={(e) =>
                                  handleChange("lead_status", e.target.value)
                                }
                                onBlur={() => handleBlur("lead_status")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.leadStatusList.map(
                                  (item, index) => (
                                    <option
                                      key={index}
                                      value={item.id}
                                      disabled={item.pick_name === "Subscribed" && !isSubscribed && getRoleUserid() != 1}
                                    >
                                      {item.pick_name}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {leadStatus
                                ? dropdownOptions.leadStatusList.find(
                                  (item) => item.id == leadStatus
                                )?.pick_name || leadStatus
                                : ""}
                            </span>
                          )} */}
                          {isEditing === "lead_status" ? (
                            <span className="edit">
                              <select
                                value={leadStatus}
                                onChange={(e) => handleChange("lead_status", e.target.value)}
                                onBlur={() => handleBlur("lead_status")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.leadStatusList
                                  .filter(item => item.pick_name !== "Contacted") // FILTER OUT CONTACTED
                                  .map((item, index) => (
                                    <option
                                      key={index}
                                      value={item.id}
                                      disabled={item.pick_name === "Subscribed" && !isSubscribed && getRoleUserid() != 1}
                                    >
                                      {item.pick_name}
                                    </option>
                                  ))}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {leadStatus
                                ? dropdownOptions.leadStatusList.find(
                                  (item) => item.id == leadStatus
                                )?.pick_name || leadStatus
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Client Status */}
                        <PermissionTableRow
                          fieldName="subscriber_client_status"
                          label="Client Status"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "client_status" ? (
                            <span className="edit">
                              <select
                                value={clientStatus}
                                onChange={(e) =>
                                  handleChange("client_status", e.target.value)
                                }
                                onBlur={() => handleBlur("client_status")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.clientStatusList.map(
                                  (item, index) => (
                                    <option key={index} value={item.id}>
                                      {item.pick_name}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {clientStatus
                                ? dropdownOptions.clientStatusList.find(
                                  (item) => item.id == clientStatus
                                )?.pick_name || clientStatus
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>
                        {/* Subscriber Status with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_status"
                          label="Subscriber Status"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "status" ? (
                            <span className="edit">
                              <select
                                value={subscriberStatus}
                                onChange={(e) =>
                                  handleChange("subscriber_status", e.target.value)
                                }
                                onBlur={() => handleBlur("subscriber_status")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.statusList.map((item, index) => (
                                  <option key={index} value={item.id}>
                                    {item.pick_name}
                                  </option>
                                ))}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {subscriberStatus
                                ? dropdownOptions.statusList.find(
                                  (item) => item.id == subscriberStatus
                                )?.pick_name || subscriberStatus
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>
                        <PermissionTableRow
                          // fieldName="subscriber_service_category"
                          fieldName="service_category"
                          label="Service Category"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          <PermissionEditableDropdown
                            // fieldName="subscriber_service_category"
                            fieldName="service_category"
                            value={serviceCategory}
                            isEditing={isEditing}
                            editFieldName="service_category"
                            onChange={(e) =>
                              handleChange("service_category", e.target.value)
                            }
                            onBlur={() => handleBlur("service_category")}
                            options={dropdownOptions.sourceList || []}
                            optionValue="value"
                            optionLabel="label"
                            placeholder="Select Service Category"
                            id={id}
                          />
                        </PermissionTableRow>

                        {/* Reason Not Interested with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_reason_not_interested"
                          label="Reason Not Interested"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "reason_not_interested" ? (
                            <span className="edit">
                              <select
                                value={reasonNotInterested}
                                onChange={(e) =>
                                  handleChange(
                                    "reason_not_interested",
                                    e.target.value
                                  )
                                }
                                onBlur={() => handleBlur("reason_not_interested")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.reasonNotInterestedList.map(
                                  (item, index) => (
                                    <option key={index} value={item.id}>
                                      {item.pick_name}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {reasonNotInterested
                                ? dropdownOptions.reasonNotInterestedList.find(
                                  (item) => item.id == reasonNotInterested
                                )?.pick_name || reasonNotInterested
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Next Followup with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_next_followup"
                          label="Next Followup"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "next_followup" ? (
                            <span className="edit">
                              <input
                                type="date"
                                value={nextFollowup}
                                onChange={(e) => setNextFollowup(e.target.value)}
                                onBlur={(e) => {
                                  const value = e.target.value.trim();
                                  const original = originalValues.next_followup || "";
                                  if (value !== original) {
                                    handleChange("next_followup", value);
                                  } else {
                                    handleBlur("next_followup");
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    const value = e.target.value.trim();
                                    const original = originalValues.next_followup || "";
                                    if (value !== original) {
                                      handleChange("next_followup", value);
                                    } else {
                                      handleBlur("next_followup");
                                    }
                                  }
                                }}
                                autoFocus
                              />
                            </span>
                          ) : (
                            <span className="value">
                              {nextFollowup &&
                                nextFollowup !== "0000-00-00 00:00:00"
                                ? new Date(nextFollowup)
                                  .toLocaleDateString("en-GB") // formats as DD/MM/YYYY
                                  .replace(/\//g, "-") // replace slashes with dashes
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Interested Segments with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_interested_segments"
                          label="Interested Segments"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "interested_segments" ? (
                            <span className="edit">
                              <select
                                value={interestedSegments}
                                onChange={(e) =>
                                  handleChange(
                                    "interested_segments",
                                    e.target.value
                                  )
                                }
                                onBlur={() => handleBlur("interested_segments")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.interestSegmentList.map(
                                  (item, index) => (
                                    <option key={index} value={item.id}>
                                      {item.pick_name}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {interestedSegments
                                ? dropdownOptions.interestSegmentList.find(
                                  (item) => item.id == interestedSegments
                                )?.pick_name || interestedSegments
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* Price Expected with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_price_expected"
                          label="Price Expected"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "price_expected" ? (
                            <span className="edit">
                              <select
                                value={priceExpected}
                                onChange={(e) =>
                                  handleChange("price_expected", e.target.value)
                                }
                                onBlur={() => handleBlur("price_expected")}
                                autoFocus
                              >
                                <option value="">Select</option>
                                {dropdownOptions.priceExpectedList.map(
                                  (item, index) => (
                                    <option key={index} value={item.id}>
                                      {item.pick_name}
                                    </option>
                                  )
                                )}
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {priceExpected
                                ? dropdownOptions.priceExpectedList.find(
                                  (item) => item.id == priceExpected
                                )?.pick_name || priceExpected
                                : ""}
                            </span>
                          )}
                        </PermissionTableRow>

                        {/* UTM Term with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_utm_term"
                          label="UTM Term"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "utm_term" ? (
                            <span className="edit">
                              <input
                                type="text"
                                value={utmTerm}
                                onChange={(e) => setUtmTerm(e.target.value)}
                                onBlur={(e) => {
                                  if (e.target.value.trim() === "") {
                                    handleBlur("utm_term");
                                  } else {
                                    handleChange("utm_term", e.target.value);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    if (e.target.value.trim() === "") {
                                      handleBlur("utm_term");
                                    } else {
                                      handleChange("utm_term", e.target.value);
                                    }
                                  }
                                }}
                                autoFocus
                              />
                            </span>
                          ) : (
                            <span className="value">{utmTerm}</span>
                          )}
                        </PermissionTableRow>

                        {/* Billed To with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_bill_to"
                          label="Billed To"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "bill_to" ? (
                            <span className="edit">
                              <input
                                type="text"
                                value={billTo}
                                onChange={(e) => setBillTo(e.target.value)}
                                onBlur={(e) => {
                                  if (e.target.value.trim() === "") {
                                    handleBlur("bill_to");
                                  } else {
                                    handleChange("bill_to", e.target.value);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    if (e.target.value.trim() === "") {
                                      handleBlur("bill_to");
                                    } else {
                                      handleChange("bill_to", e.target.value);
                                    }
                                  }
                                }}
                                autoFocus
                              />
                            </span>
                          ) : (
                            <span className="value">{billTo}</span>
                          )}
                        </PermissionTableRow>

                        {/* GST No. with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_gstno"
                          label="GST No."
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "gstno" ? (
                            <span className="edit">
                              <input
                                type="text"
                                value={gstNo}
                                onChange={(e) => setGstNo(e.target.value)}
                                onBlur={(e) => {
                                  if (e.target.value.trim() === "") {
                                    handleBlur("gstno");
                                  } else {
                                    handleChange("gstno", e.target.value);
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    if (e.target.value.trim() === "") {
                                      handleBlur("gstno");
                                    } else {
                                      handleChange("gstno", e.target.value);
                                    }
                                  }
                                }}
                                autoFocus
                              />
                            </span>
                          ) : (
                            <span className="value">{gstNo}</span>
                          )}
                        </PermissionTableRow>

                        {/* Compliance Score with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_compliance_score"
                          label="Compliance Score"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          <PermissionEditableDropdown
                            fieldName="subscriber_compliance_score"
                            value={complianceScore ? (
                              // Reverse lookup: find value from label/value (current state)
                              dropdownOptions.complianceScoreList.find(item => item.label === complianceScore || item.value == complianceScore)?.value || complianceScore
                            ) : ""}
                            isEditing={isEditing}
                            editFieldName="compliance_score"
                            onChange={(e) => handleChange("compliance_score", e.target.value)}
                            onBlur={() => handleBlur("compliance_score")}
                            options={dropdownOptions.complianceScoreList}
                            optionValue="value"
                            optionLabel="label"
                            id={id}
                          />
                        </PermissionTableRow>
                        {/* AI Tag with Permission */}
                        <PermissionTableRow
                          fieldName="subscriber_ai_tag"
                          label="AI Tag"
                          isEditing={isEditing}
                          onToggleEdit={handleToggleEdit}
                          id={id}
                          colSpan="2"
                        >
                          {isEditing === "ai_tag" ? (
                            <span className="edit">
                              <select
                                value={aiTag}
                                onChange={(e) => handleChange("ai_tag", e.target.value)}
                                onBlur={() => handleBlur("ai_tag")}
                                autoFocus
                              >
                                <option value="">Select AI Tag</option>
                                <option value="Hot">Hot</option>
                                <option value="Warm">Warm</option>
                                <option value="Value">Value</option>
                                <option value="Escalate">Escalate</option>
                              </select>
                            </span>
                          ) : (
                            <span className="value">
                              {aiTag ? (
                                <span
                                  className={`badge ${
                                    aiTag === "Hot"
                                      ? "bg-success"
                                      : aiTag === "Value"
                                      ? "bg-primary"
                                      : aiTag === "Warm"
                                      ? "bg-warning text-dark"
                                      : aiTag === "Escalate" || (aiTag && aiTag.includes("DND"))
                                      ? "bg-danger"
                                      : "bg-info text-dark"
                                  }`}
                                >
                                  {aiTag}
                                </span>
                              ) : (
                                ""
                              )}
                            </span>
                          )}
                        </PermissionTableRow>
                      </>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            </div>

            {/* Open Tickets Section */}
            {openTickets && openTickets.length > 0 && (
              <div className="col-md-12">
                <div className="panel panel-default with-rounded-corner with-shadow">
                  <div className="panel-heading">
                    <span className="panel-title">Open Tickets</span>
                  </div>
                  <div className="panel-body">
                    <table className="table table-bordered table-ticket table-striped">
                      <tbody>
                        {openTickets.map((ticket, index) => (
                          <tr key={index}>
                            <td>{ticket.ticket_title}</td>
                            <td style={{ width: "30px" }}>
                              <Link
                                to={`/Ticket/Main/tickets/${ticket.categoryid}/edit/${ticket.id}`}
                                target="_blank"
                                className="btn btn-primary btn-xs"
                              >
                                <FontAwesomeIcon icon={faSearch} />
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="col-md-8 d-flex flex-column">
            <div className="row">
              <div className="col-md-12">
                <div className="panel panel-default with-rounded-corner with-shadow p-10">
                  <ul className="nav nav-tabs" id="nav-tabs">
                    <li className="nav-item">
                      <Link
                        to="#home"
                        className={`nav-link ${activeTab === "home" ? "active" : ""
                          }`}
                        id="home-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#home"
                        role="tab"
                        aria-controls="home"
                        aria-selected={activeTab === "home"}
                        onClick={() => setActiveTab("home")}
                        style={{
                          color: activeTab === "home" ? "#007bff" : "#000",
                        }}
                      >
                        Service Details
                      </Link>
                    </li>
                    {hasPermission("report_subscription", "view") && (
                      <li className="nav-item" role="presentation">
                        <Link
                          to="#subscriptionreport"
                          className={`nav-link ${activeTab === "subscriptionreport" ? "active" : ""
                            }`}
                          id="subscriptionreport-tab"
                          data-bs-toggle="tab"
                          data-bs-target="#subscriptionreport"
                          role="tab"
                          aria-controls="subscriptionreport"
                          aria-selected={activeTab === "subscriptionreport"}
                          onClick={() => setActiveTab("subscriptionreport")}
                          style={{
                            color:
                              activeTab === "subscriptionreport"
                                ? "#007bff"
                                : "#000",
                          }}
                        >
                          Subscription Report
                        </Link>
                      </li>
                    )}
                    {(hasPermission("subscriber_cashflow_report", "view") ||
                      hasPermission("report_cashflow", "view") ||
                      hasPermission("cashflow_report", "view")) && (
                      <li className="nav-item" role="presentation">
                        <Link
                          to="#report"
                          className={`nav-link ${activeTab === "report" ? "active" : ""
                            }`}
                          id="cashflowreport-tab"
                          data-bs-toggle="tab"
                          data-bs-target="#report"
                          role="tab"
                          aria-controls="report"
                          aria-selected={activeTab === "report"}
                          onClick={() => setActiveTab("report")}
                          style={{
                            color: activeTab === "report" ? "#007bff" : "#000",
                          }}
                        >
                          Cashflow Report
                        </Link>
                      </li>
                    )}
                    {/* <li className="nav-item" role="presentation">
                      <Link
                        to="#cartData"
                        className="nav-link"
                        id="cartData-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#cartData"
                        role="tab"
                        aria-controls="cartData"
                        aria-selected="false"
                      >
                        Cart Data
                      </Link>
                    </li> */}
                  </ul>
                  <div className="tab-content tab-content-bordered">
                    <div
                      className="tab-pane fade active in"
                      id="home"
                      style={{ overflowX: "scroll" }}
                    >
                      {(() => {
                        const showServiceCol = hasPermission("subscription_service", "view");
                        const showTypeCol = hasPermission("subscription_type", "view");
                        const showAmountCol = hasPermission("subscription_amount", "view");
                        const showActDateCol = hasPermission("subscription_activation_date", "view");
                        const showExpDateCol = hasPermission("subscription_expiry_date", "view");
                        const showOnHoldCol = hasPermission("subscription_onhold_date", "view");
                        const showReactivationCol = hasPermission("subscription_reactivation_date", "view");
                        const showCreditsCol = hasPermission("subscription_trades_total", "view");
                        const showStatusCol = hasPermission("subscription_status", "view");

                        const beforeAmountCols =
                          (showServiceCol ? 1 : 0) + (showTypeCol ? 1 : 0);
                        const afterAmountCols =
                          (showActDateCol ? 1 : 0) +
                          (showExpDateCol ? 1 : 0) +
                          (showOnHoldCol ? 1 : 0) +
                          (showReactivationCol ? 1 : 0) +
                          (showCreditsCol ? 1 : 0) +
                          (showStatusCol ? 1 : 0);
                        const totalVisibleCols =
                          beforeAmountCols +
                          (showAmountCol ? 1 : 0) +
                          afterAmountCols;

                        return serviceDetailsLoading ? (
                          <table className="table table-condensed table-hover bg-white mb-0">
                            <thead>
                              <tr>
                                {showServiceCol && <th>Service</th>}
                                {showTypeCol && <th>Type</th>}
                                {showAmountCol && <th>Amount</th>}
                                {showActDateCol && <th>Activation<br />Date</th>}
                                {showExpDateCol && <th>Expiry<br />Date</th>}
                                {showOnHoldCol && <th>OnHold<br />Date</th>}
                                {showReactivationCol && <th>Reactivation<br />Date</th>}
                                {showCreditsCol && <th>Credits</th>}
                                {showStatusCol && <th>Status</th>}
                              </tr>
                            </thead>
                            <tbody>
                              <TableSkeletonRow columns={totalVisibleCols || 1} />
                              <TableSkeletonRow columns={totalVisibleCols || 1} />
                              <TableSkeletonRow columns={totalVisibleCols || 1} />
                              <TableSkeletonRow columns={totalVisibleCols || 1} />
                              <TableSkeletonRow columns={totalVisibleCols || 1} />
                            </tbody>
                          </table>
                        ) : serviceDetails ? (
                          <table className="table table-condensed table-hover bg-white mb-0">
                            <thead>
                              <tr>
                                {showServiceCol && <th>Service</th>}
                                {showTypeCol && <th>Type</th>}
                                {showAmountCol && <th>Amount</th>}
                                {showActDateCol && <th>Activation<br />Date</th>}
                                {showExpDateCol && <th>Expiry<br />Date</th>}
                                {showOnHoldCol && <th>OnHold<br />Date</th>}
                                {showReactivationCol && <th>Reactivation<br />Date</th>}
                                {showCreditsCol && <th>Credits</th>}
                                {showStatusCol && <th>Status</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {serviceDetails.map((post, index) => {
                                return (
                                  <tr
                                    key={index}
                                    onClick={() => {
                                      // Check if user has permission to edit subscription
                                      if (hasPermission("subscription", "edit")) {
                                        // Carry the subscriber listing filters so they survive the round trip
                                        if (post.service_type === 0)
                                          navigate(
                                            `/SubscriptionCredits/Edit/${post.id}`,
                                            { state: { subscriberFilterParams: filters } }
                                          );
                                        else if (post.service_type === 1)
                                          navigate(
                                            `/SubscriptionPortfolio/Edit/${post.id}`,
                                            { state: { subscriberFilterParams: filters } }
                                          );
                                      } else {
                                        // showToast.error("You don't have permission to edit subscriptions");
                                      }
                                    }}
                                    style={{
                                      cursor: hasPermission(
                                        "subscription",
                                        "edit"
                                      )
                                        ? "pointer"
                                        : "not-allowed",
                                    }}
                                  >
                                    {showServiceCol && <td>{post.service}</td>}
                                    {showTypeCol && <td>{post.type}</td>}
                                    {showAmountCol && <td>{post.amount}</td>}
                                    {showActDateCol && <td>{formatDate(post.activationDate)}</td>}
                                    {showExpDateCol && <td>{formatDate(post.expiryDate)}</td>}
                                    {showOnHoldCol && (
                                      <td>{formatDate(post.onhold_date || post.onholdDate) || "-"}</td>
                                    )}
                                    {showReactivationCol && (
                                      <td>{formatDate(post.reactivation_date || post.reactivationDate) || "-"}</td>
                                    )}
                                    {showCreditsCol && (
                                      <td>
                                        {post.subscriptionDetails?.startsWith("Total Credits") && post.trades_total !== undefined
                                          ? `${post.trades_total ?? 0} - ${post.trades_used ?? 0} = ${post.trades_available ?? 0}`
                                          : post.subscriptionDetails?.includes("| Duration")
                                          ? post.subscriptionDetails.split("|")[0].trim().replace("1 Months", "1 Month")
                                          : post.subscriptionDetails}
                                      </td>
                                    )}
                                    {showStatusCol && (
                                      <td>
                                        <span>{post.status}</span>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr>
                                {showAmountCol ? (
                                  <>
                                    {beforeAmountCols > 0 && (
                                      <td
                                        style={{
                                          textAlign: "right",
                                          fontWeight: "bold",
                                        }}
                                        colSpan={beforeAmountCols}
                                      >
                                        Total
                                      </td>
                                    )}
                                    <td
                                      style={{
                                        textAlign: "left",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {serviceDetails.reduce((sum, item) => {
                                        return sum + parseFloat(item.amount);
                                      }, 0)}
                                    </td>
                                    <td colSpan={afterAmountCols > 0 ? afterAmountCols : 1}>
                                      <PermissionWrapper
                                        module="subscription"
                                        action="view"
                                      >
                                        <button
                                          className="btn btn-primary btn-sm pull-right mx-1 my-1"
                                          onClick={() =>
                                            navigate(
                                              `/SubscriptionPortfolio?subscriberId=${id}`
                                            )
                                          }
                                        >
                                          Portfolio Service
                                        </button>
                                      </PermissionWrapper>
                                      <PermissionWrapper
                                        module="subscription"
                                        action="view"
                                      >
                                        <button
                                          className="btn btn-primary btn-sm pull-right mx-1 my-1"
                                          onClick={() =>
                                            navigate(
                                              `/SubscriptionCredits?subscriberId=${id}`
                                            )
                                          }
                                        >
                                          Credit Service
                                        </button>
                                      </PermissionWrapper>
                                    </td>
                                  </>
                                ) : (
                                  <td colSpan={totalVisibleCols || 1}>
                                    <PermissionWrapper
                                      module="subscription"
                                      action="view"
                                    >
                                      <button
                                        className="btn btn-primary btn-sm pull-right mx-1 my-1"
                                        onClick={() =>
                                          navigate(
                                            `/SubscriptionPortfolio?subscriberId=${id}`
                                          )
                                        }
                                      >
                                        Portfolio Service
                                      </button>
                                    </PermissionWrapper>
                                    <PermissionWrapper
                                      module="subscription"
                                      action="view"
                                    >
                                      <button
                                        className="btn btn-primary btn-sm pull-right mx-1 my-1"
                                        onClick={() =>
                                          navigate(
                                            `/SubscriptionCredits?subscriberId=${id}`
                                          )
                                        }
                                      >
                                        Credit Service
                                      </button>
                                    </PermissionWrapper>
                                  </td>
                                )}
                              </tr>
                            </tfoot>
                          </table>
                        ) : (
                          <p>No records found.</p>
                        );
                      })()}
                    </div>
                    {(hasPermission("subscriber_cashflow_report", "view") ||
                      hasPermission("report_cashflow", "view") ||
                      hasPermission("cashflow_report", "view")) && (
                      <div className="tab-pane fade" id="report">
                        {/* <canvas id="pieChart" height="150"></canvas> */}
                        <CashFlowChart
                          cashFlowSubscriptionReport={cashFlowSubscriptionReport}
                        />
                      </div>
                    )}
                    {/* <div className="tab-pane fade" id="cartData">
                      <table className="table table-condensed table-hover bg-white mb-0">
                        <thead>
                          <tr>
                            <th>Sr.</th>
                            <th>Service</th>
                            <th>Trade</th>
                            <th>Amount</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cart_data && cart_data.length > 0 ? (
                            cart_data.map((row, k) => (
                              <tr key={k}>
                                <td>{k + 1}</td>
                                <td>{row.service_name}</td>
                                <td>{row.trade}</td>
                                <td>{row.price}</td>
                                <td>
                                  <form
                                    action={deleteCartItem + row.id}
                                    method="post"
                                    id={`fd${row.id}`}
                                  >
                                    <input
                                      type="hidden"
                                      name="subid"
                                      value={id}
                                    />
                                    <Link
                                      href="#"
                                      data-id={row.id}
                                      className="cancel btn btn-danger btn-xs"
                                      style={{ float: "left" }}
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Link>
                                  </form>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5">No items in cart.</td>
                            </tr>
                          )}
                        </tbody>
                        {cart_data && cart_data.length > 0 && (
                          <tfoot>
                            <tr>
                              <td colSpan="2"></td>
                              <td>Sub Total</td>
                              <td>
                                <input
                                  type="hidden"
                                  name="cart_total"
                                  id="cart_total"
                                  value={cart_data.reduce(
                                    (total, row) => total + row.price,
                                    0
                                  )}
                                />
                                {cart_data.reduce(
                                  (total, row) => total + row.price,
                                  0
                                )}
                              </td>
                              <td></td>
                            </tr>
                            {discount_val && (
                              <tr>
                                <td colSpan="2"></td>
                                <td>Discount ({offercode})</td>
                                <td>{discount_val}</td>
                                <td>
                                  {cart_ref_data.discount_type && (
                                    <Link
                                      href={clearCart}
                                      className="btn btn-danger btn-xs"
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Link>
                                  )}
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td colSpan="2"></td>
                              <td>Tax ({tax_rate}%)</td>
                              <td>{tax_amt}</td>
                              <td></td>
                            </tr>
                            <tr>
                              <td colSpan="2"></td>
                              <td>Final Total</td>
                              <td>
                                {cart_data.reduce(
                                  (total, row) => total + row.price,
                                  0
                                ) + tax_amt}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                      <div className="panel panel-default with-rounded-corner pb-2">
                        <button
                          className="btn btn-primary btn-sm ml-1 mt-2"
                          data-toggle="modal"
                          data-target="#cardaddmodal"
                          onClick={toggleModalOpencardaddModal}
                        >
                          <FontAwesomeIcon icon={faPlus} /> Add
                        </button>
                        <div className="panel-heading px-0">
                          <span className="panel-title">Direct Discount</span>
                        </div>
                        <form
                          action={pageSaveDiscount}
                          method="post"
                          name="frm1"
                          id="cartdisFrm"
                          encType="multipart/form-data"
                          className="stdform"
                        >
                          <input type="hidden" name="subid" value={id} />
                          <input
                            type="hidden"
                            name="cart_ref_offer_limit"
                            id="cart_ref_offer_limit"
                          // value={cart_ref_offer.offervalue}
                          />
                          <div className="panel-body px-0">
                            <div className="row">
                              <div className="col-md-4">
                                <div className="form-group">
                                  <label className="control-label">
                                    Discount Type
                                  </label>
                                  <select
                                    name="servicetrade"
                                    className="form-control"
                                    // value={serviceTrade}
                                    // onChange={(e) =>
                                    //   setServiceTrade(e.target.value)
                                    // }
                                    required
                                  >
                                    <option value="amount">Amount</option>
                                    <option value="percent">Percent</option>
                                  </select>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="form-group">
                                  <label className="control-label">Value</label>
                                  <input
                                    type="text"
                                    name="discount_value"
                                    id="discount_value"
                                    placeholder="Enter Value"
                                    className="form-control"
                                    // value={cart_ref_data.discount_value || ""}
                                    required
                                  />
                                  <span className="text-danger d-none discount_val_err">
                                    The value cannot exceed the{" "}
                                    {cart_ref_offer.offervalue}%
                                  </span>
                                </div>
                              </div>
                              <div className="row">
                                <div className="col-lg-12">
                                  <div
                                    style={{ float: "left", marginTop: "20px" }}
                                  >
                                    <input
                                      type="submit"
                                      value="Submit"
                                      className="btn btn-primary discountSubmitBtn"
                                    />
                                  </div>
                                  <div
                                    style={{
                                      float: "left",
                                      marginTop: "20px",
                                      paddingLeft: "10px",
                                    }}
                                  >
                                    <Link
                                      href={pageBackDiscount}
                                      className="btn btn-danger"
                                    >
                                      Cancel
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </form>
                        {cart_data && cart_data.length > 0 && (
                          <button className="btn btn-primary btn-sm ml-1 anchorPlaceOrder">
                            Place Order
                          </button>
                        )}
                      </div>
                    </div> */}

                    {hasPermission("report_subscription", "view") && (
                      <div
                        className="tab-pane fade pb-3"
                        id="subscriptionreport"
                        style={{ overflowX: "scroll" }}
                      >
                        {(() => {
                          const showService = hasPermission("subscription_service", "view");
                          const showTotalCalls =
                            hasPermission("subcription_report_total_calls", "view") ||
                            hasPermission("subscription_report_total_calls", "view");
                          const showProfitableCalls =
                            hasPermission("subcription_report_profitable_calls", "view") ||
                            hasPermission("subscription_report_profitable_calls", "view");
                          const showLossCalls =
                            hasPermission("subcription_report_loss_calls", "view") ||
                            hasPermission("subscription_report_loss_calls", "view");
                          const showTotalProfit =
                            hasPermission("subcription_report_total_profit", "view") ||
                            hasPermission("subscription_report_total_profit", "view");
                          const showTotalLoss =
                            hasPermission("subcription_report_total_loss", "view") ||
                            hasPermission("subscription_report_total_loss", "view");
                          const showNetProfit =
                            hasPermission("subcription_report_net_profit", "view") ||
                            hasPermission("subscription_report_net_profit", "view");
                          const showActDate = hasPermission("subscription_activation_date", "view");
                          const showExpDate = hasPermission("subscription_expiry_date", "view");

                          return subscriptionReport && subscriptionReport.length > 0 ? (
                            <table className="table table-condensed table-hover bg-white mb-0">
                              <thead>
                                <tr>
                                  {showService && <th style={{ minWidth: "120px" }}>Service</th>}
                                  {showTotalCalls && <th>Total Calls</th>}
                                  {showProfitableCalls && <th>Profitable Calls</th>}
                                  {showLossCalls && <th>Loss Calls</th>}
                                  {showTotalProfit && <th style={{ minWidth: "85px" }}>Total Profit</th>}
                                  {showTotalLoss && <th style={{ minWidth: "85px" }}>Total Loss</th>}
                                  {showNetProfit && <th style={{ minWidth: "80px" }}>Net Profit</th>}
                                  <th>Accuracy</th>
                                  {showActDate && <th>Activation Date</th>}
                                  {showExpDate && <th>Expiry Date</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {subscriptionReport.map((post, index) => {
                                  return (
                                    <tr key={index}>
                                      {showService && <td>{post.service}</td>}
                                      {showTotalCalls && (
                                        <td className="text-primary">{post.totalCalls}</td>
                                      )}
                                      {showProfitableCalls && (
                                        <td className="text-success">{post.profitableCalls}</td>
                                      )}
                                      {showLossCalls && (
                                        <td className="text-danger">{post.lossCalls}</td>
                                      )}
                                      {showTotalProfit && (
                                        <td className="text-success">₹ {post.totalProfit}</td>
                                      )}
                                      {showTotalLoss && (
                                        <td className="text-danger">₹ {post.totalLoss}</td>
                                      )}
                                      {showNetProfit && (
                                        <td
                                          className={
                                            post.netProfit > 0
                                              ? "text-success"
                                              : "text-danger"
                                          }
                                        >
                                          ₹ {post.netProfit}
                                        </td>
                                      )}
                                      <td className="text-success">{post.accuracy}%</td>
                                      {showActDate && <td>{post.activationDate}</td>}
                                      {showExpDate && <td>{post.expiryDate}</td>}
                                    </tr>
                                  );
                                })}
                              </tbody>

                              {/* Footer Row for Totals */}
                              <tfoot>
                                <tr>
                                  {showService && (
                                    <td
                                      style={{
                                        textAlign: "right",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      Total
                                    </td>
                                  )}
                                  {showTotalCalls && (
                                    <td
                                      style={{
                                        textAlign: "left",
                                        fontWeight: "bold",
                                      }}
                                      className="text-primary"
                                    >
                                      {subscriptionReport.reduce(
                                        (sum, item) => sum + item.totalCalls,
                                        0
                                      )}
                                    </td>
                                  )}
                                  {showProfitableCalls && (
                                    <td
                                      style={{
                                        textAlign: "left",
                                        fontWeight: "bold",
                                      }}
                                      className="text-success"
                                    >
                                      {subscriptionReport.reduce(
                                        (sum, item) => sum + item.profitableCalls,
                                        0
                                      )}
                                    </td>
                                  )}
                                  {showLossCalls && (
                                    <td
                                      style={{
                                        textAlign: "left",
                                        fontWeight: "bold",
                                      }}
                                      className="text-danger"
                                    >
                                      {subscriptionReport.reduce(
                                        (sum, item) => sum + item.lossCalls,
                                        0
                                      )}
                                    </td>
                                  )}
                                  {showTotalProfit && (
                                    <td
                                      style={{
                                        textAlign: "left",
                                        fontWeight: "bold",
                                      }}
                                      className="text-success"
                                    >
                                      ₹{" "}
                                      {subscriptionReport.reduce(
                                        (sum, item) => sum + item.totalProfit,
                                        0
                                      )}
                                    </td>
                                  )}
                                  {showTotalLoss && (
                                    <td
                                      style={{
                                        textAlign: "left",
                                        fontWeight: "bold",
                                      }}
                                      className="text-danger"
                                    >
                                      ₹{" "}
                                      {subscriptionReport.reduce(
                                        (sum, item) => sum + item.totalLoss,
                                        0
                                      )}
                                    </td>
                                  )}
                                  {showNetProfit && (
                                    <td
                                      style={{
                                        textAlign: "left",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      ₹{" "}
                                      {subscriptionReport.reduce(
                                        (sum, item) => sum + item.netProfit,
                                        0
                                      )}
                                    </td>
                                  )}
                                  <td
                                    style={{
                                      textAlign: "left",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {/* Calculate Accuracy % */}
                                    {(() => {
                                      const totalCalls = subscriptionReport.reduce(
                                        (sum, item) => sum + item.totalCalls,
                                        0
                                      );
                                      const totalProfitable =
                                        subscriptionReport.reduce(
                                          (sum, item) => sum + item.profitableCalls,
                                          0
                                        );
                                      return totalCalls > 0
                                        ? `${(
                                            (totalProfitable / totalCalls) *
                                            100
                                          ).toFixed(2)}%`
                                        : "0%";
                                    })()}
                                  </td>
                                  {((showActDate ? 1 : 0) + (showExpDate ? 1 : 0)) > 0 && (
                                    <td
                                      style={{
                                        textAlign: "right",
                                        fontWeight: "bold",
                                      }}
                                      colSpan={(showActDate ? 1 : 0) + (showExpDate ? 1 : 0)}
                                    ></td>
                                  )}
                                </tr>
                              </tfoot>
                            </table>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* KYC Details + Terms of Service Agreement (side by side) */}
              <PermissionWrapper module="kyc_details" action="view">
                <div className={acceptanceData?.length > 0 ? "col-md-6" : "col-md-12"}>
                <div className="panel panel-default with-rounded-corner with-shadow pb-2 mb-3">
                  <div
                    className="panel-heading border-0 d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => setIsKycSectionOpen((prev) => !prev)}
                  >
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={isKycSectionOpen ? faChevronUp : faChevronDown}
                        className="me-2 text-muted"
                        style={{ fontSize: "12px", marginRight: "8px" }}
                      />
                      <span className="panel-title mb-0">KYC Details</span>
                      {verifyKyc === 1 && (
                        <span className="badge badge-success ms-2" style={{ marginLeft: "6px" }}>
                          Verified
                        </span>
                      )}
                    </div>
                  </div>

                  {isKycSectionOpen && (
                    <div className="panel-body">
                      <div className="table-responsive">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>User Info</th>
                              <th>Pan Info</th>
                            </tr>
                          </thead>
                          <tbody>
                            {kycDetailsLoading ? (
                              <TableSkeletonRow columns={2} />
                            ) : kycDetails ? (
                              <tr>
                                <td>
                                  {kycDetails.name_pann}
                                  <br />
                                  <small>
                                    DOB:{" "}
                                    {kycDetails.dob
                                      ? moment(kycDetails.dob).format("DD-MM-YYYY")
                                      : "N/A"}
                                  </small>
                                </td>
                                <td>
                                  {kycDetails.pan_number}
                                  <small>
                                    {kycDetails.pan_copy ? (
                                      <>
                                        <button
                                          className="btn btn-primary btn-sm ms-1"
                                          onClick={() => openPanLightbox(kycDetails.pan_copy)}
                                          title="View PAN Copy"
                                        >
                                          <FontAwesomeIcon icon={faSearch} />
                                        </button>
                                      </>
                                    ) : (
                                      <span className="text-muted small ms-2"></span>
                                    )}
                                  </small>
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>

                      {verifyKyc === 1 && (
                        <div className="text-muted mt-2" style={{ fontSize: "12px" }}>
                          <b>Verified On:</b>{" "}
                          {verifyKycOn ? moment(verifyKycOn).format("DD/MM/YYYY") : "-"}
                          <span className="mx-2">&middot;</span>
                          <b>Verified By:</b>{" "}
                          {kycVerifiedByName ? kycVerifiedByName : "System Verified"}
                        </div>
                      )}

                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <div>
                          {kycDetails?.kycRes && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              style={{ fontSize: "12px", padding: "2px 8px", lineHeight: "1.5" }}
                              onClick={() => handleDownloadKycRes(kycDetails.kycRes)}
                              title="Download KYC Response Data"
                            >
                              <FontAwesomeIcon icon={faDownload} className="me-1" />
                              Download KYC
                            </button>
                          )}
                        </div>
                        <PermissionWrapper module="kyc_details" action="edit">
                          <button
                            className="btn btn-primary btn-xs shadow-sm"
                            type="button"
                            onClick={() => {
                              if (hasPermission("kyc_details", "edit")) {
                                setIsModalOpenCvlKycModal(true);
                              }
                            }}
                            title="Edit and Verify KYC via CVL KRA"
                          >
                            <FontAwesomeIcon icon={faEdit} className="me-1" />
                            Edit
                          </button>
                        </PermissionWrapper>
                      </div>

                      {(kycLogsLoading || (kycLogs && kycLogs.length > 0)) && (
                        <div className="mt-3">
                          <hr className="my-3" />
                          <div className="fw-bold mb-2" style={{ fontSize: "13px", color: "#333" }}>
                            KYC History
                          </div>
                          <div
                            className="commentbox"
                            style={{
                              fontSize: "12px",
                              border: "1px solid #cecece",
                              borderRadius: "5px",
                              padding: "10px",
                              backgroundColor: "#fafafa",
                            }}
                          >
                            {kycLogsLoading ? (
                              <p className="mb-0">Loading KYC logs...</p>
                            ) : (
                              kycLogs.map((log, index) => {
                                const createdon = (log.kl_created_on || log.created_on)
                                  ? moment(log.kl_created_on || log.created_on).format(
                                    "DD/MM/YYYY HH:mm A"
                                  )
                                  : "Unknown date";

                                return (
                                  <div key={index} className="kyc-log-item mb-2">
                                    <p className="mb-0">
                                      <span
                                        style={{
                                          color: "slategray",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        KYC Status Updated
                                      </span>
                                      <br /> {log.kl_message || log.message}
                                      <span
                                        style={{ color: "blue", fontWeight: "bold" }}
                                      >
                                        {" "}
                                        on {createdon}
                                      </span>
                                    </p>
                                    {index < kycLogs.length - 1 && (
                                      <hr className="my-2" />
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                </div>
              </PermissionWrapper>

              {/* Terms of Service Agreement Section */}
              {acceptanceData?.length > 0 && (
                <div className={hasPermission("kyc_details", "view") ? "col-md-6" : "col-md-12"}>
                <div className="panel panel-default with-rounded-corner with-shadow pb-2 mb-3">
                  <div
                    className="panel-heading border-0 d-flex justify-content-between align-items-center"
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => setIsTermsAgreementOpen((prev) => !prev)}
                  >
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon
                        icon={isTermsAgreementOpen ? faChevronUp : faChevronDown}
                        className="me-2 text-muted"
                        style={{ fontSize: "12px", marginRight: "8px" }}
                      />
                      <span className="panel-title mb-0">Terms of Service Agreement</span>
                    </div>
                  </div>
                  {isTermsAgreementOpen && (
                    <div className="panel-body">
                      <div className="table-responsive">
                        <table className="table table-condensed table-hover bg-white mb-0">
                          <thead>
                            <tr>
                              <th>Accepted On</th>
                              <th style={{ width: "140px", textAlign: "center" }}>Agreement</th>
                            </tr>
                          </thead>
                          <tbody>
                            {acceptanceData.map((item, index) => (
                              <tr key={item.id || index}>
                                <td style={{ verticalAlign: "middle" }}>
                                  <b>{moment(item.accepted_on).format("ddd MMM DD YYYY hh:mm A")}</b>
                                </td>
                                <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                                  {Number(item.source) === 2 ? (
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      style={{ fontSize: "12px", padding: "2px 8px", lineHeight: "1.5" }}
                                      onClick={() => handleDownloadAcceptancePdf(item.id)}
                                      disabled={downloadingAcceptanceId === item.id}
                                      title="Download Terms of Service Agreement"
                                    >
                                      <FontAwesomeIcon icon={faDownload} className="me-1" />
                                      {downloadingAcceptanceId === item.id ? "Downloading..." : "Download"}
                                    </button>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-muted mt-2 mb-0" style={{ fontSize: "11px" }}>
                        The Investor has accepted the Terms & Conditions /
                        Client-Acceptance
                      </p>
                    </div>
                  )}
                </div>
                </div>
              )}

              {/* Lead Comments (half width beside AI Call Details, with Comments & AI Comments tabs) */}
              {showCommentsPanel && (
                <div
                  className={`${commentsRowColClass} ${showAiCallDetailsPanel ? "lc-pair-col" : ""}`}
                >
                  {/* When paired, the panel fills the column so its height follows AI Call Details */}
                  <div
                    className="panel panel-default with-rounded-corner with-shadow pb-2 d-flex flex-column mb-3"
                    style={showAiCallDetailsPanel ? undefined : { height: "480px" }}
                  >
                    <div className="panel-body pt-3 pb-2" style={{ flexShrink: 0 }}>
                      <div className="lc-tabs" role="tablist" id="lead-comments-tabs">
                        {hasPermission("subscriber_comment", "view") && (
                          <button
                            type="button"
                            role="tab"
                            className={`lc-tab ${commentsActiveTab === "comments" ? "active" : ""}`}
                            aria-selected={commentsActiveTab === "comments"}
                            onClick={() => setCommentsActiveTab("comments")}
                          >
                            Lead Comments
                            {comments.length > 0 && (
                              <span className="lc-tab-count">{comments.length}</span>
                            )}
                          </button>
                        )}
                        {(hasPermission("subscriber_ai_comments", "view") ||
                          hasPermission("subscriber_summary", "view")) && (
                          <button
                            type="button"
                            role="tab"
                            className={`lc-tab ${commentsActiveTab === "ai_comments" ? "active" : ""}`}
                            aria-selected={commentsActiveTab === "ai_comments"}
                            onClick={() => setCommentsActiveTab("ai_comments")}
                          >
                            AI Comments
                            {(() => {
                              const aiCount = (voiceCallLogs || []).filter(
                                (l) => l.overallSummary || l.overall_summary
                              ).length;
                              return aiCount > 0 ? <span className="lc-tab-count">{aiCount}</span> : null;
                            })()}
                          </button>
                        )}
                      </div>
                    </div>

                    {(commentsActiveTab === "comments" && hasPermission("subscriber_comment", "view")) ||
                    (!hasPermission("subscriber_ai_comments", "view") && !hasPermission("subscriber_summary", "view")) ? (
                      <>
                        {/* Comment composer - only show if user has add permission */}
                        <PermissionWrapper module="subscriber_comment" action="edit">
                          <div className="panel-body pt-0 pb-2" style={{ flexShrink: 0 }}>
                            <div className="lc-composer">
                              <textarea
                                rows="2"
                                placeholder="Write a comment..."
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    postComment();
                                  }
                                }}
                                disabled={commentLoading}
                              ></textarea>
                              <div className="lc-composer-toolbar">
                                <input
                                  type="file"
                                  id="comment_file"
                                  name="comment_file"
                                  hidden
                                  onChange={handleCommentFileChange}
                                  accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.mpeg,.mpg"
                                  disabled={commentLoading}
                                />
                                {commentFile ? (
                                  <div className="lc-file-chip" title={commentFile.name}>
                                    {commentFile.type.startsWith("image/") ? (
                                      <img src={URL.createObjectURL(commentFile)} alt="" />
                                    ) : (
                                      <FontAwesomeIcon icon={faPaperclip} />
                                    )}
                                    <span>{commentFile.name}</span>
                                    <button
                                      type="button"
                                      title="Remove file"
                                      onClick={() => {
                                        setCommentFile(null);
                                        const fileInput = document.querySelector(
                                          'input[name="comment_file"]'
                                        );
                                        if (fileInput) fileInput.value = "";
                                      }}
                                      disabled={commentLoading}
                                    >
                                      <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                  </div>
                                ) : (
                                  <label htmlFor="comment_file" className="lc-icon-btn" title="Attach a file">
                                    <FontAwesomeIcon icon={faPaperclip} /> Attach
                                  </label>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-primary lc-send-btn"
                                  onClick={postComment}
                                  disabled={commentLoading || (!commentText.trim() && !commentFile)}
                                >
                                  {commentLoading ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <FontAwesomeIcon icon={faPaperPlane} /> Save
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                            <p className="lc-hint">Enter to save &middot; Shift + Enter for a new line</p>
                          </div>
                        </PermissionWrapper>

                        {/* Comments list - only show if user has view permission */}
                        <PermissionWrapper module="subscriber_comment" action="view">
                          <div
                            id="commentTarget"
                            className="lc-list"
                            style={{
                              borderTop: hasPermission("subscriber_comment", "edit") ? "1px solid #eef0f4" : "none",
                            }}
                          >
                            {commentLoading && comments.length === 0 ? (
                              <>
                                <ListSkeletonItem />
                                <ListSkeletonItem />
                                <ListSkeletonItem />
                              </>
                            ) : comments && comments.length > 0 ? (
                              comments.map((comment, index) => {
                                const createdon = comment.created_on
                                  ? moment(comment.created_on).format("DD MMM YYYY, hh:mm A")
                                  : "Unknown date";
                                const author = comment.created_by || "Unknown User";

                                return (
                                  <div key={index} className="lc-item">
                                    <div className="lc-item-body">
                                      {comment.comments && (
                                        <div className="lc-text">{comment.comments}</div>
                                      )}
                                      {(comment.attachment_url || comment.file) && (
                                        <PermissionWrapper module="subscriber_comment_download" action="view">
                                          <button
                                            type="button"
                                            className="lc-attachment"
                                            onClick={() => {
                                              const fileUrl = comment.file || comment.attachment_url;
                                              window.open(fileUrl, "_blank");
                                            }}
                                            title="Download attachment"
                                          >
                                            <FontAwesomeIcon icon={faDownload} /> Attachment
                                          </button>
                                        </PermissionWrapper>
                                      )}
                                    </div>
                                    <div className="lc-item-meta">
                                      <span className="lc-author" title={author}>{author}</span>
                                      <span className="lc-date">{createdon}</span>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="lc-empty">
                                <div className="lc-empty-icon">
                                  <FontAwesomeIcon icon={faComments} />
                                </div>
                                <p className="mb-0">No comments added yet</p>
                                {hasPermission("subscriber_comment", "edit") && (
                                  <p className="small mt-1 mb-0">Be the first to add a comment</p>
                                )}
                              </div>
                            )}
                          </div>
                        </PermissionWrapper>
                      </>
                    ) : (
                      /* AI Comments List with Subscriber Summary first */
                      <div
                        id="aiCommentsTarget"
                        style={{
                          flex: 1,
                          overflowY: "auto",
                          padding: "15px",
                        }}
                      >
                        {/* Subscriber Summary Section at top */}
                        {(hasPermission("subscriber_summary", "view") ||
                          hasPermission("subscriber_ai_comments", "view")) && (
                          <div
                            className="mb-3 p-3"
                            style={{
                              border: "1px solid #d0e2ff",
                              borderLeft: "4px solid #007aff",
                              borderRadius: "6px",
                              backgroundColor: "#f7f9fc",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                              
                                <strong style={{ fontSize: "13px", color: "#1a202c" }}>Subscriber Summary</strong>
                              
                              {(hasPermission("subscriber_summary", "edit") ||
                                hasPermission("subscriber_ai_comments", "edit")) && (
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm ms-auto"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleGenerateSummary();
                                  }}
                                  disabled={isGeneratingSummary || loading || subscriberLoading}
                                  title="Generate subscriber summary"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {isGeneratingSummary ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                        style={{ marginRight: "6px" }}
                                      ></span>
                                      Generating...
                                    </>
                                  ) : (
                                    "Generate Summary"
                                  )}
                                </button>
                              )}
                            </div>
                            {subscriberSummaryLoading ? (
                              <div className="text-center py-2">
                                <span
                                  className="spinner-border spinner-border-sm text-primary"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              </div>
                            ) : (
                              <p
                                className="mb-0"
                                style={{
                                  whiteSpace: "pre-wrap",
                                  wordWrap: "break-word",
                                  fontSize: "13px",
                                  color: subscriberSummary ? "#343a40" : "#6c757d",
                                  lineHeight: 1.6,
                                }}
                              >
                                {subscriberSummary || "No summary generated yet"}
                              </p>
                            )}
                          </div>
                        )}

                        {voiceCallLogsLoading ? (
                          <>
                            <ListSkeletonItem />
                            <ListSkeletonItem />
                            <ListSkeletonItem />
                          </>
                        ) : (() => {
                          const aiLogs = (voiceCallLogs || []).filter(
                            (l) => (l.overallSummary && l.overallSummary.trim()) || (l.overall_summary && l.overall_summary.trim())
                          );

                          if (aiLogs.length === 0) {
                            return (
                              <div className="text-center py-3">
                                <p className="text-muted mb-0 small">No AI call comments or summaries recorded yet</p>
                              </div>
                            );
                          }

                          return aiLogs.map((log, index) => {
                            const summaryText = log.overallSummary || log.overall_summary;
                            const callDate = log.createdOn || log.startedAt || log.created_on || log.started_at;
                            const formattedDate = callDate
                              ? moment(callDate).format("DD/MM/YYYY hh:mm A")
                              : "Unknown Date";
                            const callStatus = log.outcome || log.status;

                            return (
                              <div
                                key={log.id || index}
                                className="commentbox mt-0 mx-0 p-3"
                                style={{
                                  border: "1px solid #e2e8f0",
                                  borderLeft: "4px solid #28a745",
                                  borderRadius: "6px",
                                  backgroundColor: "#fdfdfd",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                }}
                              >
                                <div
                                  className="d-flex justify-content-between align-items-center mb-2"
                                  style={{ fontSize: "12px" }}
                                >
                                  <div>
                                    {/* <span className="badge badge-success" style={{ fontWeight: 600 }}>
                                      AI Voice Call
                                    </span> */}
                                    {callStatus && (
                                      <span
                                        className="badge badge-secondary ms-2 text-capitalize"
                                        style={{ marginLeft: "6px" }}
                                      >
                                        {String(callStatus).replace(/_/g, " ")}
                                      </span>
                                    )}
                                    {log.durationSeconds > 0 && (
                                      <span className="text-muted small ms-2" style={{ marginLeft: "8px" }}>
                                        ({formatDuration(log.durationSeconds)})
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-muted fw-semibold">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="me-1" style={{ marginRight: "4px" }} />
                                    {formattedDate}
                                  </div>
                                </div>

                                <div
                                  style={{
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    fontSize: "13px",
                                    color: "#2d3748",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  {summaryText}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Subscriber AI Voice Call Details (half width beside Comments) */}
              {showAiCallDetailsPanel && (
                <div className={commentsRowColClass}>
                  <div
                    className="panel panel-default with-rounded-corner with-shadow d-flex flex-column mb-3"
                    style={{ height: showCommentsPanel ? "calc(100% - 1rem)" : "auto" }}
                  >
                    <div
                      className="panel-heading border-0 d-flex justify-content-between align-items-center flex-wrap"
                      style={{
                        padding: "8px 12px",
                        gap: "8px",
                        flexShrink: 0,
                      }}
                    >
                      <span className="panel-title mb-0">Subscriber AI Call Details</span>
                      <div
                        className="d-flex align-items-center flex-wrap"
                        style={{ gap: "6px" }}
                      >
                        {/* {subscriberVoiceData && (
                          <>
                            <PermissionWrapper module="subscriber_voice" action="edit">
                              <button
                                type="button"
                                className="btn btn-primary btn-xs"
                                onClick={handleOpenEditSubscriberVoice}
                                title="Edit Voice Record"
                                style={{
                                  fontSize: "11px",
                                  padding: "3px 8px",
                                  fontWeight: 500,
                                }}
                              >
                                <FontAwesomeIcon icon={faEdit} className="me-1" style={{ marginRight: "4px" }} />
                                Edit
                              </button>
                            </PermissionWrapper>
                            <PermissionWrapper module="subscriber_voice" action="delete">
                              <button
                                type="button"
                                className="btn btn-danger btn-xs"
                                onClick={() => handleDeleteSubscriberVoice(subscriberVoiceData.id)}
                                disabled={isDeletingVoice}
                                title="Delete Voice Record"
                                style={{
                                  fontSize: "11px",
                                  padding: "3px 8px",
                                  fontWeight: 500,
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} className="me-1" style={{ marginRight: "4px" }} />
                                {isDeletingVoice ? "Deleting..." : "Delete"}
                              </button>
                            </PermissionWrapper>
                          </>
                        )} */}
                        <PermissionWrapper module="ai_voice_calling" action="add">
                          <button
                            type="button"
                            className="btn btn-success btn-xs"
                            onClick={handleAiVoiceCall}
                            disabled={isPlacingAiCall || subscriberLoading || !mobileno}
                            title={
                              !mobileno
                                ? "Subscriber mobile number is not available"
                                : "Place an AI call"
                            }
                            style={{
                              fontSize: "11px",
                              padding: "3px 8px",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                            }}
                          >
                            <FontAwesomeIcon icon={faPhone} className="me-1" style={{ marginRight: "4px" }} />
                            {isPlacingAiCall ? "Calling..." : "AI Call"}
                          </button>
                        </PermissionWrapper>
                      </div>
                    </div>
                    <div
                      className="panel-body pt-0"
                      style={{ flex: 1, overflowY: "auto", minHeight: 0 }}
                    >
                      <div className="table-responsive">
                        <table className="table table-condensed table-hover bg-white usertable mb-0">
                          <tbody>
                            {subscriberVoiceLoading ? (
                              <>
                                <SkeletonRow label="DND Status" />
                                <SkeletonRow label="DND Reason / On" />
                                <SkeletonRow label="AI Warmth" />
                                <SkeletonRow label="AI Tags" />
                                <SkeletonRow label="WhatsApp Consent" />
                                <SkeletonRow label="Last Called On" />
                                <SkeletonRow label="Calls (30d)" />
                                <SkeletonRow label="Next Followup" />
                                <SkeletonRow label="Campaign" />
                                <SkeletonRow label="Last Call Status" />
                                <SkeletonRow label="Recording" />
                              </>
                            ) : !subscriberVoiceData ? (
                              <tr>
                                <td colSpan="3" className="text-center py-3 text-muted">
                                  No AI voice call data recorded for this subscriber yet.
                                </td>
                              </tr>
                            ) : (
                              <>
                                <tr>
                                  <td width="140">DND Status</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.dnd === 1 ? "DND Active" : "Can Dial"}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">DND Reason / On</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.dnd_reason ? (
                                      <div>
                                        <span className="text-capitalize">
                                          {subscriberVoiceData.dnd_reason}
                                        </span>
                                        {subscriberVoiceData.dnd_on && (
                                          <span className="text-muted small ms-1" style={{ marginLeft: "6px" }}>
                                            ({moment(subscriberVoiceData.dnd_on).format(
                                              "DD-MM-YYYY hh:mm A"
                                            )})
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">AI Warmth</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.ai_warmth_score !== null &&
                                    subscriberVoiceData.ai_warmth_score !== undefined
                                      ? `${subscriberVoiceData.ai_warmth_score} / 100`
                                      : "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">AI Tags</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.ai_tags || "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">Segment</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.trading_segment || "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">Capital</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.trading_capital || "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">Pain Point</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.main_pain_point || "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">WhatsApp Consent</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.whatsapp_consent === 1 ? (
                                      <div>
                                        <span>Consented</span>
                                        {subscriberVoiceData.whatsapp_consent_on && (
                                          <span className="text-muted small ms-1" style={{ marginLeft: "6px" }}>
                                            ({moment(
                                              subscriberVoiceData.whatsapp_consent_on
                                            ).format("DD-MM-YYYY hh:mm A")})
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      "No"
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">Last Called On</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.ai_last_called_on
                                      ? moment(
                                          subscriberVoiceData.ai_last_called_on
                                        ).format("DD-MM-YYYY hh:mm A")
                                      : "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">Calls (30d)</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.ai_call_count_30d || 0}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">Next Followup</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.next_followup_time ? (
                                      moment(
                                        subscriberVoiceData.next_followup_time
                                      ).format("DD-MM-YYYY hh:mm A")
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">Campaign</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.campaign_name ||
                                      subscriberVoiceData.last_campaign_id ||
                                      "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td width="140">Last Call Status</td>
                                  <td colSpan="2">
                                    {subscriberVoiceData.call_log_status ? (
                                      <div>
                                        <span className="text-capitalize">
                                          {subscriberVoiceData.call_log_status}
                                        </span>
                                        {subscriberVoiceData.call_log_outcome && (
                                          <span className="small text-muted text-capitalize ms-1" style={{ marginLeft: "6px" }}>
                                            ({subscriberVoiceData.call_log_outcome})
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                </tr>
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Subscriber Voice Modal */}
              {isEditingSubscriberVoice && (
                <>
                  <div
                    className="modal-backdrop fade show"
                    style={{ zIndex: 1040 }}
                    onClick={() => setIsEditingSubscriberVoice(false)}
                  ></div>
                  <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ zIndex: 1050 }}
                  >
                    <div className="modal-dialog" role="document">
                      <div className="modal-content">
                        <div className="modal-header d-flex justify-content-between align-items-center">
                          <h5 className="modal-title font-weight-bold mb-0">
                            Edit Subscriber Voice Record
                          </h5>
                          <button
                            type="button"
                            className="btn btn-default float-end"
                            onClick={() => setIsEditingSubscriberVoice(false)}
                            style={{ fontSize: "16px", lineHeight: "1", padding: "4px 10px" }}
                          >
                            &times;
                          </button>
                        </div>
                        <form onSubmit={handleSaveSubscriberVoice}>
                          <div className="modal-body">
                            <div className="form-group mb-3">
                              <label className="font-weight-bold">DND Status</label>
                              <select
                                className="form-control"
                                value={editVoiceForm.dnd}
                                onChange={(e) =>
                                  setEditVoiceForm({
                                    ...editVoiceForm,
                                    dnd: Number(e.target.value),
                                  })
                                }
                              >
                                <option value={0}>Can Dial (DND Inactive)</option>
                                <option value={1}>DND Active (Do Not Call)</option>
                              </select>
                            </div>

                            <div className="form-group mb-3">
                              <label className="font-weight-bold">DND Reason</label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. opt_out, ndnc, requested"
                                value={editVoiceForm.dndReason}
                                onChange={(e) =>
                                  setEditVoiceForm({
                                    ...editVoiceForm,
                                    dndReason: e.target.value,
                                  })
                                }
                              />
                            </div>

                            <div className="form-group mb-3">
                              <label className="font-weight-bold">AI Warmth Score (0-100)</label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                className="form-control"
                                placeholder="e.g. 75"
                                value={editVoiceForm.aiWarmthScore}
                                onChange={(e) =>
                                  setEditVoiceForm({
                                    ...editVoiceForm,
                                    aiWarmthScore: e.target.value,
                                  })
                                }
                              />
                            </div>

                            <div className="form-group mb-3">
                              <label className="font-weight-bold">AI Tags</label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Comma separated: WHALE, STARTER, GENTLE..."
                                value={editVoiceForm.aiTags}
                                onChange={(e) =>
                                  setEditVoiceForm({
                                    ...editVoiceForm,
                                    aiTags: e.target.value,
                                  })
                                }
                              />
                            </div>

                            <div className="form-group mb-3">
                              <label className="font-weight-bold">WhatsApp Consent</label>
                              <select
                                className="form-control"
                                value={editVoiceForm.whatsappConsent}
                                onChange={(e) =>
                                  setEditVoiceForm({
                                    ...editVoiceForm,
                                    whatsappConsent: Number(e.target.value),
                                  })
                                }
                              >
                                <option value={0}>No</option>
                                <option value={1}>Yes (Consented)</option>
                              </select>
                            </div>
                          </div>
                          <div className="modal-footer">
                            <button
                              type="button"
                              className="btn btn-default"
                              onClick={() => setIsEditingSubscriberVoice(false)}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={isSavingVoice}
                            >
                              {isSavingVoice ? "Saving..." : "Save Changes"}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </>
              )}

            </div>

            {/* WhatsApp chat history (read-only): fills the column down to the left column's end */}
            <SubscriberWhatsappChat subscriberId={id} />
          </div>

          {/* Activity History (Accordion, full width below the WhatsApp chat) */}
          <div className="col-md-12">
            <div className="panel panel-default with-rounded-corner with-shadow pb-2">
              <div
                className="panel-heading border-0 d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => setIsActivityHistoryOpen((prev) => !prev)}
              >
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon
                    icon={isActivityHistoryOpen ? faChevronUp : faChevronDown}
                    className="me-2 text-muted"
                    style={{ fontSize: "12px", marginRight: "8px" }}
                  />
                  <span className="panel-title mb-0">Activity History</span>
                  {(activityList?.length > 0 || voiceLogsList?.length > 0) && (
                    <span className="badge badge-secondary ms-2" style={{ marginLeft: "8px" }}>
                      {(activityList?.length || 0) + (voiceLogsList?.length || 0)}
                    </span>
                  )}
                </div>
              </div>

              {isActivityHistoryOpen && (
                <div id="activityTarget" style={{ padding: "0 15px 10px 15px" }}>
                  <ul className="nav nav-tabs mb-3" id="activity-history-tabs">
                    <li className="nav-item" role="presentation">
                      <button
                        type="button"
                        className={`nav-link ${activityTab === "activity" ? "active" : ""}`}
                        role="tab"
                        aria-selected={activityTab === "activity"}
                        onClick={() => setActivityTab("activity")}
                        style={{
                          color: activityTab === "activity" ? "#007bff" : "#000",
                        }}
                      >
                        Activity History {activityList?.length > 0 && `(${activityList.length})`}
                      </button>
                    </li>
                    {hasPermission("subscriber_voice_logs", "view", true) && (
                      <li className="nav-item" role="presentation">
                        <button
                          type="button"
                          className={`nav-link ${activityTab === "call_history" ? "active" : ""}`}
                          role="tab"
                          aria-selected={activityTab === "call_history"}
                          onClick={() => setActivityTab("call_history")}
                          style={{
                            color: activityTab === "call_history" ? "#007bff" : "#000",
                          }}
                        >
                          Subscriber Call History {voiceLogsList?.length > 0 && `(${voiceLogsList.length})`}
                        </button>
                      </li>
                    )}
                  </ul>

                  {activityTab === "activity" && (
                    <div
                      id="activityData"
                      style={{
                        maxHeight: "350px",
                        overflowY: "auto",
                        padding: "12px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                      }}
                    >
                      {activityLoading && (
                        <>
                          <ListSkeletonItem />
                          <ListSkeletonItem />
                          <ListSkeletonItem />
                        </>
                      )}

                      {!activityLoading && activityList && activityList.length > 0 ? (
                        activityList.map((log, index) => {
                          const createdon = log.log_created_on
                            ? moment(log.log_created_on).format(
                              "DD/MM/YYYY HH:mm A"
                            )
                            : "Unknown date";

                          const fieldName = log.log_colname
                            ? log.log_colname.replace(/_/g, " ")
                            : "";

                          const setToMessage = log.log_message
                            ? log.log_message.replace(
                              /^Changed (.*?) to /,
                              "Set to "
                            )
                            : "";

                          return (
                            <div
                              key={index}
                              className="activity-log-item"
                              style={{
                                padding: "8px 0",
                                borderBottom: index < activityList.length - 1 ? "1px solid #edf2f7" : "none",
                              }}
                            >
                              <p className="mb-1 d-block text-muted" style={{ fontSize: "13px" }}>
                                <strong className="text-dark">{log.workBy}</strong> Updated{" "}
                                <span className="fw-semibold text-secondary">{fieldName}</span>
                              </p>

                              <p className="mb-0 d-block" style={{ fontSize: "12px" }}>
                                {setToMessage}{" "}
                                <span className="text-primary fw-medium">
                                  on {createdon}
                                </span>
                              </p>
                            </div>
                          );
                        })
                      ) : (
                        <p className="pl-3 text-muted mb-0">No activities found</p>
                      )}
                    </div>
                  )}

                  {activityTab === "call_history" && (
                    <div
                      id="callHistoryData"
                      style={{
                        maxHeight: "350px",
                        overflowY: "auto",
                        padding: "12px 14px",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                      }}
                    >
                      {voiceLogsLoading && (
                        <>
                          <ListSkeletonItem />
                          <ListSkeletonItem />
                          <ListSkeletonItem />
                        </>
                      )}

                      {!voiceLogsLoading && voiceLogsList && voiceLogsList.length > 0 ? (
                        voiceLogsList.map((log, index) => {
                          const createdon = log.created_on
                            ? moment(log.created_on).format("DD/MM/YYYY hh:mm A")
                            : "Unknown date";
                          const updatedBy = log.updated_by_name || (log.updated_by ? `User #${log.updated_by}` : "AI System");

                          return (
                            <div
                              key={log.id || index}
                              className="activity-log-item"
                              style={{
                                padding: "10px 0",
                                borderBottom: index < voiceLogsList.length - 1 ? "1px solid #edf2f7" : "none",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span style={{ fontSize: "12px", color: "#4a5568" }}>
                                  <strong className="text-dark">{updatedBy}</strong>
                                </span>
                                <span className="text-primary fw-medium" style={{ fontSize: "11px" }}>
                                  {createdon}
                                </span>
                              </div>

                              <div
                                style={{
                                  fontSize: "12px",
                                  lineHeight: "1.6",
                                  whiteSpace: "pre-line",
                                  color: "#2d3748",
                                  backgroundColor: "#f8fafc",
                                  padding: "8px 12px",
                                  borderRadius: "4px",
                                  borderLeft: "3px solid #3b82f6",
                                  wordBreak: "break-word",
                                }}
                              >
                                {log.message}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-muted mb-0" style={{ fontSize: "12px", padding: "8px 0" }}>
                          No call history logs found
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Merged Call Logs Panel with Tabs (Full Width) */}
          {(hasPermission("subscriber_callhistory", "view") || hasPermission("subscriber_voice_call_logs", "view")) && (
            <div className="col-md-12">
              <div className="panel panel-default with-rounded-corner with-shadow pb-2">
                <div className="panel-heading border-0">
                  <span className="panel-title">Call Logs</span>
                </div>
                <div className="panel-body">
                  <ul className="nav nav-tabs mb-3" id="call-logs-tabs">
                    {hasPermission("subscriber_callhistory", "view") && (
                      <li className="nav-item" role="presentation">
                        <Link
                          to="#"
                          className={`nav-link ${callLogTab === "call_summary" ? "active" : ""}`}
                          role="tab"
                          aria-selected={callLogTab === "call_summary"}
                          onClick={(e) => {
                            e.preventDefault();
                            setCallLogTab("call_summary");
                          }}
                          style={{
                            color: callLogTab === "call_summary" ? "#007bff" : "#000",
                          }}
                        >
                          Call Summary{callSummary?.length > 0 ? ` (${callSummary.length})` : ""}
                        </Link>
                      </li>
                    )}
                    {hasPermission("subscriber_voice_call_logs", "view") && (
                      <li className="nav-item" role="presentation">
                        <Link
                          to="#"
                          className={`nav-link ${callLogTab === "voice_call_logs" ? "active" : ""}`}
                          role="tab"
                          aria-selected={callLogTab === "voice_call_logs"}
                          onClick={(e) => {
                            e.preventDefault();
                            setCallLogTab("voice_call_logs");
                          }}
                          style={{
                            color: callLogTab === "voice_call_logs" ? "#007bff" : "#000",
                          }}
                        >

                          AI Voice Call Logs{voiceCallLogs?.length > 0 ? ` (${voiceCallLogs.length})` : ""}
                        </Link>
                      </li>
                    )}
                  </ul>
                  {/* Call Summary Tab */}
                  {callLogTab === "call_summary" && hasPermission("subscriber_callhistory", "view") && (
                    <div className="table-responsive" style={{ width: "100%" }}>
                      {callSummaryLoading ? (
                        <table
                          id="callsummary"
                          data-order='[[ 0, "desc" ]]'
                          className="table table-condensed table-hover bg-white mb-0 w-100"
                          style={{ width: "100%" }}
                        >
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Created Date</th>
                              <th>Duration</th>
                              <th>Status</th>
                              <th>Start Time</th>
                              <th>End Time</th>
                              <th>Recording</th>
                            </tr>
                          </thead>
                          <tbody>
                            <TableSkeletonRow columns={7} />
                            <TableSkeletonRow columns={7} />
                            <TableSkeletonRow columns={7} />
                            <TableSkeletonRow columns={7} />
                            <TableSkeletonRow columns={7} />
                          </tbody>
                        </table>
                      ) : (
                        <table
                          id="callsummary"
                          data-order='[[ 0, "desc" ]]'
                          className="table table-condensed table-hover bg-white mb-0 w-100"
                          style={{ width: "100%" }}
                        >
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Created Date</th>
                              <th>Duration</th>
                              <th>Status</th>
                              <th>Start Time</th>
                              <th>End Time</th>
                              <th>Recording</th>
                            </tr>
                          </thead>
                          <tbody>
                            {callSummary && callSummary.length > 0 ? (
                              callSummary.map((summary, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>
                                    {summary.created_on
                                      ? moment(summary.created_on).format(
                                        "DD/MM/YYYY"
                                      )
                                      : "-"}
                                  </td>
                                  <td>{formatDuration(summary.duration)}</td>
                                  <td>{summary.status || "-"}</td>
                                  <td>
                                    {summary.start_time
                                      ? moment(summary.start_time).format(
                                        "hh:mm A"
                                      )
                                      : "-"}
                                  </td>
                                  <td>
                                    {summary.end_time
                                      ? moment(summary.end_time).format("hh:mm A")
                                      : "-"}
                                  </td>
                                  <td className="text-center">
                                    {(summary.s3_recording_url || (summary.recording_url &&
                                    summary.recording_url.startsWith("http") &&
                                    summary.recording_url !== "Array")) ? (
                                      (() => {
                                        const recId = summary.autoId || summary.auto_id || summary.sid;
                                        const currentToken = localStorage.getItem("authToken") || token;
                                        const streamUrl = summary.s3_recording_url || `${API_URL}incoming-call-summary/recording/${recId}?token=${currentToken}`;
                                        const downloadUrl = `${API_URL}incoming-call-summary/recording/${recId}?token=${currentToken}&download=1`;
                                        return (
                                          <>
                                            <Link
                                              to="#"
                                              className="play_audio text-primary"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                setPopupAudio({
                                                  name: fullname || summary.subscriberfullname || summary.fullname || `Subscriber #${id}`,
                                                  audioUrl: streamUrl,
                                                });
                                              }}
                                            >
                                              <FontAwesomeIcon icon={faPlay} />
                                            </Link>
                                            <a
                                              href={downloadUrl}
                                              className="btn btn-sm text-primary"
                                              style={{ marginLeft: "10px" }}
                                              download={`recording_${recId}.mp3`}
                                              title="Download Recording"
                                            >
                                              <FontAwesomeIcon icon={faDownload} />
                                            </a>
                                          </>
                                        );
                                      })()
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="text-center py-3 text-muted">
                                  No call summary records found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* AI Voice Call Logs Tab */}
                  {callLogTab === "voice_call_logs" && hasPermission("subscriber_voice_call_logs", "view") && (
                    <div className="table-responsive" style={{ width: "100%" }}>
                      {voiceCallLogsLoading ? (
                        <table className="table table-condensed table-hover bg-white mb-0 w-100" style={{ width: "100%" }}>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Date / Time</th>
                              <th>Campaign / Type</th>
                              <th>Status</th>
                              <th>Outcome</th>
                              <th>Duration</th>
                              <th className="text-center">Recording</th>
                              <th className="text-center">Call Overview</th>
                            </tr>
                          </thead>
                          <tbody>
                            <TableSkeletonRow columns={8} />
                            <TableSkeletonRow columns={8} />
                            <TableSkeletonRow columns={8} />
                          </tbody>
                        </table>
                      ) : (
                        <table className="table table-condensed table-hover bg-white mb-0 w-100" style={{ width: "100%" }}>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Date / Time</th>
                              <th>Campaign / Type</th>
                              <th>Status</th>
                              <th>Outcome</th>
                              <th>Duration</th>
                              <th className="text-center">Recording</th>
                              <th className="text-center">Call Overview</th>
                            </tr>
                          </thead>
                          <tbody>
                            {voiceCallLogs && voiceCallLogs.length > 0 ? (
                              voiceCallLogs.map((log, index) => {
                                return (
                                  <tr key={log.id || index}>
                                    <td>{index + 1}</td>
                                    <td>
                                      <div>
                                        {log.createdOn || log.startedAt
                                          ? moment(log.createdOn || log.startedAt).format("DD/MM/YYYY")
                                          : "-"}
                                      </div>
                                      <small className="text-muted">
                                        {log.createdOn || log.startedAt
                                          ? moment(log.createdOn || log.startedAt).format("hh:mm A")
                                          : ""}
                                      </small>
                                    </td>
                                    <td>
                                      {log.campaignId
                                        ? (log.campaign_name ? log.campaign_name : `Campaign #${log.campaignId}`)
                                        : "Instant / Direct Call"}
                                    </td>
                                    <td style={{ textTransform: "capitalize" }}>
                                      {log.status || "-"}
                                    </td>
                                    <td style={{ textTransform: "capitalize" }}>
                                      {log.outcome ? log.outcome.replace(/_/g, " ") : "-"}
                                    </td>
                                    <td>{formatDuration(log.durationSeconds || 0)}</td>
                                    <td className="text-center">
                                      {log.recordingUrl ? (
                                        <>
                                          <Link
                                            to="#"
                                            className="play_audio text-primary"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              setPopupAudio({
                                                name: fullname || `Subscriber #${id}`,
                                                audioUrl: log.recordingUrl,
                                              });
                                            }}
                                            title="Play Recording"
                                          >
                                            <FontAwesomeIcon icon={faPlay} />
                                          </Link>
                                          <a
                                            href={log.recordingUrl}
                                            className="btn btn-sm text-primary"
                                            style={{ marginLeft: "10px" }}
                                            download={`recording_voice_${log.id || id}.mp3`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Download Recording"
                                          >
                                            <FontAwesomeIcon icon={faDownload} />
                                          </a>
                                        </>
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                    <td className="text-center">
                                      {(log.call_summary || log.callSummary || log.summary) ? (
                                        <button
                                          type="button"
                                          className="btn btn-primary btn-xs"
                                          onClick={() => {
                                            setSelectedCallSummary(
                                              log.call_summary || log.callSummary || log.summary
                                            );
                                            setIsModalOpenCallOverviewModal(true);
                                          }}
                                          title="View Call Overview"
                                          style={{
                                            fontSize: "11px",
                                            padding: "3px 8px",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <FontAwesomeIcon
                                            icon={faEye}
                                            className="me-1"
                                            style={{ marginRight: "4px" }}
                                          />
                                          Call Overview
                                        </button>
                                      ) : (
                                        <span className="text-muted" style={{ fontSize: "11px" }}>-</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center py-3 text-muted">
                                  No AI voice call logs found for this subscriber
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <PermissionWrapper module="subscriber_email_data" action="view">
          <div className="col-md-12">
              <div className="panel panel-default with-rounded-corner with-shadow pb-2">
                <div className="panel-heading border-0">
                  <span className="panel-title">Emails</span>
                </div>
                <div className="panel-body">
                  <ul className="nav nav-tabs mb-3" id="email-tabs">
                    <li className="nav-item" role="presentation">
                      <Link
                        to="#"
                        className={`nav-link ${emailTab === "sent" ? "active" : ""}`}
                        role="tab"
                        aria-selected={emailTab === "sent"}
                        onClick={(e) => {
                          e.preventDefault();
                          setEmailTab("sent");
                        }}
                        style={{
                          color: emailTab === "sent" ? "#007bff" : "#000",
                        }}
                      >
                        Sent{sentEmailsTotal > 0 ? ` (${sentEmailsTotal})` : ""}
                      </Link>
                    </li>
                    <li className="nav-item" role="presentation">
                      <Link
                        to="#"
                        className={`nav-link ${emailTab === "inbox" ? "active" : ""}`}
                        role="tab"
                        aria-selected={emailTab === "inbox"}
                        onClick={(e) => {
                          e.preventDefault();
                          setEmailTab("inbox");
                        }}
                        style={{
                          color: emailTab === "inbox" ? "#007bff" : "#000",
                        }}
                      >
                        Inbox{inboxEmailsTotal > 0 ? ` (${inboxEmailsTotal})` : ""}
                      </Link>
                    </li>
                  </ul>
                  {emailTab === "sent" ? (
                    <>
                      <table
                        id="sentemails"
                        className="table table-condensed table-hover bg-white"
                      >
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>From</th>
                            <th>Subject</th>
                            <th>Sent At</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sentEmailsLoading ? (
                            <>
                              <TableSkeletonRow columns={5} />
                              <TableSkeletonRow columns={5} />
                              <TableSkeletonRow columns={5} />
                              <TableSkeletonRow columns={5} />
                              <TableSkeletonRow columns={5} />
                            </>
                          ) : sentEmails && sentEmails.length > 0 ? (
                            sentEmails.map((mail, index) => (
                              <tr
                                key={mail.message_id || index}
                                onClick={() => {
                                  if (mail.message_id) {
                                    setViewEmail({ type: "sent", ...mail });
                                  }
                                }}
                                style={{
                                  cursor: mail.message_id ? "pointer" : "default",
                                }}
                                title={
                                  mail.message_id
                                    ? "Click to view email"
                                    : ""
                                }
                              >
                                <td>
                                  {(sentEmailsPage - 1) * SENT_EMAILS_LIMIT +
                                    index +
                                    1}
                                </td>
                                <td>{mail.from_email || "-"}</td>
                                <td>{mail.subject || "-"}</td>
                                <td>{mail.sent_at || "-"}</td>
                                <td>{mail.status || "-"}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center">
                                No sent emails found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      {sentEmailsTotal > SENT_EMAILS_LIMIT && (
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">
                            Page {sentEmailsPage} of{" "}
                            {Math.ceil(sentEmailsTotal / SENT_EMAILS_LIMIT)}
                          </span>
                          <div>
                            <button
                              type="button"
                              className="btn btn-default btn-sm"
                              style={{ marginRight: "8px" }}
                              disabled={sentEmailsLoading || sentEmailsPage <= 1}
                              onClick={() =>
                                fetchSentEmails(id, sentEmailsPage - 1)
                              }
                            >
                              Previous
                            </button>
                            <button
                              type="button"
                              className="btn btn-default btn-sm"
                              disabled={
                                sentEmailsLoading ||
                                sentEmailsPage >=
                                Math.ceil(sentEmailsTotal / SENT_EMAILS_LIMIT)
                              }
                              onClick={() =>
                                fetchSentEmails(id, sentEmailsPage + 1)
                              }
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : inboxAddress.length < 3 ? (
                    <p className="text-center text-muted m-0 py-3">
                      Add an email address to see conversations.
                    </p>
                  ) : inboxEmailsError && inboxEmails.length === 0 ? (
                    <div className="text-center py-3">
                      <p className="text-muted mb-2">
                        Couldn't load emails. Try again.
                      </p>
                      <button
                        type="button"
                        className="btn btn-default btn-sm"
                        onClick={() => fetchInboxEmails(inboxAddress)}
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <>
                      <table
                        id="inboxemails"
                        className="table table-condensed table-hover bg-white"
                      >
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Subject</th>
                            <th>Mailbox</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inboxEmails.map((mail, index) => (
                            <tr
                              key={`${mail.accountId}-${mail.threadId}`}
                              onClick={() =>
                                setViewEmail({ type: "inbox", ...mail })
                              }
                              style={{ cursor: "pointer" }}
                              title="Click to view conversation"
                            >
                              <td>{index + 1}</td>
                              <td style={{ maxWidth: "520px" }}>
                                <div
                                  style={{
                                    fontWeight: mail.isUnread ? "bold" : "normal",
                                  }}
                                >
                                  {mail.subject || "(no subject)"}
                                  {mail.messageCount > 1 && (
                                    <span
                                      className="badge bg-secondary"
                                      style={{ marginLeft: "8px" }}
                                    >
                                      {mail.messageCount}
                                    </span>
                                  )}
                                </div>
                                {mail.snippet && (
                                  <div
                                    className="text-muted"
                                    style={{
                                      fontSize: "12px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {mail.snippet}
                                  </div>
                                )}
                              </td>
                              <td className="text-muted">
                                {mail.accountEmail
                                  ? `via ${mail.accountEmail}`
                                  : "-"}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatInboxDate(mail.date)}
                              </td>
                            </tr>
                          ))}
                          {inboxEmailsLoading ? (
                            <>
                              <TableSkeletonRow columns={4} />
                              <TableSkeletonRow columns={4} />
                              <TableSkeletonRow columns={4} />
                            </>
                          ) : (
                            inboxEmails.length === 0 && (
                              <tr>
                                <td colSpan="4" className="text-center">
                                  No received emails
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                      {inboxEmailsError && (
                        <p className="text-center text-danger mb-2">
                          Couldn't load more emails. Try again.
                        </p>
                      )}
                      {inboxNextCursor && !inboxEmailsLoading && (
                        <div className="text-center">
                          <button
                            type="button"
                            className="btn btn-default btn-sm"
                            onClick={() =>
                              fetchInboxEmails(inboxAddress, inboxNextCursor)
                            }
                          >
                            Load more
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
          </div>
          </PermissionWrapper>

        </div>
      </div >
      {
        (isModalOpenCvlKycModal ||
          isModalOpenVerifyKycModal ||
          isModalOpenAddPaidServiceModal ||
          isModalOpenSendSmsModal ||
          isModalOpenSendPushNotificationModal ||
          isModalOpenAddOrderModal ||
          isModalOpenCartAddModal ||
          isModalOpenReminderModal ||
          isModalOpenAddTicketModal ||
          isModalOpenFreeServiceModal ||
          isRenewModalOpen ||
          isModalOpenCallOverviewModal ||
          Boolean(selectedVoiceTranscript)) && (
          <div
            className="modal-backdrop fade show"
            style={{
              backgroundColor: "rgba(0, 0, 0, 1)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1040,
            }}
          ></div>
        )
      }
      <PermissionWrapper module="kyc_details" action="edit">
        <CvlKycVerifyModal
          isOpen={isModalOpenCvlKycModal}
          onClose={() => setIsModalOpenCvlKycModal(false)}
          subscriberId={id}
          initialName={kycDetails?.name_pann || fullname || ""}
          initialPan={kycDetails?.pan_number || ""}
          initialDob={kycDetails?.dob || dob || ""}
          initialMobile={mobileno || ""}
          onSuccess={() => {
            fetchKycDetails(id);
            fetchSubscriber();
            fetchKycLogs(id);
            setVerifyKyc(1);
          }}
        />
        <VerifyKycPopup
          isOpen={isModalOpenVerifyKycModal}
          onClose={() => setIsModalOpenVerifyKycModal(false)}
          kycData={kycDetails}
          onSubmit={handleVerifyKycSubmit}
          subscriberId={id}
        />
      </PermissionWrapper>
      <EmailViewPopup
        mail={viewEmail}
        canViewOriginal={hasPermission("subscriber_email_data", "edit")}
        onClose={() => setViewEmail(null)}
      />
      <TicketPopup
        isOpen={isModalOpenAddTicketModal}
        onClose={() => setIsModalOpenAddTicketModal(false)}
        onSubmit={handleAddTicketSubmit}
        dropdownOptions={dropdownOptions}
      />

      <ReminderPopup
        isOpen={isModalOpenReminderModal}
        onClose={() => setIsModalOpenReminderModal(false)}
        onSubmit={handleSetReminder}
        dropdownOptions={dropdownOptions}
        data={reminderTime}
      />

      <FreeServicePopup
        isOpen={isModalOpenFreeServiceModal}
        onClose={() => setIsModalOpenFreeServiceModal(false)}
        onSubmit={handleAddFreeService}
        dropdownOptions={dropdownOptions}
      />

      {/* Renew Free M Credit Confirmation Modal */}
      {isRenewModalOpen && renewingService && (
        <div
          className="modal fade show"
          style={{ display: "block", zIndex: 1050 }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog" style={{ maxWidth: "26rem", marginTop: "15vh" }}>
            <div className="modal-content">
              <div
                className="modal-header d-flex justify-content-between align-items-center"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h5 className="modal-title m-0" style={{ fontWeight: "bold", margin: 0 }}>
                  <FontAwesomeIcon
                    icon={faSync}
                    className="text-primary"
                    style={{ marginRight: "8px" }}
                  />
                  Renew Credit (Free M)
                </h5>
                <button
                  type="button"
                  className="close"
                  aria-label="Close"
                  onClick={() => {
                    setIsRenewModalOpen(false);
                    setRenewingService(null);
                  }}
                  disabled={isRenewLoading}
                  style={{
                    border: "none",
                    background: "none",
                    fontSize: "1.5rem",
                    lineHeight: 1,
                    cursor: "pointer",
                    padding: "0 4px",
                    color: "#6c757d",
                  }}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <p style={{ fontSize: "14px", marginBottom: "12px" }}>
                  Are you sure you want to add <strong>1 credit</strong> to <strong>{renewingService.service}</strong>?
                </p>
                <div
                  className="alert alert-info py-2 mb-0"
                  style={{ fontSize: "13px", padding: "8px 12px" }}
                >
                  Renewal attempt:{" "}
                  <strong>
                    {(renewingService.renew_count ??
                      Math.max(
                        0,
                        (renewingService.trades_total || 1) - 1
                      )) + 1}{" "}
                    of 2
                  </strong>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmRenewCredit}
                  disabled={isRenewLoading}
                >
                  {isRenewLoading ? "Renewing..." : "Confirm"}
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => {
                    setIsRenewModalOpen(false);
                    setRenewingService(null);
                  }}
                  disabled={isRenewLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AddOrderPopup
        isOpen={isModalOpenAddOrderModal}
        onClose={() => setIsModalOpenAddOrderModal(false)}
        onSubmit={handleAddOrderData}
        dropdownOptions={dropdownOptions}
        subscriberName={fullname}
        subscriberGST={gstNo}
        subscriberState={state}
        subscriberId={id}
      />

      <AddPaidServiceOrder
        isOpen={isModalOpenAddPaidServiceModal}
        onClose={() => setIsModalOpenAddPaidServiceModal(false)}
        onSubmit={handleAddPaidServiceOrder}
        dropdownOptions={dropdownOptions}
        subscriberId={id}
      />

      <SendPushNotificationPopup
        isOpen={isModalOpenSendPushNotificationModal}
        onClose={() => setIsModalOpenSendPushNotificationModal(false)}
        onSubmit={handleSendPushNotificationSubmit}
        subscriberId={id}
      />

      <SendSmsPopup
        isOpen={isModalOpenSendSmsModal}
        onClose={() => setIsModalOpenSendSmsModal(false)}
        onSubmit={handleSendSmsSubmit}
        subscriberId={id}
        subscriberMobile={mobileno}
        subscriberName={fullname}
      />

      {
        isModalOpenCartAddModal && (
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="exampleModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog" style={{ maxWidth: "30rem" }}>
              <div className="modal-content">
                <div className="modal-header">
                  <div className="col-md-11">
                    <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                      Add Cart Item
                    </h5>
                  </div>
                  {/* Close button to hide the modal */}
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={closeModal} // Ensure this function hides the modal
                  >
                    &times;
                  </button>
                  <div className="clearfix"></div>
                </div>
                <div className="modal-body">
                  <form
                    action="http://localhost/newsstreet/app/Subscriber/saveCart"
                    name="frm1"
                    id="saveCartForm"
                    encType="multipart/form-data"
                    method="post"
                    acceptCharset="utf-8"
                    className="stdform"
                  >
                    <input type="hidden" name="subid" value="5" />
                    <div className="panel-body">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group">
                            <label className="control-label">Service</label>
                            <select
                              name="cart_service"
                              className="form-control"
                              required
                              id="cart_service"
                            >
                              <option value="" selected>
                                - Select -
                              </option>
                              <option value="1">INTRADAY STOCKS</option>
                              <option value="2">PREMIUM STOCKS</option>
                              <option value="3">STOCK FUTURES</option>
                              <option value="4">STOCK OPTIONS</option>
                              <option value="5">INDEX FUTURES</option>
                              <option value="6">INDEX OPTIONS</option>
                              <option value="7">SWING TRADING</option>
                              <option value="8">POSITIONAL STOCKS</option>
                              <option value="9">MULTIBAGGERS</option>
                              <option value="10">COMMODITY FUTURES</option>
                              <option value="11">COMMODITY OPTIONS</option>
                              <option value="12">BASE METAL</option>
                              <option value="13">STOCK OPTIONS BASIC</option>
                              <option value="14">TEST SERVICE</option>
                              <option value="16">CENTURY STOCKS</option>
                              <option value="17">INDEX OPTIONS BASIC</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group">
                            <label className="control-label">Trades</label>
                            <select
                              name="servicetrade"
                              className="form-control"
                              id="servicetrade"
                              required
                            ></select>
                          </div>
                        </div>
                      </div>

                      {/* Submit and Cancel buttons */}
                      <div className="col-md-12" style={{ textAlign: "right" }}>
                        <button
                          type="submit"
                          name="submit"
                          className="btn btn-primary mx-2"
                        >
                          Submit
                        </button>
                        <button
                          type="button"
                          name="cancel"
                          className="btn btn-default"
                          onClick={closeModal} // Use closeModal for the cancel button as well
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )
      }
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
        on={{ view: ({ index: currentIndex }) => setLightboxIndex(currentIndex) }}
      />

      {showEmailNotificationModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1050 }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5 className="modal-title m-0">Send Email Notification</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowEmailNotificationModal(false)}
                  aria-label="Close"
                  style={{ border: "none", background: "none", fontSize: "1.25rem", cursor: "pointer" }}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group mb-3">
                  <label className="form-label fw-bold mb-2">Campaign Template:</label>
                  <select
                    className="form-control form-select"
                    value={selectedEmailNotificationCampaignId}
                    onChange={(e) => setSelectedEmailNotificationCampaignId(e.target.value)}
                    disabled={isFetchingEmailNotificationTemplates}
                  >
                    <option value="">Select Template</option>
                    {emailNotificationTemplates.length > 0 ? (
                      emailNotificationTemplates.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.template_name || item.template || `Campaign Template #${item.id}`}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No templates available</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default btn-secondary mx-1"
                  onClick={() => setShowEmailNotificationModal(false)}
                  disabled={isSendingEmailNotification}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary mx-1"
                  onClick={handleSendEmailNotification}
                  disabled={!selectedEmailNotificationCampaignId || isSendingEmailNotification}
                >
                  {isSendingEmailNotification ? "Sending..." : "Send Notification"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSfIntroModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1050 }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5 className="modal-title m-0">Send SF Intro Notification</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSfIntroModal(false)}
                  aria-label="Close"
                  style={{ border: "none", background: "none", fontSize: "1.25rem", cursor: "pointer" }}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group mb-3">
                  <label className="form-label fw-bold mb-2">Campaign Template:</label>
                  <select
                    className="form-control form-select"
                    value={selectedCampaignId}
                    onChange={(e) => setSelectedCampaignId(e.target.value)}
                  >
                    <option value="">Select Template</option>
                    {sfCampaignTemplates.length > 0 ? (
                      sfCampaignTemplates.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.template_name || item.template || `Campaign Template #${item.id}`}
                        </option>
                      ))
                    ) : (
                      <option value={21}>Campaign Template #21</option>
                    )}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default btn-secondary mx-1"
                  onClick={() => setShowSfIntroModal(false)}
                  disabled={isSendingSfIntro}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary mx-1"
                  onClick={handleSendSfIntro}
                  disabled={!selectedCampaignId || isSendingSfIntro}
                >
                  {isSendingSfIntro ? "Sending..." : "Send Notification"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call Overview Modal */}
      {isModalOpenCallOverviewModal && (
        <div
          className="modal fade show"
          style={{ display: "block", zIndex: 1050 }}
          tabIndex="-1"
          role="dialog"
          aria-labelledby="callOverviewModalLabel"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl" role="document" style={{ maxWidth: "1000px", width: "95%" }}>
            <div className="modal-content shadow">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5 className="modal-title m-0 font-weight-bold" id="callOverviewModalLabel" style={{ fontWeight: 800, color: "#111827" }}>
                  <FontAwesomeIcon icon={faEye} className="me-2 text-primary" style={{ marginRight: "8px" }} />
                  Call Overview
                </h5>
                <button
                  type="button"
                  className="close btn btn-link p-0 text-muted"
                  onClick={() => {
                    setIsModalOpenCallOverviewModal(false);
                    setSelectedCallSummary(null);
                  }}
                  style={{ fontSize: "1.5rem", textDecoration: "none" }}
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", overflowX: "hidden" }}>
                {Boolean(selectedCallSummary || subscriberVoiceData?.call_summary || subscriberVoiceData?.call_overview || subscriberVoiceData?.call_log_summary) ? (
                  (() => {
                    const rawSummary =
                      selectedCallSummary ||
                      subscriberVoiceData?.call_summary ||
                      subscriberVoiceData?.call_overview ||
                      subscriberVoiceData?.call_log_summary;
                    let parsedJson = null;
                    try {
                      parsedJson =
                        typeof rawSummary === "string"
                          ? JSON.parse(rawSummary)
                          : rawSummary;
                    } catch (e) {
                      parsedJson = null;
                    }

                    if (parsedJson && typeof parsedJson === "object") {
                      // Helper to render nested values recursively or as clean rows
                      const renderNestedValue = (val) => {
                        if (val === null || val === undefined) return "-";
                        if (typeof val !== "object") return String(val);

                        if (Array.isArray(val)) {
                          if (val.length === 0) return "-";
                          return (
                            <ul className="mb-0 ps-3 list-unstyled">
                              {val.map((item, idx) => (
                                <li key={idx} className="py-1 border-bottom" style={{ wordBreak: "break-word" }}>
                                  {typeof item === "object" ? renderNestedValue(item) : String(item)}
                                </li>
                              ))}
                            </ul>
                          );
                        }

                        // It's a nested object: render as clean inner table rows
                        return (
                          <div className="table-responsive m-0">
                            <table className="table table-sm table-bordered mb-0 bg-white" style={{ fontSize: "12px", tableLayout: "auto" }}>
                              <tbody>
                                {Object.entries(val).map(([nestedKey, nestedVal]) => (
                                  <tr key={nestedKey}>
                                    <td
                                      style={{
                                        width: "250px",
                                        minWidth: "180px",
                                        fontWeight: 600,
                                        color: "#475569",
                                        background: "#fbfcfd",
                                        textTransform: "capitalize",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {nestedKey.replace(/_/g, " ")}
                                    </td>
                                    <td style={{ color: "#212529", wordBreak: "break-word", whiteSpace: "normal" }}>
                                      {renderNestedValue(nestedVal)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      };

                      return (
                        <div className="d-flex flex-column gap-3">
                          {Object.entries(parsedJson).map(([sectionKey, sectionVal]) => {
                            const isComplexObject =
                              typeof sectionVal === "object" &&
                              sectionVal !== null &&
                              !Array.isArray(sectionVal);

                            return (
                              <div
                                key={sectionKey}
                                className="panel panel-default border rounded shadow-sm overflow-hidden mb-3"
                                style={{ background: "#ffffff" }}
                              >
                                {/* Section Heading */}
                                <div
                                  className="panel-heading px-3 py-2 border-bottom"
                                  style={{
                                    backgroundColor: "#f1f5f9",
                                    borderLeft: "4px solid #2563eb",
                                  }}
                                >
                                  <h6
                                    className="m-0"
                                    style={{
                                      textTransform: "capitalize",
                                      fontSize: "14px",
                                      fontWeight: 800,
                                      color: "#0f172a",
                                      letterSpacing: "0.2px",
                                    }}
                                  >
                                    {sectionKey.replace(/_/g, " ")}
                                  </h6>
                                </div>

                                {/* Section Body */}
                                <div className="panel-body p-0">
                                  {isComplexObject ? (
                                    <div className="table-responsive m-0">
                                      <table
                                        className="table table-hover mb-0"
                                        style={{ fontSize: "12.5px", width: "100%", tableLayout: "auto" }}
                                      >
                                        <tbody>
                                          {Object.entries(sectionVal).map(([subKey, subVal]) => (
                                            <tr key={subKey}>
                                              <td
                                                style={{
                                                  width: "240px",
                                                  minWidth: "180px",
                                                  maxWidth: "280px",
                                                  fontWeight: 500,
                                                  color: "#64748b",
                                                  background: "#f8fafc",
                                                  textTransform: "capitalize",
                                                  borderRight: "1px solid #e2e8f0",
                                                  borderBottom: "1px solid #f1f5f9",
                                                  wordBreak: "break-word",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                {subKey.replace(/_/g, " ")}
                                              </td>
                                              <td
                                                style={{
                                                  color: "#0f172a",
                                                  fontWeight: 400,
                                                  lineHeight: 1.6,
                                                  borderBottom: "1px solid #f1f5f9",
                                                  wordBreak: "break-word",
                                                  whiteSpace: "normal",
                                                  verticalAlign: "top",
                                                }}
                                              >
                                                {renderNestedValue(subVal)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="p-3" style={{ fontSize: "13px", wordBreak: "break-word", whiteSpace: "normal" }}>
                                      {renderNestedValue(sectionVal)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                          {/* Raw JSON viewer */}
                          <details className="mt-2">
                            <summary className="text-muted" style={{ cursor: "pointer", fontSize: "12px" }}>
                              View Raw JSON Payload
                            </summary>
                            <pre
                              className="mt-2 p-2 bg-light rounded border"
                              style={{
                                fontSize: "11px",
                                maxHeight: "200px",
                                overflowY: "auto",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              {JSON.stringify(parsedJson, null, 2)}
                            </pre>
                          </details>
                        </div>
                      );
                    }

                    return (
                      <pre
                        className="p-3 bg-light rounded border m-0"
                        style={{
                          fontSize: "12px",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {String(rawSummary)}
                      </pre>
                    );
                  })()
                ) : (
                  <div className="text-center py-4 text-muted">
                    <p className="mb-0">No call overview / summary data available for this call.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Voice Call Transcript & Summary Modal */}
      {selectedVoiceTranscript && (
        <div
          className="modal fade show"
          style={{ display: "block", zIndex: 1050 }}
          tabIndex="-1"
          role="dialog"
          aria-labelledby="voiceTranscriptModalLabel"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content shadow">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5 className="modal-title m-0" id="voiceTranscriptModalLabel">
                  <FontAwesomeIcon icon={faComments} className="me-2 text-primary" style={{ marginRight: "8px" }} />
                  AI Voice Call Details
                  <span className="ms-2 badge badge-info" style={{ marginLeft: "8px", fontSize: "12px" }}>
                    ID: #{selectedVoiceTranscript.id}
                  </span>
                </h5>
                <button
                  type="button"
                  className="close btn btn-link p-0 text-muted"
                  onClick={() => setSelectedVoiceTranscript(null)}
                  style={{ fontSize: "1.5rem", textDecoration: "none" }}
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {/* Meta details */}
                <div className="row mb-3 pb-2 border-bottom">
                  <div className="col-md-3">
                    <small className="text-muted d-block">Status</small>
                    <span className="badge badge-success text-capitalize">
                      {selectedVoiceTranscript.status || "-"}
                    </span>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted d-block">Outcome</small>
                    <span className="badge badge-light border text-dark">
                      {selectedVoiceTranscript.outcome ? selectedVoiceTranscript.outcome.replace(/_/g, " ") : "-"}
                    </span>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted d-block">Duration</small>
                    <strong>{formatDuration(selectedVoiceTranscript.durationSeconds || 0)}</strong>
                  </div>
                  <div className="col-md-3">
                    <small className="text-muted d-block">Date & Time</small>
                    <small>
                      {selectedVoiceTranscript.createdOn || selectedVoiceTranscript.startedAt
                        ? moment(selectedVoiceTranscript.createdOn || selectedVoiceTranscript.startedAt).format("DD MMM YYYY hh:mm A")
                        : "-"}
                    </small>
                  </div>
                </div>

                {/* Audio Recording */}
                {selectedVoiceTranscript.recordingUrl && (
                  <div className="mb-3 p-2 bg-light rounded border d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faVolumeUp} className="text-primary me-2" style={{ marginRight: "8px" }} />
                      <span style={{ fontWeight: 600, fontSize: "13px" }}>Call Audio Recording:</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <audio controls preload="none" src={selectedVoiceTranscript.recordingUrl} style={{ height: "32px" }} />
                      <a
                        href={selectedVoiceTranscript.recordingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary ms-2"
                        style={{ marginLeft: "8px" }}
                      >
                        <FontAwesomeIcon icon={faDownload} className="me-1" style={{ marginRight: "4px" }} />
                        Download
                      </a>
                    </div>
                  </div>
                )}

                {/* Call Summary */}
                {selectedVoiceTranscript.summary && (
                  <div className="mb-3">
                    <h6 className="font-weight-bold mb-2">
                      <FontAwesomeIcon icon={faFileAlt} className="me-1 text-info" style={{ marginRight: "6px" }} />
                      Call Summary
                    </h6>
                    {(() => {
                      let parsed = null;
                      try {
                        parsed =
                          typeof selectedVoiceTranscript.summary === "string"
                            ? JSON.parse(selectedVoiceTranscript.summary)
                            : selectedVoiceTranscript.summary;
                      } catch (e) {
                        parsed = null;
                      }

                      if (parsed && typeof parsed === "object") {
                        return (
                          <div className="table-responsive">
                            <table className="table table-sm table-bordered mb-0 bg-white">
                              <tbody>
                                {Object.entries(parsed).map(([k, v]) => (
                                  <tr key={k}>
                                    <td style={{ width: "180px", fontWeight: 600, background: "#f8f9fa", textTransform: "capitalize" }}>
                                      {k.replace(/_/g, " ")}
                                    </td>
                                    <td>
                                      {typeof v === "object" && v !== null ? (
                                        <pre className="m-0 p-1 bg-light rounded" style={{ fontSize: "11px", whiteSpace: "pre-wrap" }}>
                                          {JSON.stringify(v, null, 2)}
                                        </pre>
                                      ) : (
                                        String(v ?? "-")
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      }

                      return (
                        <div className="p-3 bg-light rounded border" style={{ fontSize: "13px", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                          {String(selectedVoiceTranscript.summary)}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Conversation Transcript */}
                <div>
                  <h6 className="font-weight-bold mb-2">
                    <FontAwesomeIcon icon={faComments} className="me-1 text-primary" style={{ marginRight: "6px" }} />
                    Conversation Transcript
                  </h6>
                  {(() => {
                    let turns = [];
                    const rawTrans = selectedVoiceTranscript.transcript;
                    if (Array.isArray(rawTrans)) {
                      turns = rawTrans;
                    } else if (typeof rawTrans === "string") {
                      try {
                        const parsed = JSON.parse(rawTrans);
                        if (Array.isArray(parsed)) turns = parsed;
                        else if (parsed && Array.isArray(parsed.turns)) turns = parsed.turns;
                        else if (parsed && Array.isArray(parsed.messages)) turns = parsed.messages;
                      } catch (e) {
                        turns = [];
                      }
                    }

                    if (turns && turns.length > 0) {
                      return (
                        <div
                          className="p-3 bg-light rounded border d-flex flex-column gap-2"
                          style={{ maxHeight: "350px", overflowY: "auto" }}
                        >
                          {turns.map((turn, tIdx) => {
                            const role = (turn.role || turn.speaker || "").toLowerCase();
                            const isAgent = role === "agent" || role === "assistant" || role === "bot" || role === "ai";
                            const text = turn.text || turn.message || turn.content || (typeof turn === "string" ? turn : JSON.stringify(turn));

                            return (
                              <div
                                key={tIdx}
                                className={`d-flex flex-column ${isAgent ? "align-items-start" : "align-items-end"} mb-2`}
                              >
                                <span
                                  className="badge mb-1"
                                  style={{
                                    fontSize: "10px",
                                    backgroundColor: isAgent ? "#e9ecef" : "#cce5ff",
                                    color: isAgent ? "#495057" : "#004085",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  {isAgent ? "AI Agent" : "Customer / User"}
                                </span>
                                <div
                                  className="p-2 rounded shadow-sm"
                                  style={{
                                    maxWidth: "85%",
                                    backgroundColor: isAgent ? "#ffffff" : "#007bff",
                                    color: isAgent ? "#212529" : "#ffffff",
                                    border: isAgent ? "1px solid #dee2e6" : "none",
                                    fontSize: "13px",
                                    lineHeight: 1.5,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  {text}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    if (typeof rawTrans === "string" && rawTrans.trim()) {
                      return (
                        <pre
                          className="p-3 bg-light rounded border m-0"
                          style={{ fontSize: "12px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                        >
                          {rawTrans}
                        </pre>
                      );
                    }

                    return (
                      <div className="p-3 text-center text-muted bg-light rounded border">
                        No detailed transcript available for this call.
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setSelectedVoiceTranscript(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Audio Player Popup */}
      <AudioPlayerPopup
        isOpen={Boolean(popupAudio)}
        onClose={() => setPopupAudio(null)}
        name={popupAudio?.name}
        audioUrl={popupAudio?.audioUrl}
      />

    </div >
  );
};

export default SubscriberEdit;
